import React, { useCallback, useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";

import "./TableSetup.css";
import "./KotSettings.css";
import ExerciseSlider from "./SelectedExercisetable";
import tableimg from "./assets/table_tennis_table.png";
import GridConfigDialog from "./GridConfigDialog";
import {
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  List,
  ListItemButton,
  ListItemText,
  Typography,
  CircularProgress,
  Paper,
  Chip,
  Tooltip,
} from "@mui/material";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import SettingsIcon from "@mui/icons-material/Settings";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import RotateLeftIcon from "@mui/icons-material/RotateLeft";
import RotateRightIcon from "@mui/icons-material/RotateRight";
import LeaderboardIcon from "@mui/icons-material/Leaderboard";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import PersonalizeExerciseDialog from "./PersonalizeExerciseDialog";
import { ExerciseCard } from "./ExerciseCard";
import { KingOfTableSettings } from "./KingOfTableSettings";
import {
  grpplayerlist,
  playeradtable,
  playerswaptable,
  copyTableSetup,
  rotatePlayers,
  sessionexlist,
  tablelist,
  tableplayerlist,
  personalizeExerciseForPlayer,
  exdetails,
  listSessionSubgroups,
} from "./funtionsBD/Mainservice";
import {
  extraSeatedPlayers,
  mapTableIdToSubgroup,
  orderedSeatedPlayers,
  seatedPlayerIds,
  sortTablesByStation,
} from "../utils/tableSeats";
import { PLANNING_REFRESH_EVENT } from "../utils/planningSubgroups";
import PlayerPresenceDialog from "./PlayerPresenceDialog";
import { PlayerTrainingProfileDialog } from "./PlayerTrainingProfileDialog";
import { usePermissions } from "../context/Permissioncontext";
import { useTrainingRoomAccess } from "../hooks/useTrainingRoomAccess";
import { initializeSocket } from "../socket";
import axios from "axios";
import { ip_back } from "../config";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import { alpha } from "@mui/material/styles";
import { useBrand } from "../context/BrandContext";
import {
  TE_STATE_CANCELLED,
  TE_STATE_FINALIZED,
  TE_STATE_PAUSED,
  TE_STATE_SCHEDULED,
  TE_STATE_STARTED,
  pickCoachControlTargetIndex,
  resolveExerciseStateIdFromRow,
} from "../utils/coachExerciseNavigation";
import {
  canOpenSessionPresenceDialog,
  isSessionClosedForPresence,
  shouldAutoSeatAfterPresenceChange,
} from "../utils/sessionPresenceEdit";
import { userCanEditFinalizedExerciseSeating } from "../utils/planningAccess";
import {
  isKotExercise,
  isSoloCapableSequenceExercise,
  isTeRounds,
  isTeSequences,
  teExecutionTypeLabel,
  resolveTeExecutionType,
} from "../utils/teExecutionType";
import {
  isPersonalizedTrainingSlot,
  overrideForPlayer,
  localizedPersonalizedName,
} from "../utils/personalizedTrainingSlot";
import {
  isStaleExerciseTimerEvent,
  noteExerciseTimerRevision,
  patchExercisesFromSyncDomains,
  requestSessionSync,
} from "../utils/sessionSync";
import { patchSequenceGapFieldsFromPayload } from "../utils/sequenceTransitionPause";
import {
  filterPlayersAvailableToSeat,
  followingExercisesForTableCopy,
  planPresenceSeatAssignments,
} from "../utils/coachSessionTables";
import { currentAppLanguage } from "../utils/appLanguages";

function isInsidePlayerSlotTarget(el) {
  if (!el || !el.closest) return false;
  return !!el.closest(
    '.table_player_label_container, .table_player_label, .table_select_player, .table_player_stats, [data-slot="player1"], [data-slot="player2"], [data-slot="player3"]'
  );
}

/** Classement level + club ranking points under player name (e.g. C2 · 232 pts). */
function renderPlayerRankingStats(player, statsFontSize) {
  if (!player || typeof player !== "object") return null;
  const rankingPoints =
    player.ranking_points !== null && player.ranking_points !== undefined
      ? Math.round(player.ranking_points)
      : null;
  const classement =
    player.classement || player.fltt_classement || player.classment || null;
  if (!classement && rankingPoints === null) return null;
  return (
    <div
      className="table_player_stats"
      style={{ fontSize: `${statsFontSize}px` }}
    >
      {classement ? (
        <span className="table_player_stats__level">{classement}</span>
      ) : null}
      {rankingPoints !== null ? (
        <span className="table_player_stats__points">{rankingPoints} pts</span>
      ) : null}
    </div>
  );
}

/**
 * Sequence row to use for A/B roles on this table at the current step.
 * Uses the current sequence index from the server; if that row is scoped to
 * another table_number, prefer the row with the same sequence_order for this table.
 */
function resolveSequenceRowForTableRoles(exercise, tableNumberStr, currentIndex0) {
  const seqs = exercise?.sequences;
  if (!Array.isArray(seqs) || seqs.length === 0) return null;
  const idx =
    typeof currentIndex0 === "number" &&
    currentIndex0 >= 0 &&
    currentIndex0 < seqs.length
      ? currentIndex0
      : 0;
  const atIdx = seqs[idx];
  if (!atIdx) return null;
  const tn = atIdx.table_number;
  if (tn == null || tn === "" || String(tn) === tableNumberStr) {
    return atIdx;
  }
  const order = atIdx.sequence_order;
  if (order != null && order !== undefined) {
    const forTable = seqs.find(
      (s) =>
        s &&
        s.sequence_order === order &&
        s.table_number != null &&
        String(s.table_number) === tableNumberStr
    );
    if (forTable) return forTable;
  }
  return atIdx;
}

/**
 * Prefer server `exercise_slot_roles` (uniform A/B + Player1/2) when present;
 * otherwise legacy `player_a_role` / `player_b_role` on the same payload.
 */
function pickLiveRolesFromSocketPayload(data) {
  const r = data?.exercise_slot_roles;
  if (r?.by_player_position) {
    return {
      player_a_role: r.by_player_position.Player1 ?? data.player_a_role ?? null,
      player_b_role: r.by_player_position.Player2 ?? data.player_b_role ?? null,
    };
  }
  const slots = r?.slots;
  if (Array.isArray(slots) && slots.length >= 2) {
    const a = slots.find((s) => s.side === "A");
    const b = slots.find((s) => s.side === "B");
    return {
      player_a_role: a?.role ?? data.player_a_role ?? null,
      player_b_role: b?.role ?? data.player_b_role ?? null,
    };
  }
  return {
    player_a_role: data.player_a_role ?? null,
    player_b_role: data.player_b_role ?? null,
  };
}

/** Protocol revision 2: per-table lane for the current sequence step (`current_sequences_by_table`). */
function pickRolesFromPartitionMeta(meta, tableNumberStr) {
  const rows = meta?.current_sequences_by_table;
  if (!Array.isArray(rows) || rows.length === 0) return null;
  const match = rows.find(
    (r) => r != null && String(r.table_number) === String(tableNumberStr)
  );
  if (!match) return null;
  const a = match.player_a_role ?? null;
  const b = match.player_b_role ?? null;
  if (a == null && b == null) return null;
  return { player_a_role: a, player_b_role: b };
}

/** Normalize id for Set lookup (API may use string or number). */
function sessionPresencePlayerKey(playerId) {
  if (playerId === null || playerId === undefined || playerId === "") return null;
  return String(playerId);
}

/** Live HR window aligned with backend hr-update heartbeat (5s tick, §6.8). */
const HR_LIVE_SAMPLE_MAX_AGE_MS = 12000;

function isLiveHrSample(recordedAt) {
  if (recordedAt == null || recordedAt === "") return false;
  const t = new Date(recordedAt).getTime();
  if (!Number.isFinite(t)) return false;
  return Date.now() - t <= HR_LIVE_SAMPLE_MAX_AGE_MS;
}

/**
 * HR for one table slot (player1 = side A / Player1 only). No cross-slot lookup —
 * only players actively streaming (recent recorded_at) show BPM, matching iOS
 * single-sensor (active) vs dual-sensor modes.
 */
function resolveTableSlotLiveHr(tableHR, slotKey, seatedPlayer) {
  if (!tableHR || !slotKey) return null;
  const entry = tableHR[slotKey];
  if (!entry || typeof entry !== "object") return null;
  if (!isLiveHrSample(entry.recorded_at)) return null;
  if (seatedPlayer && typeof seatedPlayer === "object") {
    const seatedUid =
      seatedPlayer.user_id != null ? String(seatedPlayer.user_id) : "";
    const seatedPid =
      seatedPlayer.player_id != null ? String(seatedPlayer.player_id) : "";
    const entryUid = entry.user_id != null ? String(entry.user_id) : "";
    const entryPid = entry.player_id != null ? String(entry.player_id) : "";
    if (seatedUid && entryUid && seatedUid !== entryUid) return null;
    if (seatedPid && entryPid && seatedPid !== entryPid) return null;
  }
  const hr = Number(entry.heart_rate ?? entry.hr_value);
  return Number.isFinite(hr) && hr > 0 ? hr : null;
}

/**
 * Table assignments are not always cleared server-side when presence becomes false.
 * After we know who is present for the session, drop any table slots that still
 * reference absent players so the training view matches presence.
 */
function sanitizeTablesToSessionPresence(tables, presentPlayerIds) {
  if (!Array.isArray(tables) || !(presentPlayerIds instanceof Set)) {
    return tables;
  }
  const slotOk = (slot) => {
    if (!slot || typeof slot !== "object") return slot ?? null;
    const keys = Object.keys(slot);
    if (keys.length === 0) return null;
    const pid = sessionPresencePlayerKey(slot.player_id);
    if (!pid || !presentPlayerIds.has(pid)) return null;
    return slot;
  };
  return tables.map((table) => ({
    ...table,
    player1: slotOk(table.player1),
    player2: slotOk(table.player2),
    player3: slotOk(table.player3),
    players: Array.isArray(table.players)
      ? table.players.map(slotOk).filter(Boolean)
      : table.players,
  }));
}

/** Dedupe and sort tables like gettablelist (table_id, then table_number). */
function normalizeTablesList(tablesArray) {
  if (!Array.isArray(tablesArray)) return [];
  const uniqueTablesByTableId = new Map();
  tablesArray.forEach((table) => {
    if (table?.table_id != null && !uniqueTablesByTableId.has(table.table_id)) {
      uniqueTablesByTableId.set(table.table_id, table);
    }
  });
  const uniqueTablesByNumber = new Map();
  Array.from(uniqueTablesByTableId.values()).forEach((table) => {
    const tableNumber = table.table_number || 0;
    if (!uniqueTablesByNumber.has(tableNumber)) {
      uniqueTablesByNumber.set(tableNumber, table);
    }
  });
  return Array.from(uniqueTablesByNumber.values())
    .sort((a, b) => (a.table_number || 0) - (b.table_number || 0))
    .map((table) => {
      if (!Array.isArray(table.players) || table.players.length === 0) return table;
      const byPos = {};
      for (const p of table.players) {
        if (p?.player_position) byPos[p.player_position] = p;
      }
      return {
        ...table,
        player1: table.player1 || byPos.Player1 || null,
        player2: table.player2 || byPos.Player2 || null,
        player3: table.player3 || byPos.Player3 || null,
      };
    });
}

/** True when at least one slot on the table has an assigned player. */
function tableHasAssignedPlayer(table) {
  return orderedSeatedPlayers(table).length > 0;
}

/**
 * Match Planning (Exercises.jsx): keep rows the API returns for a session.
 * Competency-block and duplicated exercises often have ex_sub_type_id / competency_block
 * but sub_type_ex === null — the old sub_type_ex-only filter hid them in Table Setup.
 */
function isSessionExerciseForTableSetup(ex) {
  if (!ex) return false;
  if (ex.sub_type_ex != null) return true;
  const cb = ex.ex_sub_type_id;
  if (cb && typeof cb === "object" && (cb.cb_id || cb.cb_name_en || cb.cb_name_fr)) {
    return true;
  }
  if (
    ex.competency_block &&
    typeof ex.competency_block === "object" &&
    (ex.competency_block.cb_id || ex.competency_block.cb_name_en)
  ) {
    return true;
  }
  if (ex.ex_id) return true;
  if (ex.type_ex) return true;
  return Boolean(ex.te_id);
}

function resolveSessionGroupId(selectedSession, selectedgrp) {
  return (
    selectedgrp?.groupDetails?.gr_id ||
    selectedSession?.gr_id ||
    selectedSession?._groupId ||
    selectedSession?.groupDetails?.gr_id ||
    null
  );
}

export const TableSetup = ({
  selectedSession,
  selectedgrp,
  onExercisesSnapshot,
  onSessionLiveStatusUpdate,
  /** Training room: live maps for coach floating bar (staff-only parent) */
  onCoachLiveContext,
  /** Parent may hide session prefs (e.g. auto-start sequences) for KoT exercises */
  onSelectedExerciseChange,
}) => {
  const [eximg, seteximg] = useState("");
  const [tables, setTable] = useState();
  const [exercises, setexercise] = useState([]);
  const [selectedex, setselectedex] = useState();
  const [players, setplayers] = useState();
  const [allSessionPlayers, setAllSessionPlayers] = useState([]);
  const [selplayer, setselplayer] = useState();
  const [selplayer2, setselplayer2] = useState();
  const [cname, setcname] = useState("table_player_name");
  const [cname2, setcname2] = useState("table_player_name");
  const [draggingPlayerId, setDraggingPlayerId] = useState(null);
  const [dragOverTableId, setDragOverTableId] = useState(null);
  const [dragOverPlayerType, setDragOverPlayerType] = useState(null);
  const [perror, setperror] = useState();
  const [from, setfrom] = useState();
  const [currentExerciseIndex, setCurrentExerciseIndex] = useState(0);
  const [isAutoAssigning, setIsAutoAssigning] = useState(false);
  const [autoAssignAttempted, setAutoAssignAttempted] = useState(false);
  const [gridConfigOpen, setGridConfigOpen] = useState(false);
  const [gridColumns, setGridColumns] = useState(1);
  const [draggedTableIndex, setDraggedTableIndex] = useState(null);
  const [openPlayerModal, setOpenPlayerModal] = useState(false);
  const [assignTableDialogOpen, setAssignTableDialogOpen] = useState(false);
  const [assignTableDialogPlayer, setAssignTableDialogPlayer] = useState(null);
  const [assignTableDialogTables, setAssignTableDialogTables] = useState([]);
  const [assignTableDialogRemaining, setAssignTableDialogRemaining] = useState([]);
  const [sessionSubgroups, setSessionSubgroups] = useState([]);
  const [addExtraTable, setAddExtraTable] = useState(null);
  const [sessionState, setSessionState] = useState(null); // Real-time session state
  const [exerciseStates, setExerciseStates] = useState({}); // Real-time exercise states map: te_id -> state
  const [currentSequences, setCurrentSequences] = useState({}); // Real-time current sequence map: te_id -> currentSequence object
  /** Authoritative A/B slot roles from server (timer/sequence events) while exercise runs — updates each sequence change. */
  const [liveSequenceRolesByTeId, setLiveSequenceRolesByTeId] = useState({});
  /** Socket protocol v2: `current_sequences_by_table`, `sequence_partitioned`, `coach_warnings`, etc. */
  const [socketSequenceMetaByTeId, setSocketSequenceMetaByTeId] = useState({});
  const [sequenceTimers, setSequenceTimers] = useState({}); // Real-time sequence timers map: te_id -> sequenceTimer (in seconds)
  const [exerciseTimers, setExerciseTimers] = useState({}); // Real-time exercise timers map: te_id -> timer (in seconds)
  /** KoT: round elapsed (chronometer) per te_id, from socket round_elapsed_seconds. */
  const [hrData, setHrData] = useState({}); // Real-time HR data map: table_id -> { player1: { heart_rate }, player2: { heart_rate } }
  const [personalizeMode, setPersonalizeMode] = useState(false);
  const [personalizeDialogOpen, setPersonalizeDialogOpen] = useState(false);
  const [personalizePlayerId, setPersonalizePlayerId] = useState(null);
  const [personalizeGroupPlayers, setPersonalizeGroupPlayers] = useState([]);
  const [exerciseTypes, setExerciseTypes] = useState([]);
  const [personalizeSnackbar, setPersonalizeSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [personalizedExDetailsOpen, setPersonalizedExDetailsOpen] = useState(false);
  const [personalizedExDetailsData, setPersonalizedExDetailsData] = useState(null);
  const [personalizedExDetailsLoading, setPersonalizedExDetailsLoading] = useState(false);
  const [playerProfileOpen, setPlayerProfileOpen] = useState(false);
  const [playerProfileUserId, setPlayerProfileUserId] = useState(null);
  const [playerProfilePlayerId, setPlayerProfilePlayerId] = useState(null);
  const profileTouchTapRef = useRef({ key: "", t: 0 });
  const PROFILE_DOUBLE_TAP_MS = 420;
  /** Touch player swap: hold before drag so scrolling the page does not start a swap. */
  const PLAYER_SWAP_HOLD_MS = 500;
  const PLAYER_SWAP_CANCEL_MOVE_PX = 14;
  const joinedSessionRef = useRef(null); // `${ts_id}:${role}` we've joined
  const joinedExercisesRef = useRef(new Set()); // Track which exercises we've requested getStatus for
  const exerciseTimerRevisionRef = useRef({});
  const previousPlayersRef = useRef([]); // Track previous players list to detect new additions
  const tableListInFlightRef = useRef(null); // Dedupe concurrent groupTable requests
  const { t } = useTranslation();
  const { hasPermission } = usePermissions();
  const { isCoachView, viewerPlayerId, isPresent } =
    useTrainingRoomAccess(allSessionPlayers);
  const { brand } = useBrand();
  /** Personalize UI: secondary when idle, primary when active — both from club brand. */
  const personalizeIdle = brand?.color_secondary || "#46528b";
  const personalizeActive = brand?.color_primary || "#0877BC";
  const personalizeBrandVars = {
    "--personalize-idle": personalizeIdle,
    "--personalize-active": personalizeActive,
    "--personalize-idle-hover-bg": alpha(personalizeIdle, 0.18),
    "--personalize-active-glow": alpha(personalizeActive, 0.35),
  };
  const navBtnSx = {
    borderColor: "rgba(var(--brand-primary-rgb), 0.45)",
    color: personalizeActive,
    fontWeight: 600,
    textTransform: "none",
    borderRadius: 2,
    "&:hover": {
      borderColor: personalizeActive,
      bgcolor: "rgba(var(--brand-primary-rgb), 0.06)",
    },
    "&.Mui-disabled": {
      borderColor: "rgba(0,0,0,0.12)",
    },
  };
  const token = localStorage.getItem("token");
  const socket = initializeSocket(token);
  const currentlng = currentAppLanguage();
  
  // Use real-time session state if available, otherwise fallback to selectedSession state
  const currentSessionState = sessionState || selectedSession?.te_state || 
                              selectedSession?.state_te?.st_id || 
                              selectedSession?.tbl_state_te?.st_id;
  
  // Check if session is started (using real-time state)
  const isSessionStarted = currentSessionState === "st202406071447279901";
  
  // Resolved exercise state (socket map + row from API)
  const currentExerciseStateId = selectedex
    ? resolveExerciseStateIdFromRow(selectedex, exerciseStates)
    : null;

  const isCurrentExerciseRunning = currentExerciseStateId === TE_STATE_STARTED;
  const isCurrentExerciseFinalized =
    currentExerciseStateId === TE_STATE_FINALIZED ||
    currentExerciseStateId === TE_STATE_CANCELLED;
  const tableSetupUser = (() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "null");
    } catch {
      return null;
    }
  })();
  const canEditFinalizedTables =
    userCanEditFinalizedExerciseSeating(tableSetupUser);
  // Coaching staff may correct seating after finalize; keep running/cancelled locked.
  const isCurrentExerciseLocked =
    isCurrentExerciseRunning ||
    (isCurrentExerciseFinalized &&
      !(
        canEditFinalizedTables &&
        currentExerciseStateId === TE_STATE_FINALIZED
      ));

  /** Add players / personalize: on while exercise is still pre-start; off once started, paused, finalized, or cancelled. */
  const exerciseHasStartedForPlayerTools =
    currentExerciseStateId === TE_STATE_STARTED ||
    currentExerciseStateId === TE_STATE_PAUSED;
  const exerciseHasEndedForPlayerTools =
    currentExerciseStateId === TE_STATE_FINALIZED ||
    currentExerciseStateId === TE_STATE_CANCELLED;
  const canAddPlayersForDisplayedExercise =
    !selectedex ||
    (!exerciseHasStartedForPlayerTools && !exerciseHasEndedForPlayerTools);

  const isKotDisplayedExercise = Boolean(
    selectedex && isKotExercise(selectedex)
  );

  const hasPresencePermission =
    hasPermission("PLAYER_ADD_TO_SESSION") ||
    hasPermission("PLAYER_REMOVE_FROM_SESSION");
  const sessionClosedForPresence =
    selectedSession?.is_completed === true ||
    isSessionClosedForPresence(selectedSession) ||
    currentSessionState === TE_STATE_FINALIZED;
  const canOpenPresenceDialog =
    isCoachView &&
    canOpenSessionPresenceDialog({
      hasPresencePermission,
      exerciseStateId: currentExerciseStateId,
      sessionClosed: sessionClosedForPresence,
      kotAllowsPresence: isKotDisplayedExercise,
    });

  const showSessionAddPlayers = canOpenPresenceDialog;
  const showPersonalizeControl = isCoachView && !isKotDisplayedExercise;
  const showManualSeatingToolbar =
    isCoachView &&
    Boolean(selectedex) &&
    (!isKotDisplayedExercise || canAddPlayersForDisplayedExercise);
  const kotTableEditingEnabled =
    isCoachView &&
    (!isKotDisplayedExercise ||
      (!isCurrentExerciseLocked && canAddPlayersForDisplayedExercise));

  /** Personalize uses the same rule as Add players; while active, allow Cancel even if state changes. */
  const personalizeControlEnabled =
    personalizeMode || canAddPlayersForDisplayedExercise;

  useEffect(() => {
    if (!canAddPlayersForDisplayedExercise && personalizeMode) {
      setPersonalizeMode(false);
      setPersonalizeDialogOpen(false);
      setPersonalizePlayerId(null);
    }
  }, [canAddPlayersForDisplayedExercise, personalizeMode]);

  useEffect(() => {
    if (isKotDisplayedExercise && personalizeMode) {
      setPersonalizeMode(false);
      setPersonalizeDialogOpen(false);
      setPersonalizePlayerId(null);
    }
  }, [isKotDisplayedExercise, personalizeMode]);

  useEffect(() => {
    if (typeof onExercisesSnapshot === "function") {
      onExercisesSnapshot(
        Array.isArray(exercises) && exercises.length > 0 ? exercises : []
      );
    }
  }, [exercises, onExercisesSnapshot]);

  useEffect(() => {
    if (typeof onSelectedExerciseChange !== "function") return;
    onSelectedExerciseChange(selectedex || null);
  }, [selectedex, onSelectedExerciseChange]);

  useEffect(() => {
    if (typeof onCoachLiveContext !== "function") return;
    if (!selectedSession?.ts_id) {
      onCoachLiveContext(null);
      return;
    }
    onCoachLiveContext({
      tsId: selectedSession.ts_id,
      exercises: Array.isArray(exercises) ? exercises : [],
      exerciseStates,
      exerciseTimers,
      sequenceTimers,
      currentSequences,
      socketSequenceMetaByTeId,
      sessionState: currentSessionState,
    });
  }, [
    onCoachLiveContext,
    selectedSession?.ts_id,
    exercises,
    exerciseStates,
    exerciseTimers,
    sequenceTimers,
    currentSequences,
    socketSequenceMetaByTeId,
    currentSessionState,
  ]);

  // Initialize grid columns from session
  useEffect(() => {
    if (selectedSession?.ts_id) {
      setGridColumns(selectedSession.ts_grid_columns || 1);
    }
  }, [selectedSession?.ts_id, selectedSession?.ts_grid_columns]);
  
  // WebSocket listeners for real-time state updates
  useEffect(() => {
    if (!selectedSession?.ts_id || !exercises || exercises.length === 0) {
      return;
    }
    
    const user = JSON.parse(localStorage.getItem("user"));
    const user_id = user?.user_id || "coach";
    const ts_id = selectedSession.ts_id;
    const socketRole = isCoachView ? "coach" : "player";

    // Join session room once (rooms are session-level only)
    const joinKey = `${ts_id}:${socketRole}`;
    if (ts_id && joinedSessionRef.current !== joinKey) {
      socket.emit("joinTrainingSession", {
        ts_id,
        user_id,
        role: socketRole,
        active_te_id: selectedex?.te_id || undefined,
      });
      joinedSessionRef.current = joinKey;
      joinedExercisesRef.current.clear();
      requestSessionSync(socket, {
        tsId: ts_id,
        activeTeId: selectedex?.te_id,
        onDomains: (domains) => {
          setexercise((prev) => patchExercisesFromSyncDomains(prev, domains));
        },
      });
    }

    // Request current status for exercises not covered by session:sync (bootstrap timers)
    const validExercises = exercises.filter(ex => ex && ex !== null && ex !== undefined);
    validExercises.forEach((ex) => {
      if (ex?.te_id && !joinedExercisesRef.current.has(ex.te_id)) {
        joinedExercisesRef.current.add(ex.te_id);
        if (String(ex.te_id) !== String(selectedex?.te_id)) {
          socket.emit("getStatus", { te_id: ex.te_id });
        }
      }
    });

    const mergeIncomingSocketSequenceMeta = (payload) => {
      if (!payload?.te_id) return;
      if (
        payload.protocol_revision == null &&
        payload.sequence_partitioned == null &&
        payload.current_sequences_by_table == null &&
        payload.coach_warnings == null
      ) {
        return;
      }
      const teId = payload.te_id;
      setSocketSequenceMetaByTeId((prev) => {
        const cur = prev[teId] || {};
        const next = { ...cur };
        if (payload.protocol_revision != null)
          next.protocol_revision = payload.protocol_revision;
        if (payload.sequence_partitioned != null)
          next.sequence_partitioned = !!payload.sequence_partitioned;
        if (Array.isArray(payload.current_sequences_by_table))
          next.current_sequences_by_table = payload.current_sequences_by_table;
        if (payload.coach_warnings != null)
          next.coach_warnings = Array.isArray(payload.coach_warnings)
            ? payload.coach_warnings
            : [];
        return { ...prev, [teId]: next };
      });
    };
    
    // Listen for exercise state updates
    const handleExerciseStateUpdate = (data) => {
      console.log("🔔 TableSetup - exerciseStateUpdate received:", data);
      if (
        data.te_id &&
        isStaleExerciseTimerEvent(data.te_id, data.exercise_timer_revision)
      ) {
        return;
      }
      if (data.te_id && data.exercise_timer_revision != null) {
        noteExerciseTimerRevision(data.te_id, data.exercise_timer_revision);
        exerciseTimerRevisionRef.current[String(data.te_id)] =
          Number(data.exercise_timer_revision) || 0;
      }
      
      // Update exercise state
      if (data.te_id) {
        setExerciseStates(prev => ({
          ...prev,
          [data.te_id]: data.te_state
        }));
        
        // Also update the exercise in the exercises array
        setexercise(prev => prev.map(ex => {
          if (ex.te_id === data.te_id) {
            const isTerminal =
              data.te_state === TE_STATE_FINALIZED ||
              data.te_state === TE_STATE_CANCELLED ||
              data.te_state === "finalized" ||
              data.te_state === "cancelled";
            return {
              ...patchSequenceGapFieldsFromPayload(ex, data, { isTerminal }),
              te_state: data.te_state,
            };
          }
          return ex;
        }));
        
        // Update selectedex if it's the current exercise
        if (selectedex?.te_id === data.te_id) {
          setselectedex(prev => {
            if (!prev) return prev;
            const isTerminal =
              data.te_state === TE_STATE_FINALIZED ||
              data.te_state === TE_STATE_CANCELLED ||
              data.te_state === "finalized" ||
              data.te_state === "cancelled";
            return {
              ...patchSequenceGapFieldsFromPayload(prev, data, { isTerminal }),
              te_state: data.te_state,
            };
          });
        }
        
        // Initialize timers from exerciseStateUpdate (when exercise is started)
        // Backend sends actual remaining time in seconds + readable format: timer, sequenceTimer, remaining_exercise_time_seconds, remaining_sequence_time_seconds, remaining_exercise_time_formatted, remaining_sequence_time_formatted
        const exerciseRemaining = data.timer ?? data.remaining_exercise_time_seconds;
        if (exerciseRemaining !== undefined) {
          setExerciseTimers(prev => ({
            ...prev,
            [data.te_id]: exerciseRemaining
          }));
          console.log(`⏱️ TableSetup - Initialized exercise timer from exerciseStateUpdate: te_id=${data.te_id}, timer=${exerciseRemaining}s`);
        }
        
        if (data.currentSequence !== undefined) {
          // Convert from 1-indexed (backend) to 0-indexed (array index) for internal use
          const sequenceIndex = data.currentSequence - 1;
          setCurrentSequences(prev => ({
            ...prev,
            [data.te_id]: sequenceIndex
          }));
          console.log(`⏱️ TableSetup - Initialized current sequence from exerciseStateUpdate: te_id=${data.te_id}, currentSequence=${data.currentSequence} (index=${sequenceIndex})`);
        }

        const isTerminalTe =
          data.te_id &&
          (data.te_state === TE_STATE_FINALIZED ||
            data.te_state === TE_STATE_CANCELLED ||
            data.te_state === "finalized" ||
            data.te_state === "cancelled");
        if (isTerminalTe) {
          setLiveSequenceRolesByTeId((prev) => {
            const next = { ...prev };
            delete next[data.te_id];
            return next;
          });
          setSocketSequenceMetaByTeId((prev) => {
            const next = { ...prev };
            delete next[data.te_id];
            return next;
          });
        } else if (
          data.te_id &&
          (data.exercise_slot_roles != null ||
            data.player_a_role !== undefined ||
            data.player_b_role !== undefined)
        ) {
          setLiveSequenceRolesByTeId((prev) => ({
            ...prev,
            [data.te_id]: pickLiveRolesFromSocketPayload(data),
          }));
          mergeIncomingSocketSequenceMeta(data);
        } else if (data.te_id) {
          mergeIncomingSocketSequenceMeta(data);
        }
        
        const sequenceRemaining =
          data.sequenceTimer ??
          data.sequenceRemainingTime ??
          data.remaining_sequence_time_seconds;
        if (sequenceRemaining !== undefined) {
          setSequenceTimers(prev => ({
            ...prev,
            [data.te_id]: sequenceRemaining
          }));
          console.log(`⏱️ TableSetup - Initialized sequence timer from exerciseStateUpdate: te_id=${data.te_id}, sequenceTimer=${sequenceRemaining}s`);
        }
      }
      
      // If session state is included, update it
      // Note: The backend should emit session state updates separately
      // For now, we'll infer session state from exercise states
    };
    
    // Listen for timer updates (may include state info and timer/sequence data)
    const handleTimerUpdate = (data) => {
      // Timer updates might include exercise state info
      if (data.te_id && data.te_state) {
        handleExerciseStateUpdate(data);
      }
      
      // Update timers from actual remaining time (seconds + readable format from server)
      const exerciseRemaining = data.timer ?? data.remaining_exercise_time_seconds;
      if (data.te_id && exerciseRemaining !== undefined) {
        setExerciseTimers(prev => ({
          ...prev,
          [data.te_id]: exerciseRemaining
        }));
        console.log(`⏱️ TableSetup - timerUpdate: Updated exercise timer for te_id=${data.te_id}, timer=${exerciseRemaining}s`);
      }
      
      if (data.te_id && data.currentSequence !== undefined) {
        const sequenceIndex = data.currentSequence - 1; // Convert from 1-indexed to 0-indexed
        setCurrentSequences(prev => ({
          ...prev,
          [data.te_id]: sequenceIndex
        }));
        console.log(`⏱️ TableSetup - timerUpdate: Updated current sequence for te_id=${data.te_id}, currentSequence=${data.currentSequence} (index=${sequenceIndex})`);
      }
      
      const sequenceRemaining =
        data.sequenceTimer ??
        data.sequenceRemainingTime ??
        data.remaining_sequence_time_seconds;
      if (data.te_id && sequenceRemaining !== undefined) {
        setSequenceTimers(prev => ({
          ...prev,
          [data.te_id]: sequenceRemaining
        }));
        console.log(`⏱️ TableSetup - timerUpdate: Updated sequence timer for te_id=${data.te_id}, sequenceTimer=${sequenceRemaining}s`);
      }
      if (
        data.te_id &&
        (data.awaiting_manual_sequence_start != null ||
          data.awaiting_auto_sequence_start != null ||
          data.sequence_transition_pause_remaining != null ||
          data.next_sequence_number != null)
      ) {
        setexercise((prev) =>
          prev.map((ex) =>
            ex.te_id === data.te_id
              ? patchSequenceGapFieldsFromPayload(ex, data)
              : ex
          )
        );
        if (selectedex?.te_id === data.te_id) {
          setselectedex((prev) =>
            prev ? patchSequenceGapFieldsFromPayload(prev, data) : prev
          );
        }
      }

      if (
        data.te_id &&
        (data.exercise_slot_roles != null ||
          data.player_a_role !== undefined ||
          data.player_b_role !== undefined)
      ) {
        setLiveSequenceRolesByTeId((prev) => ({
          ...prev,
          [data.te_id]: pickLiveRolesFromSocketPayload(data),
        }));
      }
      mergeIncomingSocketSequenceMeta(data);
    };
    
    // Listen for sequence updates
    const handleSequenceUpdate = (data) => {
      if (data.te_id) {
        // Convert currentSequence from 1-indexed (backend) to 0-indexed (array index) for internal use
        if (data.currentSequence !== undefined) {
          const sequenceIndex = data.currentSequence - 1; // Convert from 1-indexed to 0-indexed
          setCurrentSequences(prev => ({
            ...prev,
            [data.te_id]: sequenceIndex
          }));
          console.log(`⏱️ TableSetup - sequenceUpdate: Updated current sequence for te_id=${data.te_id}, currentSequence=${data.currentSequence} (index=${sequenceIndex})`);
        }
        const seqRem = data.sequenceTimer ?? data.remaining_sequence_time_seconds;
        if (seqRem !== undefined) {
          setSequenceTimers(prev => ({
            ...prev,
            [data.te_id]: seqRem
          }));
          console.log(`⏱️ TableSetup - sequenceUpdate: Updated sequence timer for te_id=${data.te_id}, sequenceTimer=${seqRem}s`);
        }
        if (
          data.exercise_slot_roles != null ||
          data.player_a_role !== undefined ||
          data.player_b_role !== undefined
        ) {
          setLiveSequenceRolesByTeId((prev) => ({
            ...prev,
            [data.te_id]: pickLiveRolesFromSocketPayload(data),
          }));
        }

        if (
          data.awaiting_manual_sequence_start != null ||
          data.awaiting_auto_sequence_start != null ||
          data.sequence_transition_pause_remaining != null ||
          data.next_sequence_number != null
        ) {
          setexercise((prev) =>
            prev.map((ex) =>
              ex.te_id === data.te_id
                ? patchSequenceGapFieldsFromPayload(ex, data)
                : ex
            )
          );
          if (selectedex?.te_id === data.te_id) {
            setselectedex((prev) =>
              prev ? patchSequenceGapFieldsFromPayload(prev, data) : prev
            );
          }
        }
        mergeIncomingSocketSequenceMeta(data);
      }
    };
    
    // Listen for getStatusTE response (callback from getStatus request)
    const handleGetStatusTE = (data) => {
      if (data && !data.error && data.data) {
        const responseData = data.data;
        console.log(`📊 TableSetup - getStatusTE received:`, responseData);
        
        if (responseData.te_id) {
          // Update exercise state
          if (responseData.te_state) {
            setExerciseStates(prev => ({
              ...prev,
              [responseData.te_id]: responseData.te_state
            }));
          }
          
          // Initialize timers from getStatusTE response
          if (responseData.timer !== undefined && responseData.timer !== null) {
            setExerciseTimers(prev => ({
              ...prev,
              [responseData.te_id]: responseData.timer
            }));
            console.log(`⏱️ TableSetup - Initialized exercise timer from getStatusTE: te_id=${responseData.te_id}, timer=${responseData.timer}s`);
          }
          
          // Initialize current sequence from getStatusTE response
          // Convert currentSequence from 1-indexed (backend) to 0-indexed (array index) for internal use
          if (responseData.currentSequence !== undefined && responseData.currentSequence !== null) {
            const sequenceIndex = responseData.currentSequence - 1;
            setCurrentSequences(prev => ({
              ...prev,
              [responseData.te_id]: sequenceIndex
            }));
            console.log(`⏱️ TableSetup - Initialized current sequence from getStatusTE: te_id=${responseData.te_id}, currentSequence=${responseData.currentSequence} (index=${sequenceIndex})`);
          }
          
          // Initialize sequence timer from getStatusTE response
          if (responseData.sequenceTimer !== undefined && responseData.sequenceTimer !== null) {
            setSequenceTimers(prev => ({
              ...prev,
              [responseData.te_id]: responseData.sequenceTimer
            }));
            console.log(`⏱️ TableSetup - Initialized sequence timer from getStatusTE: te_id=${responseData.te_id}, sequenceTimer=${responseData.sequenceTimer}s`);
          }

          const statusTerminal =
            responseData.te_state === TE_STATE_FINALIZED ||
            responseData.te_state === TE_STATE_CANCELLED ||
            responseData.te_state === "finalized" ||
            responseData.te_state === "cancelled";
          if (statusTerminal) {
            setLiveSequenceRolesByTeId((prev) => {
              const n = { ...prev };
              delete n[responseData.te_id];
              return n;
            });
            setSocketSequenceMetaByTeId((prev) => {
              const n = { ...prev };
              delete n[responseData.te_id];
              return n;
            });
          } else {
            if (
              responseData.exercise_slot_roles != null ||
              responseData.player_a_role !== undefined ||
              responseData.player_b_role !== undefined
            ) {
              setLiveSequenceRolesByTeId((prev) => ({
                ...prev,
                [responseData.te_id]: pickLiveRolesFromSocketPayload(responseData),
              }));
            }
            mergeIncomingSocketSequenceMeta(responseData);
          }

          setexercise((prev) =>
            prev.map((ex) => {
              if (String(ex.te_id) !== String(responseData.te_id)) return ex;
              const isTerminal =
                responseData.te_state === TE_STATE_FINALIZED ||
                responseData.te_state === TE_STATE_CANCELLED ||
                responseData.te_state === "finalized" ||
                responseData.te_state === "cancelled";
              return {
                ...patchSequenceGapFieldsFromPayload(ex, responseData, {
                  isTerminal,
                }),
                te_state: responseData.te_state ?? ex.te_state,
              };
            })
          );
          if (selectedex?.te_id && String(selectedex.te_id) === String(responseData.te_id)) {
            setselectedex((prev) => {
              if (!prev) return prev;
              const isTerminal =
                responseData.te_state === TE_STATE_FINALIZED ||
                responseData.te_state === TE_STATE_CANCELLED ||
                responseData.te_state === "finalized" ||
                responseData.te_state === "cancelled";
              return {
                ...patchSequenceGapFieldsFromPayload(prev, responseData, {
                  isTerminal,
                }),
                te_state: responseData.te_state ?? prev.te_state,
              };
            });
          }
        }
      }
    };
    
    /**
     * Accepts the canonical `hr-update` payload from HR_TRAINING_PROTOCOL.md §6.8 — i.e.
     * `{ tables: [{ table_id, players: [{ player_id, hr_value, ... }], ...legacy }] }` — and
     * falls back to the legacy `player1/player2/slots` arrangement so we keep working with
     * older backends until everyone moves to `players[]`. The map we feed into the UI is
     * always the legacy `{ player1, player2 }` shape because that is what TableSetup renders.
     */
    const handleHRUpdate = (payload) => {
      const tablesFromPayload =
        payload &&
        typeof payload === "object" &&
        !Array.isArray(payload) &&
        Array.isArray(payload.tables)
          ? payload.tables
          : null;
      const tablesList = Array.isArray(tablesFromPayload)
        ? tablesFromPayload
        : Array.isArray(payload)
          ? payload
          : null;
      console.log("💓 TableSetup - HR update received:", payload);
      if (!tablesList) return;

      // Accept snapshots for any exercise in this session when they contain live
      // HR data that matches currently-displayed tables (mobile may be locked to a
      // different te_id than the one the coach has selected on the web).
      const currentTeId =
        selectedex?.te_id != null ? String(selectedex.te_id) : null;
      const payloadTeId =
        payload &&
        typeof payload === "object" &&
        !Array.isArray(payload) &&
        payload.te_id != null
          ? String(payload.te_id)
          : null;
      if (payloadTeId && currentTeId && payloadTeId !== currentTeId) {
        // Only skip if none of the snapshot tables have live HR data.
        const hasLiveData = tablesList.some((t) =>
          Array.isArray(t.players)
            ? t.players.some((p) => p && p.hr_value > 0 && isLiveHrSample(p.recorded_at))
            : (t.player1?.hr_value > 0 && isLiveHrSample(t.player1?.recorded_at)) ||
              (t.player2?.hr_value > 0 && isLiveHrSample(t.player2?.recorded_at))
        );
        if (!hasLiveData) return;
      }

      // Normalize a player-shaped object into the legacy `{ player_id, user_id, heart_rate, hr_value }`
      // record TableSetup expects for player1 / player2 cards.
      const toLegacyPlayer = (p) => {
        if (!p || typeof p !== "object") return null;
        const rawHr =
          p.hr_value !== undefined && p.hr_value !== null
            ? Number(p.hr_value)
            : p.heart_rate !== undefined && p.heart_rate !== null
              ? Number(p.heart_rate)
              : 0;
        const recordedAt = p.recorded_at || null;
        const live = isLiveHrSample(recordedAt);
        const heartRate =
          live && Number.isFinite(rawHr) && rawHr > 0 ? rawHr : 0;
        return {
          player_id: p.player_id || null,
          user_id: p.user_id || null,
          user_first_name: p.user_first_name || null,
          user_alias: p.user_alias || null,
          heart_rate: heartRate,
          hr_value: heartRate,
          recorded_at: live ? recordedAt : null,
        };
      };

      const hrDataMap = {};
      tablesList.forEach((tableHR) => {
        if (!tableHR || !tableHR.table_id) return;
        let player1 = null;
        let player2 = null;

        // Canonical §6.8: tables[].players[]. When any entry carries a player_position,
        // use only the positional map — never fall back to array index — so that a table
        // with only one active sensor never copies that player's HR into the other slot.
        if (Array.isArray(tableHR.players) && tableHR.players.length > 0) {
          const byPos = {};
          let anyHasPosition = false;
          for (const p of tableHR.players) {
            if (p && typeof p === "object" && p.player_position) {
              byPos[p.player_position] = p;
              anyHasPosition = true;
            }
          }
          if (anyHasPosition) {
            player1 = toLegacyPlayer(byPos.Player1);
            player2 = toLegacyPlayer(byPos.Player2);
          } else {
            player1 = toLegacyPlayer(tableHR.players[0]);
            player2 = toLegacyPlayer(tableHR.players[1]);
          }
        } else if (Array.isArray(tableHR.slots) && tableHR.slots.length > 0) {
          // Older interim shape from this same backend (slots[]).
          const slotByPos = {};
          let anySlotHasPosition = false;
          for (const s of tableHR.slots) {
            if (s && typeof s === "object" && s.position) {
              slotByPos[s.position] = s;
              anySlotHasPosition = true;
            }
          }
          if (anySlotHasPosition) {
            player1 = toLegacyPlayer(slotByPos.Player1);
            player2 = toLegacyPlayer(slotByPos.Player2);
          } else {
            player1 = toLegacyPlayer(tableHR.slots[0]);
            player2 = toLegacyPlayer(tableHR.slots[1]);
          }
        } else {
          // Legacy player1 / player2 keys.
          player1 = toLegacyPlayer(tableHR.player1);
          player2 = toLegacyPlayer(tableHR.player2);
        }

        hrDataMap[tableHR.table_id] = { player1, player2 };
      });
      setHrData((prev) => ({ ...prev, ...hrDataMap }));
    };

    const handleExercisePlanUpdated = (data) => {
      if (!data?.te_id || data.ts_id !== selectedSession?.ts_id) return;
      setexercise((prev) =>
        prev.map((ex) => {
          if (!ex || String(ex.te_id) !== String(data.te_id)) return ex;
          const next = { ...ex, te_duration: data.te_duration ?? ex.te_duration };
          if (data.sequences?.length && ex.sequences?.length) {
            const byTes = Object.fromEntries(data.sequences.map((s) => [String(s.tes_id), s]));
            next.sequences = [...ex.sequences]
              .map((seq) => {
                const u = byTes[String(seq.tes_id)];
                if (!u) return seq;
                return {
                  ...seq,
                  sequence_duration: u.sequence_duration ?? seq.sequence_duration,
                  sequence_order: u.sequence_order ?? seq.sequence_order,
                  player_a_role: u.player_a_role ?? seq.player_a_role,
                  player_b_role: u.player_b_role ?? seq.player_b_role,
                };
              })
              .sort((a, b) => (a.sequence_order ?? 0) - (b.sequence_order ?? 0));
          }
          return next;
        })
      );
      if (selectedex?.te_id && String(selectedex.te_id) === String(data.te_id)) {
        setselectedex((prev) => {
          if (!prev) return prev;
          const next = { ...prev, te_duration: data.te_duration ?? prev.te_duration };
          if (data.sequences?.length && prev.sequences?.length) {
            const byTes = Object.fromEntries(data.sequences.map((s) => [String(s.tes_id), s]));
            next.sequences = [...prev.sequences]
              .map((seq) => {
                const u = byTes[String(seq.tes_id)];
                if (!u) return seq;
                return {
                  ...seq,
                  sequence_duration: u.sequence_duration ?? seq.sequence_duration,
                  sequence_order: u.sequence_order ?? seq.sequence_order,
                  player_a_role: u.player_a_role ?? seq.player_a_role,
                  player_b_role: u.player_b_role ?? seq.player_b_role,
                };
              })
              .sort((a, b) => (a.sequence_order ?? 0) - (b.sequence_order ?? 0));
          }
          return next;
        });
      }
    };

    const handleExerciseOrderUpdated = (data) => {
      if (!data?.exercises?.length || data.ts_id !== selectedSession?.ts_id) return;
      const orderMap = Object.fromEntries(data.exercises.map((e) => [String(e.te_id), e.te_order]));
      setexercise((prev) => {
        const next = [...prev]
          .map((ex) =>
            ex && ex.te_id
              ? { ...ex, te_order: orderMap[String(ex.te_id)] ?? ex.te_order }
              : ex
          )
          .sort((a, b) => (a?.te_order ?? 0) - (b?.te_order ?? 0));
        if (selectedex?.te_id) {
          const ix = next.findIndex((e) => e && String(e.te_id) === String(selectedex.te_id));
          if (ix >= 0) setCurrentExerciseIndex(ix);
        }
        return next;
      });
      if (selectedex?.te_id && orderMap[String(selectedex.te_id)] != null) {
        setselectedex((prev) => (prev ? { ...prev, te_order: orderMap[String(prev.te_id)] } : prev));
      }
    };

    const handleTrainingSessionStatusUpdate = (data) => {
      if (!data?.ts_id || data.ts_id !== selectedSession?.ts_id) return;
      if (data.te_state) setSessionState(data.te_state);
      onSessionLiveStatusUpdate?.({
        te_state: data.te_state,
        is_completed: data.is_completed,
      });
    };

    const handleTrainingTableSetupChanged = (data) => {
      if (!data?.ts_id || data.ts_id !== selectedSession?.ts_id) return;
      if (selectedex?.te_id && data.te_id && String(data.te_id) !== String(selectedex.te_id)) return;
      if (!Array.isArray(data.tables)) return;
      const presentIdSet =
        Array.isArray(players) && players.length > 0
          ? new Set(players.map((p) => sessionPresencePlayerKey(p.player_id)).filter(Boolean))
          : null;
      const normalized = normalizeTablesList(data.tables);
      const tablesForUi =
        presentIdSet instanceof Set
          ? sanitizeTablesToSessionPresence(normalized, presentIdSet)
          : normalized;
      setTable(tablesForUi);

      // Clear stale HR data for every table whose slot assignments just changed so
      // the display never shows HR values from before the Manage-Player update.
      const changedIds = data.tables
        .map((t) => t?.table_id)
        .filter(Boolean)
        .map(String);
      if (changedIds.length > 0) {
        setHrData((prev) => {
          const next = { ...prev };
          changedIds.forEach((id) => { delete next[id]; });
          return next;
        });
      }
      // Request a fresh HR snapshot so the updated slots get their current values.
      if (socket && selectedSession?.ts_id && selectedex?.te_id) {
        socket.emit("requestHrSnapshot", {
          ts_id: selectedSession.ts_id,
          te_id: selectedex.te_id,
        });
      }
    };

    // §6.8 per-save incremental: table_id is known, update the right slot directly.
    // This fires regardless of which te_id the coach has selected — same session room.
    const handlePlayerHeartRate = (evt) => {
      if (!evt || !evt.table_id || !evt.ts_id) return;
      if (String(evt.ts_id) !== String(selectedSession?.ts_id)) return;
      const hr = parseInt(evt.hr_value, 10);
      if (!Number.isFinite(hr)) return;
      const tableId = String(evt.table_id);
      const pid = evt.player_id ? String(evt.player_id) : null;
      const uid = evt.user_id ? String(evt.user_id) : null;
      const recordedAt = evt.recorded_at || null;
      const isClear = hr <= 0;
      if (!isClear && !isLiveHrSample(recordedAt)) return;
      setHrData((prev) => {
        const existing = prev[tableId] || {};
        // Determine which slot this player belongs to (player1 or player2)
        // by matching against the existing hrData entry (which was set from snapshot)
        const updateSlot = (slot) => {
          if (!slot || typeof slot !== "object") return slot;
          const slotPid = slot.player_id ? String(slot.player_id) : null;
          const slotUid = slot.user_id ? String(slot.user_id) : null;
          if ((pid && slotPid && pid === slotPid) || (uid && slotUid && uid === slotUid)) {
            if (isClear) {
              return { ...slot, heart_rate: 0, hr_value: 0, recorded_at: null };
            }
            return { ...slot, heart_rate: hr, hr_value: hr, recorded_at: recordedAt };
          }
          return slot;
        };
        const next1 = updateSlot(existing.player1);
        const next2 = updateSlot(existing.player2);
        const changed = next1 !== existing.player1 || next2 !== existing.player2;
        if (!changed) {
          // Slot not found in existing hrData — the snapshot hasn't arrived yet.
          // Write a minimal entry for the position so HR shows as soon as tables render.
          // We don't know the position yet, defer to snapshot to fill slot details.
          return prev;
        }
        return { ...prev, [tableId]: { ...existing, player1: next1, player2: next2 } };
      });
    };

    socket.on("exerciseStateUpdate", handleExerciseStateUpdate);
    socket.on("timerUpdate", handleTimerUpdate);
    socket.on("sequenceUpdate", handleSequenceUpdate);
    socket.on("getStatusTE", handleGetStatusTE);
    socket.on("hr-update", handleHRUpdate);
    socket.on("player_heart_rate", handlePlayerHeartRate);
    socket.on("playerHeartRate", handlePlayerHeartRate);
    socket.on("exercisePlanUpdated", handleExercisePlanUpdated);
    socket.on("exerciseOrderUpdated", handleExerciseOrderUpdated);
    socket.on("trainingSessionStatusUpdate", handleTrainingSessionStatusUpdate);
    socket.on("trainingTableSetupChanged", handleTrainingTableSetupChanged);
    
    return () => {
      socket.off("exerciseStateUpdate", handleExerciseStateUpdate);
      socket.off("timerUpdate", handleTimerUpdate);
      socket.off("sequenceUpdate", handleSequenceUpdate);
      socket.off("getStatusTE", handleGetStatusTE);
      socket.off("hr-update", handleHRUpdate);
      socket.off("player_heart_rate", handlePlayerHeartRate);
      socket.off("playerHeartRate", handlePlayerHeartRate);
      socket.off("exercisePlanUpdated", handleExercisePlanUpdated);
      socket.off("exerciseOrderUpdated", handleExerciseOrderUpdated);
      socket.off("trainingSessionStatusUpdate", handleTrainingSessionStatusUpdate);
      socket.off("trainingTableSetupChanged", handleTrainingTableSetupChanged);
      // Note: We don't clear joinedExercisesRef here because we want to persist across re-renders
      // Only clear when session changes
    };
  }, [
    selectedSession?.ts_id,
    socket,
    exercises?.length,
    selectedex?.te_id,
    players,
    isCoachView,
    onSessionLiveStatusUpdate,
  ]);

  useEffect(() => {
    if (!selectedSession?.ts_id || !selectedex?.te_id || !socket) return;
    socket.emit("requestHrSnapshot", {
      ts_id: selectedSession.ts_id,
      te_id: selectedex.te_id,
    });
  }, [selectedSession?.ts_id, selectedex?.te_id, socket]);
  
  // Reset joined exercises when session changes
  useEffect(() => {
    joinedExercisesRef.current.clear();
    setLiveSequenceRolesByTeId({});
  }, [selectedSession?.ts_id]);
  
  // Update session state based on exercise states
  useEffect(() => {
    if (!selectedSession?.ts_id || !exercises || exercises.length === 0) {
      return;
    }
    
    // Filter out null/undefined exercises
    const validExercises = exercises.filter(ex => ex && ex !== null && ex !== undefined);
    if (validExercises.length === 0) {
      return;
    }
    
    // Check if any exercise is running
    const hasRunningExercise = validExercises.some(ex => {
      if (!ex || !ex.te_id) return false;
      const exState = exerciseStates[ex.te_id] || ex.te_state;
      return exState === "st202406071447279901";
    });
    
    // Check if all exercises are in a terminal state (finalized or cancelled)
    const allFinalized = validExercises.every((ex) => {
      if (!ex || !ex.te_id) return true;
      const exState = exerciseStates[ex.te_id] || ex.te_state;
      return (
        exState === TE_STATE_FINALIZED ||
        exState === TE_STATE_CANCELLED ||
        exState === "finalized" ||
        exState === "cancelled"
      );
    });
    
    // Check if any exercise is paused
    const hasPausedExercise = validExercises.some(ex => {
      if (!ex || !ex.te_id) return false;
      const exState = exerciseStates[ex.te_id] || ex.te_state;
      return exState === "st202406071447279902";
    });
    
    // Determine session state based on exercise states
    if (hasRunningExercise) {
      setSessionState("st202406071447279901"); // started
    } else if (hasPausedExercise) {
      setSessionState("st202406071447279902"); // paused
    } else if (allFinalized) {
      setSessionState("st202406071447279903"); // finalized
    } else {
      // Keep existing state or set to scheduled
      if (!sessionState) {
        setSessionState(selectedSession?.te_state || "st202406071447279900");
      }
    }
  }, [exercises, exerciseStates, selectedSession?.ts_id, selectedSession?.te_state, sessionState]);

  // Timer effect: Decrement timers every second for active exercises
  useEffect(() => {
    const intervalId = setInterval(() => {
      // Update exercise timers for active exercises
      setExerciseTimers(prev => {
        const updated = { ...prev };
        let hasUpdates = false;
        
        Object.keys(updated).forEach(te_id => {
          const exState = exerciseStates[te_id];
          const isRunning = exState === "st202406071447279901"; // "started"
          
          if (isRunning && updated[te_id] !== undefined && updated[te_id] !== null && updated[te_id] > 0) {
            updated[te_id] = Math.max(0, updated[te_id] - 1);
            hasUpdates = true;
          }
        });
        
        return hasUpdates ? updated : prev;
      });
      
      // Update sequence timers for active exercises
      setSequenceTimers(prev => {
        const updated = { ...prev };
        let hasUpdates = false;
        
        Object.keys(updated).forEach(te_id => {
          const exState = exerciseStates[te_id];
          const isRunning = exState === "st202406071447279901"; // "started"
          
          if (isRunning && updated[te_id] !== undefined && updated[te_id] !== null && updated[te_id] > 0) {
            updated[te_id] = Math.max(0, updated[te_id] - 1);
            hasUpdates = true;
          }
        });
        
        return hasUpdates ? updated : prev;
      });
    }, 1000); // Update every second
    
    return () => clearInterval(intervalId);
  }, [exerciseStates]); // Re-run when exercise states change

  // Handle table card drag start (for reordering tables visually)
  const handleTableDragStart = useCallback((tableIndex) => (event) => {
    // Do not start table drag when interacting with player slots (PC: stats/name/select are outside .table_player_label only)
    if (isInsidePlayerSlotTarget(event.target)) {
      event.preventDefault();
      return;
    }
    
    setDraggedTableIndex(tableIndex);
    event.dataTransfer.effectAllowed = "move";
    event.dataTransfer.setData("application/json", JSON.stringify({ type: "table", index: tableIndex }));
    // Add visual feedback
    event.currentTarget.style.opacity = "0.5";
  }, []);

  // Handle table card drag end
  const handleTableDragEnd = useCallback((event) => {
    event.currentTarget.style.opacity = "1";
    setDraggedTableIndex(null);
  }, []);

  // Handle table card drop (for reordering tables visually)
  const handleTableDrop = useCallback((targetIndex) => (event) => {
    if (isInsidePlayerSlotTarget(event.target)) {
      return;
    }
    
    event.preventDefault();
    event.stopPropagation();
    
    try {
      // Try to get table drag data
      let dragData;
      try {
        dragData = JSON.parse(event.dataTransfer.getData("application/json"));
      } catch (e) {
        // If it's not JSON, check if it's a player drag (dragContent format)
        const dragContent = event.dataTransfer.getData("dragContent");
        if (dragContent) {
          // This is a player drag, don't handle it here
          return;
        }
        return;
      }
      
      // Only handle table drops, ignore player label drops
      if (!dragData || dragData.type !== "table") {
        return;
      }
      
      const sourceIndex = parseInt(dragData.index);
      if (isNaN(sourceIndex) || sourceIndex === targetIndex) {
        return;
      }

      if (!tables || tables.length === 0) {
        return;
      }

      // Create a new array with reordered tables
      const newTables = [...tables];
      const [movedTable] = newTables.splice(sourceIndex, 1);
      newTables.splice(targetIndex, 0, movedTable);

      // Update the tables state with new order
      setTable(newTables);
      setDraggedTableIndex(null);
      
      console.log(`✅ Table reordered: moved from position ${sourceIndex + 1} to ${targetIndex + 1}`);
    } catch (error) {
      // If parsing fails, it might be a player drag, so just ignore
      console.log("Table drop ignored (might be player drag)");
    }
  }, [tables]);

  // Handle table card drag over
  const handleTableDragOver = useCallback((event) => {
    if (isInsidePlayerSlotTarget(event.target)) {
      return;
    }
    
    // Only prevent default for table drags (we can't check dataTransfer in dragover,
    // so we prevent default and let the drop handler decide)
    event.preventDefault();
    event.stopPropagation();
    event.dataTransfer.dropEffect = "move";
  }, []);

  // Calculate responsive sizes based on grid columns and screen size
  const getResponsiveStyles = () => {
    const baseSize = 169; // Base image size
    const baseFontSize = 40; // Base table number font size
    const basePlayerFontSize = 16; // Base player name font size (increased from 12)
    const baseStatsFontSize = 15; // Base stats font size (classement + pts row)
    
    // Check if we're on mobile
    const isMobile = typeof window !== 'undefined' && window.innerWidth <= 650;
    const isSmallMobile = typeof window !== 'undefined' && window.innerWidth <= 480;
    
    // Scale down as columns increase or on mobile
    let scale = 1;
    let playerNameScale = 1; // Separate scale for player names to keep them larger
    let statsScale = 1; // Separate scale for stats to keep them larger
    
    if (isSmallMobile) {
      // Very small mobile screens - reduce image significantly but keep text larger
      scale = 0.47; // ~80px (169 * 0.47)
      playerNameScale = 0.9; // Keep player names at 90% of base (larger than before)
      statsScale = 0.85; // Keep stats at 85% of base (larger than before)
    } else if (isMobile) {
      // Mobile screens - reduce image moderately but keep text larger
      scale = 0.59; // ~100px (169 * 0.59)
      playerNameScale = 0.95; // Keep player names at 95% of base (larger than before)
      statsScale = 0.9; // Keep stats at 90% of base (larger than before)
    } else if (gridColumns >= 4) {
      scale = 0.6; // 60% for 4+ columns
      playerNameScale = 0.85;
      statsScale = 0.8;
    } else if (gridColumns === 3) {
      scale = 0.7; // 70% for 3 columns
      playerNameScale = 0.9;
      statsScale = 0.85;
    } else if (gridColumns === 2) {
      scale = 0.85; // 85% for 2 columns
      playerNameScale = 0.95;
      statsScale = 0.9;
    }
    // 1 column keeps 100% (scale = 1) on desktop
    
    return {
      imageWidth: Math.round(baseSize * scale),
      tableNumberFontSize: Math.round(baseFontSize * scale),
      playerNameFontSize: Math.round(basePlayerFontSize * playerNameScale),
      statsFontSize: Math.round(baseStatsFontSize * statsScale),
      cardMinHeight: Math.round(230 * scale),
      cardPadding: isMobile ? (isSmallMobile ? '10px 5px' : '15px 8px') : (gridColumns >= 4 ? '10px 5px' : gridColumns === 3 ? '15px 8px' : '20px 10px'),
      playerContainerPadding: isMobile ? (isSmallMobile ? '0 2px' : '0 3px') : '0 5px', // Reduced padding on mobile
    };
  };
  
  const [responsiveStyles, setResponsiveStyles] = useState(getResponsiveStyles());
  
  // Update responsive styles when window is resized or gridColumns changes
  useEffect(() => {
    const handleResize = () => {
      setResponsiveStyles(getResponsiveStyles());
    };
    
    window.addEventListener('resize', handleResize);
    // Also update immediately when gridColumns changes
    setResponsiveStyles(getResponsiveStyles());
    
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [gridColumns]);

  useEffect(() => {
    const grId = resolveSessionGroupId(selectedSession, selectedgrp);
    if (selectedSession?.ts_id && grId) {
      // Load exercises first, then players; getplayerlist refreshes table assignments at the end
      // (avoids racing gettablelist with empty `players` state, which used to cap visible tables at 3).
      setselectedex(null);
      setAutoAssignAttempted(false);
      getexlist(selectedSession.ts_id).then(() => {
        getplayerlist(selectedSession.ts_id, grId);
      });
    } else {
      // Clear data if no session selected
      setexercise([]);
      setTable([]);
      setplayers([]);
      setselectedex(null);
    }
  }, [
    selectedSession?.ts_id,
    selectedSession?.gr_id,
    selectedSession?._groupId,
    selectedgrp?.groupDetails?.gr_id,
  ]);

  useEffect(() => {
    // Update current exercise index when selectedex changes first
    if (selectedex && exercises && exercises.length > 0) {
      const validExercises = exercises.filter(ex => ex && ex !== null && ex !== undefined);
      const index = validExercises.findIndex(ex => ex && ex.te_id === selectedex.te_id);
      if (index !== -1) {
        setCurrentExerciseIndex(index);
      }
    }
    
    // Check if exercises exist for this session
    const validExercises = exercises ? exercises.filter(ex => ex && ex !== null && ex !== undefined) : [];
    const hasExistingExercises = validExercises.length > 0 && validExercises.some(ex => ex && ex.te_id);
    
    if (hasExistingExercises) {
      // Exercises exist - load existing assignments (don't auto-assign by default)
      // Auto-assignment will happen in the dedicated useEffect
      setAutoAssignAttempted(true); // Prevent duplicate auto-assignment
      
    } else {
      // No exercises exist - allow auto-assignment for first exercise
      setAutoAssignAttempted(false);
    }
    
    // Don't immediately clear tables - preserve current state until new data is loaded
    // getplayerlist refreshes tables at the end (single groupTable call — avoids request storms)
    if (selectedSession?.ts_id && selectedgrp?.groupDetails?.gr_id) {
      getplayerlist(selectedSession.ts_id, selectedgrp.groupDetails.gr_id);
    }
    setselplayer();
    setselplayer2();
  }, [selectedex, exercises]);
  
  // Auto-assign players ONLY if no exercises (te_id) exist for this training session
  // If exercises exist, check if they need assignments and copy from first exercise if needed
  useEffect(() => {
    const loadOrAutoAssign = async () => {
      // Only proceed if we have a session and group
      if (!selectedSession?.ts_id || !selectedgrp?.groupDetails?.gr_id) {
        return;
      }
      
      // Prevent multiple simultaneous operations
      if (isAutoAssigning) {
        return;
      }
      
      // Check if exercises already exist for this session
      const hasExistingExercises = exercises && exercises.length > 0 && exercises.some(ex => ex.te_id);
      
      if (hasExistingExercises) {
        // Exercises exist - check if first exercise has assignments
        console.log(`📋 Exercises exist for this session (${exercises.length} exercises). Checking assignments...`);
        
        if (selectedex && selectedex.te_id) {
          // Fetch tables directly to check their current state
          try {
            const params = {
              gr_id: selectedgrp.groupDetails.gr_id,
              ts_id: selectedSession.ts_id,
              te_id: selectedex.te_id,
            };
            const tableResponse = await tablelist(params);
            const tablesArray = tableResponse.data?.data || [];
            
            // Check if current exercise has any assignments
            const hasAssignments = tablesArray.some(table => {
              const hasPlayer1 = table.player1 && 
                                typeof table.player1 === 'object' && 
                                Object.keys(table.player1).length > 0 &&
                                (table.player1.player_id || table.player1.user_first_name || table.player1.user_alias);
              const hasPlayer2 = table.player2 && 
                                typeof table.player2 === 'object' && 
                                Object.keys(table.player2).length > 0 &&
                                (table.player2.player_id || table.player2.user_first_name || table.player2.user_alias);
              return hasPlayer1 || hasPlayer2;
            });
            
            console.log(`🔍 Exercise ${selectedex.te_id} has assignments: ${hasAssignments}, tables: ${tablesArray.length}, players: ${players?.length || 0}, autoAssignAttempted: ${autoAssignAttempted}`);
            
            // Load tables to update state
          await gettablelist(selectedgrp.groupDetails.gr_id);
            
            // If viewing the first exercise
            if (selectedex.te_id === exercises[0]?.te_id) {
              if (hasAssignments) {
                const firstExercise = exercises[0];
                const firstState = resolveExerciseStateIdFromRow(
                  firstExercise,
                  exerciseStates
                );
                const firstIsFinished =
                  firstState === TE_STATE_FINALIZED ||
                  firstState === TE_STATE_CANCELLED;
                // Seed later unplayed exercises only. Never copy onto finished ones,
                // and never re-broadcast after the first exercise itself has finished.
                if (
                  exercises.length > 1 &&
                  !autoAssignAttempted &&
                  !firstIsFinished
                ) {
                  const following = followingExercisesForTableCopy(
                    exercises,
                    firstExercise.te_id
                  );
                  console.log(
                    `🔄 First exercise has assignments, copying to ${following.length} following exercise(s)...`
                  );
                  for (const nextExercise of following) {
                    try {
                      const copyParams = {
                        from_te_id: firstExercise.te_id,
                        to_te_id: nextExercise.te_id,
                        ts_id: selectedSession.ts_id,
                      };
                      await copyTableSetup(copyParams);
                      console.log(
                        `✅ Copied assignments from exercise 1 to ${nextExercise.te_id}`
                      );
                    } catch (error) {
                      console.error(
                        `❌ Error copying assignments to ${nextExercise.te_id}:`,
                        error
                      );
                    }
                  }
                  setAutoAssignAttempted(true);
                  await gettablelist(selectedgrp.groupDetails.gr_id);
                }
              } else {
                // First exercise has NO assignments - auto-assign players by ranking
                console.log(`🔍 Checking if should auto-assign: autoAssignAttempted=${autoAssignAttempted}, isAutoAssigning=${isAutoAssigning}, players=${players?.length || 0}, tables=${tablesArray.length}`);
                
                // Fetch players directly to ensure we have the latest data
                let playersData = players;
                if (!playersData || playersData.length === 0) {
                  console.log("⏳ Fetching players directly from API...");
                  try {
                    const playerParams = {
                      ts_id: selectedSession.ts_id,
                      gr_id: resolveSessionGroupId(selectedSession, selectedgrp),
                    };
                    if (!playerParams.gr_id) {
                      console.warn("⚠️ Auto-assign: missing gr_id, skipping player fetch");
                      setAutoAssignAttempted(true);
                      return;
                    }
                    const playerResponse = await grpplayerlist(playerParams);
                    const allPlayers = playerResponse.data.data || [];
                    playersData = allPlayers.filter(p => p.presensee === true);
                    // Sort by ranking
                    playersData.sort((a, b) => {
                      const pointsA = a.ranking_points || 0;
                      const pointsB = b.ranking_points || 0;
                      return pointsB - pointsA;
                    });
                    console.log(`✅ Fetched ${playersData.length} present players from API`);
                    // Update state
                    setplayers(playersData);
                  } catch (error) {
                    console.error("❌ Error fetching players:", error);
                    setAutoAssignAttempted(true);
                  }
                }
                
                if (!autoAssignAttempted && !isAutoAssigning && playersData && tablesArray.length > 0 && (playersData.length >= 2 || (playersData.length >= 1 && isSoloCapableSequenceExercise(selectedex)))) {
                  console.log("🔄 First exercise has no assignments, will auto-assign players by ranking...");
                  console.log(`📋 Players available: ${playersData.length}, Tables available: ${tablesArray.length}`);
                  // Set flag immediately to prevent multiple attempts
                  setAutoAssignAttempted(true);
                  
                  // Ensure players state is updated before calling handleAutoAssign
                  if (!players || players.length === 0) {
                    setplayers(playersData);
                    // Wait for state to update
                    await new Promise(resolve => setTimeout(resolve, 300));
                  }
                  
                  // Directly call handleAutoAssign if conditions are still met
                  // Use a timeout to ensure state is fully updated
                  setTimeout(async () => {
                    try {
                      console.log("⏰ Attempting auto-assignment...");
                      // Double-check that we're still on the first exercise and conditions are met
                      const currentSelectedEx = selectedex;
                      const currentExercises = exercises;
                      const currentPlayers = players || playersData;
                      
                      if (currentSelectedEx && 
                          currentSelectedEx.te_id && 
                          currentSelectedEx.te_id === currentExercises[0]?.te_id && 
                          currentPlayers && 
                          (currentPlayers.length >= 2 || (currentPlayers.length >= 1 && isSoloCapableSequenceExercise(currentSelectedEx))) && 
                          !isAutoAssigning) {
                        console.log("✅ All conditions met, calling handleAutoAssign...");
                        // Pass the fetched players data directly to avoid state timing issues
                        await handleAutoAssign(currentPlayers || playersData);
                      } else {
                        console.log("⚠️ Conditions changed, skipping auto-assignment:", {
                          hasSelectedEx: !!currentSelectedEx,
                          selectedExId: currentSelectedEx?.te_id,
                          firstExId: currentExercises[0]?.te_id,
                          isFirst: currentSelectedEx?.te_id === currentExercises[0]?.te_id,
                          playersCount: currentPlayers?.length || 0,
                          isAutoAssigning
                        });
                        // Reset flag so it can be retried
                        setAutoAssignAttempted(false);
                      }
                    } catch (error) {
                      console.error("❌ Error in auto-assignment:", error);
                      // Reset flag on error so it can be retried
                      setAutoAssignAttempted(false);
                    }
                  }, 1200);
                } else {
                  console.log("⚠️ Auto-assignment skipped:", { 
                    autoAssignAttempted, 
                    isAutoAssigning,
                    hasPlayers: !!playersData, 
                    playersCount: playersData?.length || 0, 
                    tablesCount: tablesArray.length 
                  });
                  // Mark as attempted even if we can't assign (to prevent retries)
                  if (tablesArray.length === 0 || !playersData || (playersData.length < 2 && !isSoloCapableSequenceExercise(selectedex))) {
                    setAutoAssignAttempted(true);
                  }
                }
              }
            } else {
              // Not the first exercise - if it has no assignments, copy from first exercise
              if (!hasAssignments && exercises.length > 0 && exercises[0]?.te_id) {
                console.log(`🔄 Exercise ${selectedex.te_id} has no assignments, copying from first exercise...`);
                try {
                  const copyParams = {
                    from_te_id: exercises[0].te_id,
                    to_te_id: selectedex.te_id,
                    ts_id: selectedSession.ts_id,
                  };
                  await copyTableSetup(copyParams);
                  console.log(`✅ Copied assignments from exercise 1 to current exercise`);
                  await gettablelist(selectedgrp.groupDetails.gr_id);
                } catch (error) {
                  console.error(`❌ Error copying assignments:`, error);
                }
              }
            }
            
            // Don't mark as attempted here - let the assignment functions do it
          } catch (error) {
            console.error("❌ Error checking table assignments:", error);
          }
        } else if (exercises.length > 0 && !selectedex?.te_id) {
          const nextExercise =
            exercises[
              pickCoachControlTargetIndex(exercises, exerciseStates)
            ] || exercises[0];
          if (nextExercise?.te_id) {
            setselectedex(nextExercise);
          }
        }
      } else {
        // No exercises exist yet - auto-assign only when first exercise is created and selected
        console.log("🔄 No exercises exist for this session. Will auto-assign when first exercise is created.");
        
        if (selectedex && selectedex.te_id && players && players.length >= 2 && tables && tables.length > 0) {
          // Check if this is the first exercise (index 0)
          const isFirstExercise = currentExerciseIndex === 0;
          
          if (isFirstExercise) {
            // More robust check for empty tables - check if ALL tables are completely empty
            const allEmpty = tables.every(table => {
              const hasPlayer1 = table.player1 && 
                                typeof table.player1 === 'object' && 
                                Object.keys(table.player1).length > 0 &&
                                (table.player1.player_id || table.player1.user_first_name || table.player1.user_alias);
              const hasPlayer2 = table.player2 && 
                                typeof table.player2 === 'object' && 
                                Object.keys(table.player2).length > 0 &&
                                (table.player2.player_id || table.player2.user_first_name || table.player2.user_alias);
              return !hasPlayer1 && !hasPlayer2;
            });
            
            // Clear any previous error messages before auto-assigning if tables are empty
            if (allEmpty) {
              setperror(null);
            }
            
            // If no players are assigned to any table, auto-assign them (only for first exercise when no exercises existed before)
            if (allEmpty && !autoAssignAttempted) {
              console.log("🔄 Auto-assigning players by ranking for first exercise (no exercises existed before, no players currently assigned)");
              setAutoAssignAttempted(true); // Mark as attempted to prevent re-runs
              // Use a small delay to ensure tables are fully loaded
              setTimeout(async () => {
                await handleAutoAssign();
              }, 500);
            }
          }
        }
      }
    };
    
    // Wait a bit for exercises and tables to load after selectedex changes
    const timer = setTimeout(() => {
      loadOrAutoAssign();
    }, 1000); // Increased delay to ensure data is fully loaded
    
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    // Removed 'tables' and 'tables?.length' from dependencies to prevent infinite loop
    // gettablelist() updates 'tables', which would trigger this useEffect again
  }, [selectedex?.te_id, players?.length, isAutoAssigning, currentExerciseIndex, autoAssignAttempted, exercises.length]);

  // Auto-assign players when entering the table setup tab
  // This effect triggers automatic assignment by ranking when the tab is first accessed
  useEffect(() => {
    loadSubgroups(selectedSession?.ts_id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSession?.ts_id]);

  useEffect(() => {
    const autoAssignOnTabEntry = async () => {
      // Only proceed if we have a session, group, selected exercise, players, and tables
      if (!selectedSession?.ts_id || !selectedgrp?.groupDetails?.gr_id || !selectedex?.te_id) {
        return;
      }
      
      // Prevent multiple simultaneous operations
      if (isAutoAssigning || autoAssignAttempted) {
        return;
      }
      
      // Wait a bit for tables and players to load
      setTimeout(async () => {
        try {
          // Fetch fresh table data to check current state
          const params = {
            gr_id: selectedgrp.groupDetails.gr_id,
            ts_id: selectedSession.ts_id,
            te_id: selectedex.te_id,
          };
          const tableResponse = await tablelist(params);
          let tablesArray = tableResponse.data?.data || [];
          
          console.log(`🔍 Auto-assign on tab entry - Tables received: ${tablesArray.length}`);
          console.log(`🔍 Auto-assign on tab entry - Full table response:`, JSON.stringify(tablesArray, null, 2));
          
          // Enrich tables with table_id if missing (using table_number to find it from other tables in the array)
          tablesArray = tablesArray.map(table => {
            // If table_id is missing but table_number exists, try to find it from another table with the same number
            if (!table.table_id && table.table_number) {
              const tableWithId = tablesArray.find(t => 
                t.table_id && t.table_number === table.table_number
              );
              if (tableWithId) {
                console.log(`✅ Found table_id ${tableWithId.table_id} for table_number ${table.table_number} from another table in array`);
                return { ...table, table_id: tableWithId.table_id };
              }
            }
            return table;
          });
          
          console.log(`🔍 Auto-assign on tab entry - Table details after enrichment:`, tablesArray.map(t => ({
            table_id: t.table_id,
            table_number: t.table_number,
            is_virtual: t.is_virtual,
            hasPlayer1: !!t.player1,
            hasPlayer2: !!t.player2,
            allKeys: Object.keys(t)
          })));
          
          // Check if we have physical tables (must have both table_id and table_number)
          const physicalTablesCheck = tablesArray.filter(table => {
            const hasTableId = table.table_id !== null && table.table_id !== undefined && table.table_id !== '';
            const hasTableNumber = table.table_number !== null && table.table_number !== undefined && table.table_number !== 0;
            const isValid = hasTableId && hasTableNumber;
            if (!isValid) {
              console.warn(`⚠️ Table missing required fields:`, {
                table_id: table.table_id,
                table_number: table.table_number,
                hasTableId,
                hasTableNumber,
                allKeys: Object.keys(table)
              });
            }
            return isValid;
          });
          
          console.log(`🔍 Auto-assign on tab entry - Physical tables: ${physicalTablesCheck.length} out of ${tablesArray.length}`);
          
          if (physicalTablesCheck.length === 0) {
            console.error("❌ Auto-assign on tab entry - No physical tables found! Tables must have both table_id and table_number.");
            console.error("❌ All tables received:", tablesArray);
            setAutoAssignAttempted(true);
            return;
          }
          
          // Check if tables have any assignments
          const hasAssignments = tablesArray.some(table => {
            const hasPlayer1 = table.player1 && 
                              typeof table.player1 === 'object' && 
                              Object.keys(table.player1).length > 0 &&
                              (table.player1.player_id || table.player1.user_first_name || table.player1.user_alias);
            const hasPlayer2 = table.player2 && 
                              typeof table.player2 === 'object' && 
                              Object.keys(table.player2).length > 0 &&
                              (table.player2.player_id || table.player2.user_first_name || table.player2.user_alias);
            return hasPlayer1 || hasPlayer2;
          });
          
          // If no assignments exist and we have players, auto-assign by ranking
          if (!hasAssignments && players && players.length >= 2 && physicalTablesCheck.length > 0) {
            console.log("🔄 Auto-assigning players by ranking on tab entry...");
            setAutoAssignAttempted(true);
            // Clear combo boxes
            setselplayer(null);
            setselplayer2(null);
            // Call handleAssignByRanking to assign players
            await handleAssignByRanking();
          } else if (hasAssignments) {
            // Tables already have assignments, just clear combo boxes
            setselplayer(null);
            setselplayer2(null);
            setAutoAssignAttempted(true);
          } else {
            console.log("⚠️ Auto-assign on tab entry - Conditions not met:", {
              hasAssignments,
              playersCount: players?.length || 0,
              physicalTablesCount: physicalTablesCheck.length
            });
            setAutoAssignAttempted(true);
          }
        } catch (error) {
          console.error("❌ Error in auto-assignment on tab entry:", error);
          setAutoAssignAttempted(true); // Mark as attempted to prevent retries
        }
      }, 1500); // Wait 1.5 seconds for data to load
    };
    
    autoAssignOnTabEntry();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSession?.ts_id, selectedgrp?.groupDetails?.gr_id, selectedex?.te_id]);

  const gettablelist = async (id, presentPlayerIdsOptional = null) => {
    if (!id || !selectedSession?.ts_id) {
      console.warn("⚠️ gettablelist - Missing required parameters:", { id, ts_id: selectedSession?.ts_id });
      setTable([]);
      return;
    }

    const params = {
      gr_id: id,
      ts_id: selectedSession.ts_id,
      te_id: selectedex?.te_id || null,
    };
    if (!selectedex?.te_id) {
      delete params.te_id;
    }
    if (!params.gr_id || !params.ts_id) {
      console.warn("⚠️ gettablelist - Missing required parameters (gr_id or ts_id), skipping API call");
      setTable([]);
      return;
    }

    const presentKey =
      presentPlayerIdsOptional instanceof Set
        ? [...presentPlayerIdsOptional].sort().join(",")
        : "";
    const fetchKey = `${params.gr_id}|${params.ts_id}|${params.te_id || ""}|${presentKey}`;
    if (tableListInFlightRef.current?.key === fetchKey) {
      return tableListInFlightRef.current.promise;
    }

    const run = async () => {
    try {
      console.log("🔍 gettablelist - Request params:", params);
      const response = await tablelist(params);
      console.log("🔍 gettablelist - Full API response:", response);
      console.log("🔍 gettablelist - Response data:", response.data);
      console.log("🔍 gettablelist - Response data.data:", response.data?.data);
      
      const tablesArray = response.data?.data || [];
      const sortedTables = normalizeTablesList(tablesArray);
      
      // Do NOT slice: stale ts_number_of_tables_required + gettablelist racing before `players` loads
      // used to cap at 3 table cards. Show every physical table the API returns (room/group limits apply server-side).
      const presentCount = players?.length || 0;
      console.log(
        `🔍 gettablelist - all ${sortedTables.length} table(s); present players in state=${presentCount}; session ts_number_of_tables_required=${selectedSession?.ts_number_of_tables_required}`
      );

      // DO NOT create virtual tables - tables must exist in the training room (created when room was created)
      if (sortedTables.length === 0) {
        console.warn(`⚠️ gettablelist - No tables returned from API. Tables should be created when the training room is created.`);
      }

      const tablesForUi =
        presentPlayerIdsOptional instanceof Set
          ? sanitizeTablesToSessionPresence(sortedTables, presentPlayerIdsOptional)
          : sortedTables;

      setTable(tablesForUi);
      console.log("🔍 gettablelist - Loaded tables:", tablesForUi.length, tablesForUi);
      // Log table data structure for debugging
      if (tablesForUi.length > 0) {
        tablesForUi.forEach((table, idx) => {
          console.log(`🔍 gettablelist - Table ${idx + 1} (${table.table_number || idx + 1}):`, {
            table_id: table.table_id,
            table_number: table.table_number,
            player1: table.player1,
            player2: table.player2,
            player1_type: typeof table.player1,
            player2_type: typeof table.player2,
            player1_null: table.player1 === null,
            player2_null: table.player2 === null,
            player1_keys: table.player1 && typeof table.player1 === 'object' ? Object.keys(table.player1) : 'N/A',
            player2_keys: table.player2 && typeof table.player2 === 'object' ? Object.keys(table.player2) : 'N/A',
          });
        });
      }
      
      if (sortedTables.length === 0) {
        console.warn("⚠️ gettablelist - No tables available. Response structure:", {
          status: response.data?.status,
          message: response.data?.message,
          dataLength: tablesArray.length,
          gr_number_of_tables: selectedgrp?.groupDetails?.gr_number_of_tables,
          fullResponse: response.data
        });
      }
      
      // If no tables returned but group has gr_number_of_tables, log a warning
      if (sortedTables.length === 0 && selectedgrp?.groupDetails?.gr_number_of_tables > 0) {
        console.warn(`⚠️ No tables returned but group has gr_number_of_tables=${selectedgrp?.groupDetails?.gr_number_of_tables}`);
      }
    } catch (error) {
      console.error("❌ Error loading tables:", error);
      setTable([]);
    }
    };

    const promise = run();
    tableListInFlightRef.current = { key: fetchKey, promise };
    try {
      await promise;
    } finally {
      if (tableListInFlightRef.current?.key === fetchKey) {
        tableListInFlightRef.current = null;
      }
    }
  };
  const getexlist = async (id) => {
    try {
      const params = {
        ts_id: id,
      };
      const response = await sessionexlist(params);
      
      // Ensure response.data.data is an array and filter out null/undefined items
      const exercisesData = Array.isArray(response.data.data) ? response.data.data : [];
      const exercisel = exercisesData.filter(isSessionExerciseForTableSetup);
      
      // Sort exercises by te_order
      exercisel.sort((a, b) => {
        const orderA = a.te_order || 0;
        const orderB = b.te_order || 0;
        return orderA - orderB;
      });

      // Ensure sequences run in order 1 to N by sequence_order
      const normalized = exercisel.map((ex) => ({
        ...ex,
        sequences: (ex.sequences || []).slice().sort((a, b) => (a.sequence_order ?? 9999) - (b.sequence_order ?? 9999)),
      }));

      setexercise(normalized);

      if (normalized.length > 0) {
        setselectedex((prev) => {
          if (prev?.te_id && normalized.some((ex) => ex.te_id === prev.te_id)) {
            return prev;
          }
          return (
            normalized[
              pickCoachControlTargetIndex(normalized, exerciseStates)
            ] || normalized[0]
          );
        });
      } else {
        setselectedex(undefined);
      }

      return normalized;
    } catch (error) {
      console.log(error);
      setexercise([]);
      return [];
    }
  };

  const loadSubgroups = async (tsId) => {
    if (!tsId) {
      setSessionSubgroups([]);
      return [];
    }
    try {
      const response = await listSessionSubgroups({ ts_id: tsId });
      const rows = response?.data?.data || [];
      setSessionSubgroups(Array.isArray(rows) ? rows : []);
      return rows;
    } catch (err) {
      console.warn("TableSetup subgroups:", err?.message || err);
      setSessionSubgroups([]);
      return [];
    }
  };

  const getplayerlist = async (id, id2) => {
    try {
      const ts_id = id;
      const gr_id =
        id2 || resolveSessionGroupId(selectedSession, selectedgrp);
      if (!ts_id || !gr_id) {
        console.warn("⚠️ getplayerlist - Missing gr_id or ts_id, skipping API call", {
          ts_id,
          gr_id,
        });
        setplayers([]);
        setAllSessionPlayers([]);
        return;
      }
      const params = {
        ts_id,
        gr_id,
        //te_id: selectedex.te_id,
      };
      // const response = await tableplayerlist(params);
      const response = await grpplayerlist(params);
      const allPlayers = Array.isArray(response.data.data)
        ? response.data.data
        : [];
      setAllSessionPlayers(allPlayers);
      const presentplayer = allPlayers.filter(
        (i) => i.presensee === true
      );
      // Sort players by ranking_points (highest to lowest)
      presentplayer.sort((a, b) => {
        const pointsA = a.ranking_points || 0;
        const pointsB = b.ranking_points || 0;
        return pointsB - pointsA; // Descending order (highest first)
      });
      
      // Detect new players by comparing with previous list
      const previousPlayers = previousPlayersRef.current || [];
      const newPlayers = presentplayer.filter(
        newPlayer => !previousPlayers.some(
          prevPlayer => prevPlayer.player_id === newPlayer.player_id
        )
      );
      
      // Update previous players reference
      previousPlayersRef.current = presentplayer;
      
      setplayers(presentplayer);

      if (selectedgrp?.groupDetails?.gr_id) {
        const presentIdSet = new Set(
          presentplayer
            .map((p) => sessionPresencePlayerKey(p?.player_id))
            .filter(Boolean)
        );
        await gettablelist(selectedgrp.groupDetails.gr_id, presentIdSet);
      }
      
      // Auto-assign new players if there are any and we have a selected exercise
      // Only auto-assign if this is not the initial load (previousPlayers.length > 0)
      // This ensures first-time auto-assignment is handled by handleAutoAssign
      if (newPlayers.length > 0 && selectedex?.te_id && !isAutoAssigning && previousPlayers.length > 0) {
        console.log(`🔄 New players detected: ${newPlayers.length}, auto-assigning...`);
        await handleAutoAssignNewPlayer(newPlayers);
      }
      
      return presentplayer;
    } catch (error) {
      console.log(error);
      return [];
    }
  };

  useEffect(() => {
    const onRefresh = () => {
      const grId = selectedgrp?.groupDetails?.gr_id;
      if (grId) gettablelist(grId);
      if (selectedSession?.ts_id && grId) {
        getplayerlist(selectedSession.ts_id, grId);
      }
    };
    window.addEventListener(PLANNING_REFRESH_EVENT, onRefresh);
    return () => window.removeEventListener(PLANNING_REFRESH_EVENT, onRefresh);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSession?.ts_id, selectedgrp?.groupDetails?.gr_id, selectedex?.te_id]);

  const handleadd = async (tableid) => {
    if (selplayer && selplayer2) {
      try {
        const params = {
          player1: selplayer?.id,
          player2: selplayer2?.id,
          table_id: tableid,
          ts_id: selectedSession.ts_id,
          te_id: selectedex.te_id,
        };
        const response = await playeradtable(params);
        gettablelist(selectedgrp?.groupDetails?.gr_id);
        getplayerlist(selectedSession.ts_id, selectedgrp?.groupDetails?.gr_id);
      } catch (error) {
        console.log(error);
      }
    } else {
      setperror(`${t("Please_select_plyr")}`);
    }
  };

  const handlesetplayer1 = (e, table) => {
    if (e.target.value !== selplayer2?.id) {
      setselplayer({
        type: "player1",
        id: e.target.value,
        tableid: table.table_id,
      });
    }
    if (selplayer2?.tableid !== table.table_id) {
      setselplayer2("");
    }
  };
  const handlesetplayer2 = (e, table) => {
    if (e.target.value !== selplayer?.id) {
      setselplayer2({
        type: "player2",
        id: e.target.value,
        tableid: table.table_id,
      });
    }

    if (selplayer?.tableid !== table.table_id) {
      setselplayer("");
    }
  };
  const playerSlotToApiPosition = (slot) =>
    slot === "player1" ? "Player1" : slot === "player3" ? "Player3" : "Player2";

  const swapBoxes = async (fromTable, fromPlayerType, toTable, toPlayerType) => {
    // Check if swap is disabled
    if (isCurrentExerciseLocked) {
      setperror(t("cannot_swap_exercise_locked"));
      console.warn("⚠️ Swap disabled: exercise is running or finalized");
      return;
    }
    
    const fromPlayer = fromTable[fromPlayerType];
    const toPlayer = toTable[toPlayerType];

    if (!fromPlayer || !fromPlayer.player_id) {
      console.error("❌ Cannot swap: source player is missing");
      return;
    }

      try {
      // Swap players for the current exercise
        const params = {
        ex_table_id: fromTable.table_id,
        ex_player_id: fromPlayer.player_id,
        ex_player_position: playerSlotToApiPosition(fromPlayerType),
        ex_ts_id: selectedSession?.ts_id,
        ex_te_id: selectedex?.te_id,
        rep_table_id: toTable.table_id,
        rep_player_id: toPlayer?.player_id || null,
        rep_player_position: playerSlotToApiPosition(toPlayerType),
        rep_ts_id: selectedSession?.ts_id,
        rep_te_id: selectedex?.te_id,
      };

      console.log(`🔄 Swapping players for exercise ${selectedex?.te_id}:`, {
        from: `${fromTable.table_number} ${fromPlayerType} (${fromPlayer.user_alias || fromPlayer.user_first_name})`,
        to: `${toTable.table_number} ${toPlayerType} (${toPlayer?.user_alias || toPlayer?.user_first_name || 'empty'})`
      });

      await playerswaptable(params);
      
      // Refresh exercises list first to get updated sequences with correct order numbers
      if (selectedSession?.ts_id) {
        const refreshedExercises = await getexlist(selectedSession.ts_id);
        // Update selectedex with fresh data to reflect updated sequences
        if (selectedex?.te_id && refreshedExercises && refreshedExercises.length > 0) {
          const updatedExercise = refreshedExercises.find(ex => ex.te_id === selectedex.te_id);
          if (updatedExercise) {
            setselectedex(updatedExercise);
          }
        }
      }
      
      // After swapping, copy the new setup only to later unfinished exercises
      const followingAfterSwap = followingExercisesForTableCopy(
        exercises,
        selectedex?.te_id
      );
      if (followingAfterSwap.length > 0) {
        console.log(
          `🔄 Updating ${followingAfterSwap.length} following exercise(s) after swap...`
        );
        
        // Get the updated table setup for the current exercise
        await gettablelist(selectedgrp?.groupDetails?.gr_id);
        
        // Wait a bit for tables to refresh
        await new Promise(resolve => setTimeout(resolve, 300));
        
        for (const nextExercise of followingAfterSwap) {
          try {
            const copyParams = {
              from_te_id: selectedex.te_id,
              to_te_id: nextExercise.te_id,
              ts_id: selectedSession.ts_id,
            };
            console.log(
              `📋 Copying table setup from ${selectedex.te_id} to ${nextExercise.te_id}...`
            );
            await copyTableSetup(copyParams);
          } catch (copyError) {
            console.error(
              `❌ Error copying setup to ${nextExercise.te_id}:`,
              copyError
            );
          }
        }
        console.log(`✅ Updated following exercises after swap`);
        
        // Refresh exercises list again after copying to subsequent exercises
        if (selectedSession?.ts_id) {
          await getexlist(selectedSession.ts_id);
        }
      }
      
      // Refresh tables to show updated state
      await gettablelist(selectedgrp?.groupDetails?.gr_id);
      
      setcname("table_player_name");
      setcname2("table_player_name");
    } catch (error) {
      console.error("❌ Error swapping players:", error);
      setperror(error.response?.data?.message || error.message || t("error_swapping_players"));
    }
  };

  const handleDragStart = useCallback(
    (data, type) => (event) => {
      if (isCurrentExerciseLocked) {
        event.preventDefault();
        return;
      }
      
      // Stop propagation to prevent table drag from interfering
      event.stopPropagation();
      event.dataTransfer.setData("dragContent", JSON.stringify({ data, type }));
      event.dataTransfer.setData("text/plain", "player-swap");
      event.dataTransfer.effectAllowed = "move";
      setcname("table_player_name draggable");
      setcname2("table_player_name draggable");
      setfrom(type);
      
      // Set dragging state for visual feedback
      if (data && data.player_id) {
        setDraggingPlayerId(data.player_id);
      }
      
      // Add visual feedback to the dragged element
      if (event.currentTarget) {
        event.currentTarget.style.opacity = "0.5";
        event.currentTarget.style.transform = "scale(0.95)";
        event.currentTarget.style.cursor = "grabbing";
      }
    },
    [isCurrentExerciseLocked]
  );

  const handleDragOver = useCallback(
    (table, playerType) => (event) => {
      if (isCurrentExerciseLocked) return;
      
      // Stop propagation to prevent table drag over from interfering
      event.stopPropagation();
      event.preventDefault();
      event.dataTransfer.dropEffect = "move";
      
      // Set drag over state for visual feedback (styles applied via JSX)
      setDragOverTableId(table.table_id);
      setDragOverPlayerType(playerType);
    },
    [isCurrentExerciseLocked]
  );
  
  const handleDragLeave = useCallback((event) => {
    // Remove visual feedback when leaving drop zone
    // Small delay to prevent flickering when moving between child elements
    setTimeout(() => {
      setDragOverTableId(null);
      setDragOverPlayerType(null);
    }, 50);
  }, []);

  const handleDrop = useCallback(
    (toTable, toPlayerType) => (event) => {
      if (isCurrentExerciseLocked) return;
      
      // Stop propagation to prevent table drop from interfering
      event.stopPropagation();
      event.preventDefault();
      
      // Remove visual feedback
      if (event.currentTarget) {
        event.currentTarget.style.backgroundColor = "";
        event.currentTarget.style.borderRadius = "";
      }
      setDragOverTableId(null);
      setDragOverPlayerType(null);
      
      try {
        const raw = event.dataTransfer.getData("dragContent");
        if (!raw) return;
        const dragContent = JSON.parse(raw);
        const fromPlayer = dragContent?.data; // The player object being dragged
        const fromPlayerType = dragContent?.type; // "player1" or "player2"

        if (!fromPlayer || !fromPlayer.player_id || !fromPlayerType) {
          console.error("❌ Invalid player data in drag");
          return;
        }

        // Find the table that contains the player being dragged
        const fromTable = tables.find(t => {
          const player = t[fromPlayerType];
          return player && player.player_id === fromPlayer.player_id;
        });

        if (!fromTable) {
          console.error("❌ Could not find source table for dragged player");
          return;
        }

        // Swap/move if dropping on a different table or different player position (or empty slot)
        if (fromTable.table_id !== toTable.table_id || fromPlayerType !== toPlayerType) {
          console.log(`🔄 Dropping player ${fromPlayerType} from table ${fromTable.table_number} to ${toPlayerType} on table ${toTable.table_number}`);
          swapBoxes(fromTable, fromPlayerType, toTable, toPlayerType);
        }
      } catch (error) {
        console.error("❌ Error in handleDrop:", error);
      }
    },
    [tables, swapBoxes, isCurrentExerciseLocked]
  );
  
  const handleDragEnd = useCallback((event) => {
    // Reset all dragging states
    setDraggingPlayerId(null);
    setDragOverTableId(null);
    setDragOverPlayerType(null);
    
    // Remove visual feedback from the dragged element
    if (event.currentTarget) {
      event.currentTarget.style.opacity = "";
      event.currentTarget.style.transform = "";
      event.currentTarget.style.cursor = "";
    }
    
    // Reset class names
    setcname("table_player_name");
    setcname2("table_player_name");
  }, []);

  const handleOpenPlayerProfile = useCallback((player) => {
    if (!player || personalizeMode) return;
    const uid = player.user_id;
    const pid = player.player_id;
    if (!uid && !pid) return;
    setPlayerProfileUserId(uid || null);
    setPlayerProfilePlayerId(!uid ? pid : null);
    setPlayerProfileOpen(true);
  }, [personalizeMode]);

  const tryProfileDoubleTapFromTouch = useCallback(
    (player) => {
      if (!player || personalizeMode) return false;
      const key = player.user_id || player.player_id;
      if (!key) return false;
      const now = Date.now();
      const prev = profileTouchTapRef.current;
      if (prev.key === String(key) && now - prev.t < PROFILE_DOUBLE_TAP_MS) {
        profileTouchTapRef.current = { key: "", t: 0 };
        handleOpenPlayerProfile(player);
        return true;
      }
      profileTouchTapRef.current = { key: String(key), t: now };
      return false;
    },
    [personalizeMode, handleOpenPlayerProfile]
  );

  const handleTouchEndProfileOnly = useCallback(
    (player) => (event) => {
      if (personalizeMode) return;
      event.stopPropagation();
      tryProfileDoubleTapFromTouch(player);
    },
    [personalizeMode, tryProfileDoubleTapFromTouch]
  );

  // Touch player swap: ref drives logic; UI state drives hold / drag / drop-target visuals
  const touchDragRef = useRef(null);
  const touchStartTimeoutRef = useRef(null);
  const [touchPlayerSwapUi, setTouchPlayerSwapUi] = useState(null);
  const [touchDropHighlight, setTouchDropHighlight] = useState(null);
  const [allowHtml5PlayerDrag, setAllowHtml5PlayerDrag] = useState(true);
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const mq = window.matchMedia("(pointer: coarse)");
    const sync = () => setAllowHtml5PlayerDrag(!mq.matches);
    sync();
    mq.addEventListener("change", sync);
    return () => mq.removeEventListener("change", sync);
  }, []);

  const resolveSwapDropFromPoint = useCallback(
    (clientX, clientY) => {
      const el = document.elementFromPoint(clientX, clientY);
      if (!el) return null;
      const dropContainer = el.closest("[data-slot]");
      if (!dropContainer) return null;
      const ds = dropContainer.getAttribute("data-slot");
      const targetPlayerType =
        ds === "player2" ? "player2" : ds === "player3" ? "player3" : "player1";
      const tableCard = dropContainer.closest(".table_setups");
      if (!tableCard || !tables?.length) return null;
      const tableIndexAttr = tableCard.getAttribute("data-table-index");
      const tableIndex =
        tableIndexAttr !== null && tableIndexAttr !== undefined
          ? parseInt(tableIndexAttr, 10)
          : Array.from(tableCard.parentElement?.children || []).indexOf(tableCard);
      if (tableIndex < 0 || !tables[tableIndex]) return null;
      return { table: tables[tableIndex], playerType: targetPlayerType };
    },
    [tables]
  );

  const clearTouchPlayerSwap = useCallback(() => {
    if (touchStartTimeoutRef.current) {
      clearTimeout(touchStartTimeoutRef.current);
      touchStartTimeoutRef.current = null;
    }
    const prev = touchDragRef.current;
    if (prev?.element) {
      prev.element.style.opacity = "";
      prev.element.style.transform = "";
      prev.element.style.transition = "";
    }
    touchDragRef.current = null;
    setTouchPlayerSwapUi(null);
    setTouchDropHighlight(null);
    setDraggingPlayerId(null);
  }, []);

  const handleTouchStart = useCallback(
    (player, playerType, table) => (event) => {
      if (isCurrentExerciseLocked) return;

      event.stopPropagation();
      const touch = event.touches[0];
      if (!touch) return;

      clearTouchPlayerSwap();

      const dragState = {
        player,
        playerType,
        fromTable: table,
        startX: touch.clientX,
        startY: touch.clientY,
        startTime: Date.now(),
        lastTouchX: touch.clientX,
        lastTouchY: touch.clientY,
        element: event.currentTarget,
        isDragging: false,
      };
      touchDragRef.current = dragState;
      setTouchDropHighlight(null);
      setTouchPlayerSwapUi({
        phase: "holding",
        sourceTableId: table.table_id,
        sourcePlayerType: playerType,
        sourcePlayerId: player?.player_id,
      });

      touchStartTimeoutRef.current = setTimeout(() => {
        const cur = touchDragRef.current;
        if (!cur || cur.element !== event.currentTarget || cur.isDragging) return;
        cur.isDragging = true;
        if (player?.player_id) {
          setDraggingPlayerId(player.player_id);
        }
        setTouchPlayerSwapUi({
          phase: "dragging",
          sourceTableId: table.table_id,
          sourcePlayerType: playerType,
          sourcePlayerId: player?.player_id,
        });
        const drop = resolveSwapDropFromPoint(
          cur.lastTouchX ?? cur.startX,
          cur.lastTouchY ?? cur.startY
        );
        if (drop?.table?.table_id) {
          setTouchDropHighlight({
            tableId: drop.table.table_id,
            playerType: drop.playerType,
          });
        }
        touchStartTimeoutRef.current = null;
      }, PLAYER_SWAP_HOLD_MS);
    },
    [isCurrentExerciseLocked, clearTouchPlayerSwap, resolveSwapDropFromPoint]
  );

  const handleTouchEndOnLabel = useCallback(
    (toTable, toPlayerType) => (event) => {
      if (isCurrentExerciseLocked) return;
      const dragState = touchDragRef.current;
      if (!dragState) {
        clearTouchPlayerSwap();
        return;
      }

      event.stopPropagation();
      event.preventDefault();
      // Do not stopImmediatePropagation: on iOS touchend often targets the drag source,
      // so the global handler (using elementFromPoint) must still run to find the real drop target.

      const touch = event.changedTouches[0];
      if (!touch) {
        clearTouchPlayerSwap();
        return;
      }

      const { player, playerType: fromPlayerType, startX, startY, startTime } = dragState;

      const deltaX = Math.abs(touch.clientX - startX);
      const deltaY = Math.abs(touch.clientY - startY);
      const deltaTime = Date.now() - startTime;

      if (touchStartTimeoutRef.current) {
        clearTimeout(touchStartTimeoutRef.current);
        touchStartTimeoutRef.current = null;
      }

      // Released before hold completed: scroll / tap — no swap
      if (!dragState.isDragging) {
        if (dragState.element) {
          dragState.element.style.opacity = "";
          dragState.element.style.transform = "";
          dragState.element.style.transition = "";
        }
        if (
          deltaX < PLAYER_SWAP_CANCEL_MOVE_PX &&
          deltaY < PLAYER_SWAP_CANCEL_MOVE_PX &&
          deltaTime < PLAYER_SWAP_HOLD_MS
        ) {
          tryProfileDoubleTapFromTouch(player);
        }
        touchDragRef.current = null;
        setTouchPlayerSwapUi(null);
        setTouchDropHighlight(null);
        return;
      }

      // Armed drag: visuals reset in global touchend; do not clear ref here if same slot (iOS)
      try {
        if (!player || !player.player_id) {
          clearTouchPlayerSwap();
          return;
        }

        const fromTableFound = tables.find((tbl) => {
          const tablePlayer = tbl[fromPlayerType];
          return tablePlayer && tablePlayer.player_id === player.player_id;
        });

        if (!fromTableFound) {
          clearTouchPlayerSwap();
          return;
        }

        if (fromTableFound.table_id !== toTable.table_id || fromPlayerType !== toPlayerType) {
          swapBoxes(fromTableFound, fromPlayerType, toTable, toPlayerType);
          clearTouchPlayerSwap();
          return;
        }
        return;
      } catch (error) {
        console.error("❌ Error in handleTouchEndOnLabel:", error);
      }

      clearTouchPlayerSwap();
    },
    [tables, swapBoxes, tryProfileDoubleTapFromTouch, clearTouchPlayerSwap, isCurrentExerciseLocked]
  );

  // Global touch: cancel hold on scroll; after hold, block scroll and track drop target
  useEffect(() => {
    const handleGlobalTouchMove = (event) => {
      const dragState = touchDragRef.current;
      if (!dragState) return;
      const touch = event.touches[0];
      if (!touch) return;
      dragState.lastTouchX = touch.clientX;
      dragState.lastTouchY = touch.clientY;

      if (!dragState.isDragging) {
        const dx = Math.abs(touch.clientX - dragState.startX);
        const dy = Math.abs(touch.clientY - dragState.startY);
        if (dx > PLAYER_SWAP_CANCEL_MOVE_PX || dy > PLAYER_SWAP_CANCEL_MOVE_PX) {
          if (touchStartTimeoutRef.current) {
            clearTimeout(touchStartTimeoutRef.current);
            touchStartTimeoutRef.current = null;
          }
          if (dragState.element) {
            dragState.element.style.opacity = "";
            dragState.element.style.transform = "";
            dragState.element.style.transition = "";
          }
          touchDragRef.current = null;
          setTouchPlayerSwapUi(null);
          setTouchDropHighlight(null);
        }
        return;
      }

      event.preventDefault();
      const drop = resolveSwapDropFromPoint(touch.clientX, touch.clientY);
      if (drop?.table?.table_id) {
        setTouchDropHighlight({
          tableId: drop.table.table_id,
          playerType: drop.playerType,
        });
      } else {
        setTouchDropHighlight(null);
      }
    };

    const handleGlobalTouchEnd = (event) => {
      const dragState = touchDragRef.current;
      if (!dragState) return;

      if (isCurrentExerciseLocked) {
        if (touchStartTimeoutRef.current) {
          clearTimeout(touchStartTimeoutRef.current);
          touchStartTimeoutRef.current = null;
        }
        if (dragState.element) {
          dragState.element.style.opacity = "";
          dragState.element.style.transform = "";
          dragState.element.style.transition = "";
        }
        touchDragRef.current = null;
        setTouchPlayerSwapUi(null);
        setTouchDropHighlight(null);
        setDraggingPlayerId(null);
        return;
      }

      const touch = event.changedTouches[0];
      if (!touch) {
        if (touchStartTimeoutRef.current) {
          clearTimeout(touchStartTimeoutRef.current);
          touchStartTimeoutRef.current = null;
        }
        if (dragState.element) {
          dragState.element.style.opacity = "";
          dragState.element.style.transform = "";
          dragState.element.style.transition = "";
        }
        touchDragRef.current = null;
        setTouchPlayerSwapUi(null);
        setTouchDropHighlight(null);
        setDraggingPlayerId(null);
        return;
      }

      if (touchStartTimeoutRef.current) {
        clearTimeout(touchStartTimeoutRef.current);
        touchStartTimeoutRef.current = null;
      }

      if (dragState.element) {
        dragState.element.style.opacity = "";
        dragState.element.style.transform = "";
        dragState.element.style.transition = "";
      }

      const { player, playerType: fromPlayerType } = dragState;

      if (!dragState.isDragging) {
        touchDragRef.current = null;
        setTouchPlayerSwapUi(null);
        setTouchDropHighlight(null);
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      try {
        if (!player || !player.player_id) {
          touchDragRef.current = null;
          setTouchPlayerSwapUi(null);
          setTouchDropHighlight(null);
          setDraggingPlayerId(null);
          return;
        }

        const drop = resolveSwapDropFromPoint(touch.clientX, touch.clientY);
        if (!drop) {
          touchDragRef.current = null;
          setTouchPlayerSwapUi(null);
          setTouchDropHighlight(null);
          setDraggingPlayerId(null);
          return;
        }

        const { table: targetTable, playerType: targetPlayerType } = drop;

        const fromTableFound = tables.find((tbl) => {
          const tablePlayer = tbl[fromPlayerType];
          return tablePlayer && tablePlayer.player_id === player.player_id;
        });

        if (!fromTableFound) {
          touchDragRef.current = null;
          setTouchPlayerSwapUi(null);
          setTouchDropHighlight(null);
          setDraggingPlayerId(null);
          return;
        }

        if (
          fromTableFound.table_id !== targetTable.table_id ||
          fromPlayerType !== targetPlayerType
        ) {
          swapBoxes(fromTableFound, fromPlayerType, targetTable, targetPlayerType);
        }
      } catch (error) {
        console.error("❌ Error in handleGlobalTouchEnd:", error);
      }

      touchDragRef.current = null;
      setTouchPlayerSwapUi(null);
      setTouchDropHighlight(null);
      setDraggingPlayerId(null);
    };

    document.addEventListener("touchmove", handleGlobalTouchMove, { passive: false });
    document.addEventListener("touchend", handleGlobalTouchEnd, { passive: false });
    document.addEventListener("touchcancel", handleGlobalTouchEnd, { passive: false });

    return () => {
      document.removeEventListener("touchmove", handleGlobalTouchMove);
      document.removeEventListener("touchend", handleGlobalTouchEnd);
      document.removeEventListener("touchcancel", handleGlobalTouchEnd);
      if (touchStartTimeoutRef.current) {
        clearTimeout(touchStartTimeoutRef.current);
        touchStartTimeoutRef.current = null;
      }
    };
  }, [tables, swapBoxes, isCurrentExerciseLocked, resolveSwapDropFromPoint]);

  // Copy table setup to next or previous exercise
  const handleCopySetup = async (direction) => {
    if (!selectedex || exercises.length <= 1) {
      setperror(t("no_exercises_to_copy") || "No other exercises to copy to");
      return;
    }
    if (isKotExercise(selectedex)) {
      setperror(
        t("te_copy_setup_not_for_kot") ||
          "Copy table setup is not available for King of the Table (rounds) exercises."
      );
      return;
    }

    try {
      let targetIndex;
      if (direction === "next") {
        targetIndex = (currentExerciseIndex + 1) % exercises.length;
      } else {
        targetIndex = (currentExerciseIndex - 1 + exercises.length) % exercises.length;
      }

      const targetExercise = exercises[targetIndex];
      if (!targetExercise || !targetExercise.te_id) {
        setperror(t("target_exercise_not_found"));
        return;
      }

      const targetState = resolveExerciseStateIdFromRow(
        targetExercise,
        exerciseStates
      );
      if (
        targetState === TE_STATE_FINALIZED ||
        targetState === TE_STATE_CANCELLED
      ) {
        setperror(
          t("table_setup_copy_skipped_finished") ||
            "Cannot copy table setup onto a finished exercise"
        );
        return;
      }

      const params = {
        from_te_id: selectedex.te_id,
        to_te_id: targetExercise.te_id,
        ts_id: selectedSession.ts_id,
      };

      const response = await copyTableSetup(params);
      
      // Refresh tables to show the copied setup if we're viewing the target exercise
      // Otherwise, just show a success message
      if (targetExercise.te_id === selectedex.te_id) {
        await gettablelist(selectedgrp?.groupDetails?.gr_id);
      }
      
      setperror(null);
      // Show success message (could use a toast notification)
      const copiedCount = response?.data?.data?.copied || 0;
      console.log(`✅ Table setup copied to ${direction} exercise (${copiedCount} assignments)`);
    } catch (error) {
      console.error("Error copying table setup:", error);
      setperror(error.response?.data?.message || error.message || t("error_copying_setup"));
    }
  };

  // Rotate players between tables
  const handleRotatePlayers = async (direction) => {
    if (!selectedex) {
      setperror(t("please_select_exercise"));
      return;
    }

    try {
      const params = {
        ts_id: selectedSession.ts_id,
        te_id: selectedex.te_id,
        direction: direction, // "left" or "right"
      };

      await rotatePlayers(params);
      
      // Refresh tables after rotation
      await gettablelist(selectedgrp?.groupDetails?.gr_id);
      
      setperror(null);
      console.log(`✅ Players rotated ${direction}`);
    } catch (error) {
      console.error("Error rotating players:", error);
      setperror(error.response?.data?.message || error.message || t("error_rotating_players"));
    }
  };

  // Assign players to tables ordered by ranking points (best players on table 1)
  const handleAssignByRanking = async () => {
    if (!selectedex || !selectedex.te_id || !players || players.length < 1) {
      setperror(t("Please_select_ex_table"));
      return;
    }
    if (players.length < 2 && !isSoloCapableSequenceExercise(selectedex)) {
      setperror(t("Please_select_ex_table"));
      return;
    }

    if (isAutoAssigning) {
      console.log("⚠️ Assignment already in progress, skipping...");
      return;
    }

    setIsAutoAssigning(true);
    // Clear combo boxes immediately
    setselplayer(null);
    setselplayer2(null);
    
    try {
      console.log("🔍 Assign by ranking - Starting assignment");
      
      // Fetch fresh table data from API
      const params = {
        gr_id: selectedgrp?.groupDetails?.gr_id,
        ts_id: selectedSession?.ts_id,
        te_id: selectedex?.te_id,
      };
      const tableResponse = await tablelist(params);
      const tablesArray = tableResponse.data?.data || [];
      
      // Remove duplicates and sort by table_number
      // Use table_number as primary key since it's unique, fallback to table_id
      const uniqueTablesByNumber = new Map();
      tablesArray.forEach(table => {
        const tableNumber = table.table_number || 0;
        // Only add if we don't already have a table with this number, or if this one has a table_id and the existing one doesn't
        if (!uniqueTablesByNumber.has(tableNumber)) {
          uniqueTablesByNumber.set(tableNumber, table);
        } else {
          // If we already have a table with this number, prefer the one with table_id
          const existing = uniqueTablesByNumber.get(tableNumber);
          if (!existing.table_id && table.table_id) {
            uniqueTablesByNumber.set(tableNumber, table);
          }
        }
      });
      let sortedTables = Array.from(uniqueTablesByNumber.values()).sort((a, b) => {
        return (a.table_number || 0) - (b.table_number || 0);
      });

      // Use all physical tables from the API for ranking assignment (do not cap to a stale session count).
      
      // Use only physical tables (must have both table_id and table_number)
      // If some tables don't have table_id, try to find it from the original tablesArray
      const physicalTables = sortedTables.map(table => {
        // Must have a table_number
        if (!table.table_number) {
          return null;
        }
        
        // If table doesn't have table_id, try to find it from the original array
        if (!table.table_id) {
          const tableWithId = tablesArray.find(t => 
            t.table_number === table.table_number && t.table_id
          );
          if (tableWithId) {
            return { ...table, table_id: tableWithId.table_id };
          }
        }
        
        // Return table if it has both table_id and table_number
        if (table.table_id && table.table_number) {
          return table;
        }
        
        return null;
      }).filter(t => t !== null); // Remove null entries
      
      console.log(`🔍 Assign by ranking - Physical tables: ${physicalTables.length} out of ${sortedTables.length} total`);
      console.log(`🔍 Assign by ranking - Table details:`, physicalTables.map(t => ({
        table_id: t.table_id,
        table_number: t.table_number,
        hasPlayer1: !!t.player1,
        hasPlayer2: !!t.player2
      })));
      
      if (physicalTables.length === 0) {
        console.error("❌ No physical tables found. Tables must have both table_id and table_number.");
        console.error("❌ Tables received:", sortedTables);
        setperror(t("no_physical_tables") || "No physical tables are configured in the training room.");
        setIsAutoAssigning(false);
        return;
      }
      
      // Only use as many physical tables as needed for present players (2 per table).
      const tablesForAssignment = physicalTables.slice(
        0,
        Math.max(1, Math.ceil(players.length / 2))
      );
      
      // Sort players by ranking_points (highest first = best players first)
      const sortedPlayers = [...players].sort((a, b) => {
        const pointsA = a.ranking_points || 0;
        const pointsB = b.ranking_points || 0;
        return pointsB - pointsA; // Descending order
      });
      
      console.log("🔍 Assign by ranking - Sorted players:", sortedPlayers.map(p => ({
        id: p.id || p.player_id,
        name: p.user_first_name || p.user_alias,
        ranking_points: p.ranking_points
      })));
      
      // Assign players to tables in pairs (2 players per table)
      // Best players go to table 1, then table 2, etc.
      const maxPlayers = tablesForAssignment.length * 2;
      const playersToAssign = sortedPlayers.slice(0, maxPlayers);
      
      // Assign players to tables
      for (let i = 0; i < playersToAssign.length; i += 2) {
        const tableIndex = Math.floor(i / 2);
        if (tableIndex >= tablesForAssignment.length) break;
        
        const table = tablesForAssignment[tableIndex];
        const player1 = playersToAssign[i];
        const player2 = playersToAssign[i + 1];
        
        // If table doesn't have table_id, we need to find it using table_number
        let tableIdToUse = table.table_id;
        if (!tableIdToUse && table.table_number) {
          // Try to find table_id from the tables array by matching table_number
          const tableWithId = tablesArray.find(t => 
            t.table_number === table.table_number && t.table_id
          );
          if (tableWithId) {
            tableIdToUse = tableWithId.table_id;
            console.log(`🔍 Found table_id for table_number ${table.table_number}: ${tableIdToUse}`);
          } else {
            console.error(`❌ Cannot find table_id for table_number ${table.table_number}`);
            continue; // Skip this table if we can't find its table_id
          }
        }
        
        if (player1 && player2 && tableIdToUse) {
          try {
            const assignParams = {
              player1: player1.id || player1.player_id,
              player2: player2.id || player2.player_id,
              table_id: tableIdToUse,
              ts_id: selectedSession.ts_id,
              te_id: selectedex.te_id,
            };
            
            console.log(`✅ Assigning players to table ${table.table_number} (table_id: ${tableIdToUse}):`, {
              player1: player1.user_first_name || player1.user_alias,
              player2: player2.user_first_name || player2.user_alias,
            });
            
            await playeradtable(assignParams);
          } catch (error) {
            console.error(`❌ Error assigning players to table ${table.table_number}:`, error);
            // Continue with next table even if one fails
          }
        } else if (player1 && tableIdToUse) {
          // Odd number of players — seat only Player1 (do not duplicate into Player2).
          try {
            const assignParams = {
              player1: player1.id || player1.player_id,
              table_id: tableIdToUse,
              ts_id: selectedSession.ts_id,
              te_id: selectedex.te_id,
            };
            
            await playeradtable(assignParams);
          } catch (error) {
            console.error(`❌ Error assigning player to table ${table.table_number}:`, error);
          }
        } else {
          console.warn(`⚠️ Skipping table ${table.table_number}: missing table_id or players`);
        }
      }
      
      // Refresh tables after assignment
      await gettablelist(selectedgrp?.groupDetails?.gr_id);
      await getplayerlist(selectedSession.ts_id, selectedgrp?.groupDetails?.gr_id);
      
      // After successfully assigning to the current exercise, assign to all other exercises
      if (exercises.length > 1) {
        console.log(`🔄 Copying table setup by ranking to all ${exercises.length - 1} remaining exercises...`);
        await assignToAllExercises(selectedex.te_id);
        
        // Refresh tables one more time to ensure all exercises are updated
        await gettablelist(selectedgrp?.groupDetails?.gr_id);
      }
      
      // Clear combo boxes after assignment
      setselplayer(null);
      setselplayer2(null);
      setperror(null);
      console.log("✅ Assignment by ranking completed successfully for all exercises");
    } catch (error) {
      console.error("❌ Error assigning players by ranking:", error);
      setperror(error.response?.data?.message || error.message || t("error_assigning_by_ranking"));
    } finally {
      setIsAutoAssigning(false);
    }
  };

  // Historic name: seating changes must never rewrite finished exercises.
  const assignToAllExercises = async (sourceExerciseId) => {
    await assignToFollowingExercises(sourceExerciseId);
  };

  const assignToFollowingExercises = async (sourceExerciseId) => {
    if (!exercises || exercises.length === 0 || !selectedSession?.ts_id || !sourceExerciseId) {
      return;
    }
    const following = followingExercisesForTableCopy(exercises, sourceExerciseId);
    for (const exercise of following) {
      try {
        await copyTableSetup({
          from_te_id: sourceExerciseId,
          to_te_id: exercise.te_id,
          ts_id: selectedSession.ts_id,
        });
        console.log(`✅ Copied table setup to following exercise ${exercise.te_id}`);
      } catch (error) {
        console.error(`❌ Error copying setup to following exercise ${exercise.te_id}:`, error);
      }
    }
  };

  // Auto-assign players to tables based on ranking
  // IMPORTANT: This function ONLY assigns players to EXISTING tables, it does NOT create new tables
  // It will assign players to ALL exercises when first creating assignments
  const handleAutoAssign = async (playersOverride = null) => {
    // Use provided players or fall back to state
    const playersToUse = playersOverride || players;
    
    if (!selectedex || !selectedex.te_id || !playersToUse || playersToUse.length < 1) {
      const errorMsg = !playersToUse || playersToUse.length < 1 
        ? "Please ensure at least 1 player is present" 
        : "Please select an exercise";
      setperror(t("Please_select_ex_table") || errorMsg);
      console.error(`❌ handleAutoAssign - Missing requirements:`, {
        hasSelectedEx: !!selectedex,
        hasTeId: !!selectedex?.te_id,
        playersCount: playersToUse?.length || 0,
        usingOverride: !!playersOverride
      });
      return;
    }
    if (playersToUse.length < 2 && !isSoloCapableSequenceExercise(selectedex)) {
      const errorMsg = "Please ensure at least 2 players are present";
      setperror(t("Please_select_ex_table") || errorMsg);
      return;
    }

    if (!selectedSession?.ts_id) {
      setperror(t("Please_select_session"));
      return;
    }

    // Prevent multiple simultaneous auto-assignments
    if (isAutoAssigning) {
      console.log("⚠️ Auto-assignment already in progress, skipping...");
      return;
    }

    setIsAutoAssigning(true);
    try {
      console.log("🔍 Auto-assign - Starting auto-assignment");
      console.log("🔍 Auto-assign - Players:", players);
      console.log("🔍 Auto-assign - Selected exercise:", selectedex);

      // Fetch tables directly from API to avoid stale state
      console.log("🔄 Fetching fresh table data from API...");
      const params = {
        gr_id: selectedgrp?.groupDetails?.gr_id,
        ts_id: selectedSession.ts_id,
        te_id: selectedex.te_id, // Use existing exercise ID, don't allow null
      };
      
      // Validate parameters before making API call
      if (!params.gr_id || !params.ts_id || !params.te_id) {
        console.error("❌ Missing required parameters for table fetch:", params);
        setperror(t("missing_parameters"));
        return;
      }
      const tableResponse = await tablelist(params);
      let tablesArray = tableResponse.data?.data || [];
      
      // Enrich tables with table_id if missing (using table_number to find it from other tables in the array)
      tablesArray = tablesArray.map(table => {
        // If table_id is missing but table_number exists, try to find it from another table with the same number
        if (!table.table_id && table.table_number) {
          const tableWithId = tablesArray.find(t => 
            t.table_id && t.table_number === table.table_number
          );
          if (tableWithId) {
            console.log(`✅ Auto-assign - Found table_id ${tableWithId.table_id} for table_number ${table.table_number} from another table in array`);
            return { ...table, table_id: tableWithId.table_id };
          }
        }
        return table;
      });
      
      // Remove duplicates by table_id AND table_number (keep first occurrence of each table_number)
      const uniqueTablesByTableId = new Map();
      // First, remove duplicates by table_id (only if table_id exists)
      tablesArray.forEach(table => {
        if (table.table_id && !uniqueTablesByTableId.has(table.table_id)) {
          uniqueTablesByTableId.set(table.table_id, table);
        }
      });
      // Then, remove duplicates by table_number (keep first occurrence)
      const uniqueTablesByNumber = new Map();
      // Add tables with table_id first
      Array.from(uniqueTablesByTableId.values()).forEach(table => {
        const tableNumber = table.table_number || 0;
        if (!uniqueTablesByNumber.has(tableNumber)) {
          uniqueTablesByNumber.set(tableNumber, table);
        }
      });
      // Add tables without table_id (they should have been enriched above, but just in case)
      tablesArray.forEach(table => {
        const tableNumber = table.table_number || 0;
        if (!uniqueTablesByNumber.has(tableNumber)) {
          uniqueTablesByNumber.set(tableNumber, table);
        } else {
          // If we already have a table with this number, prefer the one with table_id
          const existing = uniqueTablesByNumber.get(tableNumber);
          if (!existing.table_id && table.table_id) {
            uniqueTablesByNumber.set(tableNumber, table);
          } else if (existing.table_id && !table.table_id) {
            // Keep existing if it has table_id and new one doesn't
            uniqueTablesByNumber.set(tableNumber, { ...table, table_id: existing.table_id });
          }
        }
      });
      // Sort tables by table_number
      let sortedTables = Array.from(uniqueTablesByNumber.values()).sort((a, b) => {
        return (a.table_number || 0) - (b.table_number || 0);
      });

      console.log(
        `🔍 handleAutoAssign - using ${sortedTables.length} table(s) from API for ${playersToUse?.length || 0} player(s)`
      );

      // DO NOT create virtual tables - tables must exist in the training room (created when room was created)
      // If no tables are returned, it means tables haven't been set up for this training room yet
      if (sortedTables.length === 0) {
        console.warn(`⚠️ handleAutoAssign - No tables returned from API. Tables should be created when the training room is created.`);
      }

      if (sortedTables.length === 0) {
        setperror(t("no_tables_configured"));
        return;
      }

      console.log("🔍 Auto-assign - Fresh tables count:", sortedTables.length);
      console.log("🔍 Auto-assign - Tables data:", sortedTables);

      // Use the fetched and filtered tables
      const limitedTables = sortedTables;
      console.log("🔍 Auto-assign - Limited tables count:", limitedTables.length);

      if (limitedTables.length === 0) {
        console.error("❌ No tables found. Make sure tables are loaded for this group.");
        setperror(t("no_available_tables"));
        return;
      }

      // Use only physical tables (must have both table_id and table_number)
      const physicalTables = limitedTables.filter(table => table.table_id && table.table_number);
      
      console.log(`🔍 Auto-assign - Physical tables: ${physicalTables.length} of ${limitedTables.length} total`);
      
      if (physicalTables.length === 0) {
        console.error("❌ Auto-assign - No physical tables found. Tables must have both table_id and table_number.");
        console.error("❌ Auto-assign - Tables received:", limitedTables);
        setperror(t("no_physical_tables") || "No physical tables are configured in the training room.");
        setIsAutoAssigning(false);
        return;
      }
      
      console.log(`🔍 Auto-assign - Physical tables available: ${physicalTables.length}`);
      console.log(`🔍 Auto-assign - Table details:`, JSON.stringify(limitedTables.map(t => ({
        table_id: t.table_id,
        table_number: t.table_number,
        is_virtual: t.is_virtual,
        table_description: t.table_description,
        hasPlayer1: !!t.player1,
        hasPlayer2: !!t.player2
      })), null, 2));

      // Calculate maximum players that can be assigned (2 players per table)
      const maxPlayers = physicalTables.length * 2;
      if (playersToUse.length > maxPlayers) {
        const warning = t("too_many_players") || `Warning: You have ${playersToUse.length} players but only ${physicalTables.length} physical tables available (max ${maxPlayers} players). Only the first ${maxPlayers} players will be assigned.`;
        console.warn("⚠️", warning);
        setperror(warning);
        // Continue anyway, but only assign the first maxPlayers players
      }

      // Only use completely empty tables (both player1 and player2 must be null/undefined/empty)
      // The backend requires both positions to be empty before assigning
      // More robust check: handle null, undefined, empty object, or missing required properties
      const emptyTables = physicalTables.filter(table => {
        const hasPlayer1 = table.player1 && 
                          typeof table.player1 === 'object' && 
                          Object.keys(table.player1).length > 0 &&
                          (table.player1.player_id || table.player1.user_first_name || table.player1.user_alias);
        const hasPlayer2 = table.player2 && 
                          typeof table.player2 === 'object' && 
                          Object.keys(table.player2).length > 0 &&
                          (table.player2.player_id || table.player2.user_first_name || table.player2.user_alias);
        const isEmpty = !hasPlayer1 && !hasPlayer2;
        
        if (!isEmpty) {
          console.log(`⚠️ Table ${table.table_id} (${table.table_number}) is NOT empty:`, {
            player1: table.player1,
            player2: table.player2,
            player1Type: typeof table.player1,
            player2Type: typeof table.player2,
            hasPlayer1: hasPlayer1,
            hasPlayer2: hasPlayer2
          });
        }
        return isEmpty;
      });
      
      console.log("🔍 Auto-assign - Completely empty tables:", emptyTables.length);
      console.log("🔍 Auto-assign - Total tables:", limitedTables.length);
      console.log("🔍 Auto-assign - Table details:", limitedTables.map(t => ({
        table_id: t.table_id,
        table_number: t.table_number,
        hasPlayer1: !!t.player1,
        hasPlayer2: !!t.player2,
        player1: t.player1,
        player2: t.player2
      })));
      
      // Only use completely empty tables
      const availableTables = emptyTables;
      
      if (availableTables.length === 0) {
        console.warn("⚠️ No empty tables available for assignment. All tables may already have players assigned.");
        // Only show error if ALL tables have players assigned AND we're trying to assign
        // Check if ALL tables are occupied (not just some)
        const allTablesOccupied = physicalTables.length > 0 && physicalTables.every(table => {
          const hasPlayer1 = table.player1 && 
                            typeof table.player1 === 'object' && 
                            Object.keys(table.player1).length > 0 &&
                            (table.player1.player_id || table.player1.user_first_name || table.player1.user_alias);
          const hasPlayer2 = table.player2 && 
                            typeof table.player2 === 'object' && 
                            Object.keys(table.player2).length > 0 &&
                            (table.player2.player_id || table.player2.user_first_name || table.player2.user_alias);
          return hasPlayer1 || hasPlayer2;
        });
        // Only set error if ALL tables are occupied - otherwise clear it
        if (allTablesOccupied && physicalTables.length > 0) {
          setperror(t("all_tables_occupied"));
        } else {
          setperror(null); // Clear error if not all tables are occupied or no tables
        }
        return;
      }
      
      // Clear error if we have available tables
      setperror(null);
      
      console.log("🔍 Auto-assign - Available tables for assignment:", availableTables.length);

      // Deduplicate players by player_id so the same player is never assigned twice
      const seenPlayerIds = new Set();
      const uniquePlayers = (playersToUse || []).filter((p) => {
        const id = p.player_id || p.user_id;
        if (!id || seenPlayerIds.has(id)) return false;
        seenPlayerIds.add(id);
        return true;
      });
      if (uniquePlayers.length !== (playersToUse || []).length) {
        console.log(`🔍 Auto-assign - Removed duplicate players: ${(playersToUse || []).length} → ${uniquePlayers.length} unique`);
      }

      // Create pairs of players (sorted by ranking_points, highest first)
      // This ensures the best players are assigned to the first tables
      // Limit to maxPlayers if there are too many players
      const playersToAssign = uniquePlayers.length > maxPlayers ? uniquePlayers.slice(0, maxPlayers) : uniquePlayers;
      const sortedPlayers = [...playersToAssign].sort((a, b) => {
        const pointsA = a.ranking_points || 0;
        const pointsB = b.ranking_points || 0;
        return pointsB - pointsA; // Descending order (highest first = best players first)
      });
      
      console.log("🔍 Auto-assign - Players sorted by ranking (best first):", sortedPlayers.map(p => ({
        name: p.user_alias || p.user_first_name || p.player_id,
        ranking_points: p.ranking_points || 0
      })));
      console.log("🔍 Auto-assign - Players to assign:", sortedPlayers.length);

      const playerPairs = [];
      for (let i = 0; i < sortedPlayers.length - 1; i += 2) {
        if (i + 1 < sortedPlayers.length) {
          playerPairs.push({
            player1: sortedPlayers[i],      // Best player (highest ranking)
            player2: sortedPlayers[i + 1],  // Second best player
          });
        }
      }

      // Check if there's an odd player (last one without a pair)
      const hasOddPlayer = sortedPlayers.length % 2 === 1;
      const lastPlayer = hasOddPlayer ? sortedPlayers[sortedPlayers.length - 1] : null;

      // Ensure tables are sorted by table_number (table 1, then table 2, etc.)
      // This ensures best players go to the first tables
      availableTables.sort((a, b) => (a.table_number || 0) - (b.table_number || 0));
      console.log("🔍 Auto-assign - Tables sorted by number:", availableTables.map(t => ({
        table_id: t.table_id,
        table_number: t.table_number
      })));

      // Assign pairs to tables starting from table 1 (best players to first tables)
      let tableIndex = 0;
      for (const pair of playerPairs) {
        if (tableIndex >= availableTables.length) {
          console.log(`⚠️ Not enough tables for all pairs. Only ${availableTables.length} tables available.`);
          break;
        }

        const table = availableTables[tableIndex];
        
        // Double-check that table is completely empty (safety check)
        const hasPlayer1 = table.player1 && (typeof table.player1 === 'object' ? Object.keys(table.player1).length > 0 : true);
        const hasPlayer2 = table.player2 && (typeof table.player2 === 'object' ? Object.keys(table.player2).length > 0 : true);
        
        if (hasPlayer1 || hasPlayer2) {
          console.log(`⚠️ Skipping table ${table.table_id} (${table.table_number}) - already has players assigned:`, {
            player1: table.player1,
            player2: table.player2
          });
          tableIndex++;
          continue;
        }

        // Assign players to the table (best players go to first tables)
        console.log(`✅ Assigning players to table ${table.table_number}:`, {
          table_id: table.table_id,
          player1: {
            id: pair.player1.player_id,
            name: pair.player1.user_alias || pair.player1.user_first_name || pair.player1.player_id,
            ranking_points: pair.player1.ranking_points || 0
          },
          player2: {
            id: pair.player2.player_id,
            name: pair.player2.user_alias || pair.player2.user_first_name || pair.player2.player_id,
            ranking_points: pair.player2.ranking_points || 0
          }
        });
        
        const params = {
          player1: pair.player1.player_id,
          player2: pair.player2.player_id,
          table_id: table.table_id,
          ts_id: selectedSession.ts_id,
          te_id: selectedex.te_id,
        };

        try {
        await playeradtable(params);
          console.log(`✅ Successfully assigned players to table ${table.table_id}`);
        } catch (assignError) {
          console.error(`❌ Error assigning players to table ${table.table_id}:`, assignError);
          // If this table fails, skip it and try the next one
          if (assignError.response?.data?.message?.includes("already occupied")) {
            console.log(`⚠️ Table ${table.table_id} became occupied, skipping to next table`);
            tableIndex++;
            continue;
          } else {
            // For other errors, throw to be caught by outer try-catch
            throw assignError;
          }
        }
        tableIndex++;
      }

      // If odd number of players, assign the last player to a table alone
      if (hasOddPlayer && lastPlayer && tableIndex < availableTables.length) {
        const table = availableTables[tableIndex];
        
        // Double-check that table is completely empty (safety check)
        const hasPlayer1 = table.player1 && (typeof table.player1 === 'object' ? Object.keys(table.player1).length > 0 : true);
        const hasPlayer2 = table.player2 && (typeof table.player2 === 'object' ? Object.keys(table.player2).length > 0 : true);
        
        if (!hasPlayer1 && !hasPlayer2) {
          console.log(`✅ Assigning single player to table ${table.table_number} (odd number of players):`, {
            table_id: table.table_id,
            player: {
              id: lastPlayer.player_id,
              name: lastPlayer.user_alias || lastPlayer.user_first_name || lastPlayer.player_id,
              ranking_points: lastPlayer.ranking_points || 0
            }
          });
          
          const params = {
            player1: lastPlayer.player_id,
            player2: null,
            table_id: table.table_id,
            ts_id: selectedSession.ts_id,
            te_id: selectedex.te_id,
          };

          try {
            await playeradtable(params);
            console.log(`✅ Successfully assigned single player to table ${table.table_id}`);
          } catch (assignError) {
            console.error(`❌ Error assigning single player to table ${table.table_id}:`, assignError);
            // Don't throw - just log the error and continue
          }
        } else {
          console.log(`⚠️ Cannot assign single player to table ${table.table_id} (${table.table_number}) - table is not empty`);
        }
      } else if (hasOddPlayer && lastPlayer) {
        console.log(`⚠️ Odd number of players: ${sortedPlayers.length}. Last player (${lastPlayer.user_alias || lastPlayer.user_first_name || lastPlayer.player_id}, ${lastPlayer.ranking_points || 0} pts) cannot be assigned - no more tables available.`);
      }

      // Refresh table and player lists
      await gettablelist(selectedgrp?.groupDetails?.gr_id);
      await getplayerlist(selectedSession.ts_id, selectedgrp?.groupDetails?.gr_id);
      
      // After successfully assigning to the first exercise, assign to all other exercises
      if (exercises.length > 1) {
        console.log(`🔄 Copying table setup to all ${exercises.length - 1} remaining exercises...`);
        await assignToAllExercises(selectedex.te_id);
        
        // Refresh tables one more time to ensure all exercises are updated
        await gettablelist(selectedgrp?.groupDetails?.gr_id);
      }
      
      setperror(null);
      console.log("✅ Auto-assignment completed successfully for all exercises");
    } catch (error) {
      console.error("❌ Error auto-assigning players:", error);
      setperror(error.response?.data?.message || error.message || t("error_auto_assign"));
    } finally {
      setIsAutoAssigning(false);
    }
  };

  // Auto-assign new player when added to session
  // Assigns to last table with one player, or creates new table if all tables have 2 players
  const handleAutoAssignNewPlayer = async (newPlayers) => {
    if (!selectedex?.te_id || !selectedSession?.ts_id || !selectedgrp?.groupDetails?.gr_id || isAutoAssigning) {
      console.log("⚠️ Cannot auto-assign new player - missing requirements or already assigning");
      return;
    }

    setIsAutoAssigning(true);
    try {
      // Deduplicate new players by player_id so the same player is never assigned twice
      const seenNewIds = new Set();
      const uniqueNewPlayers = (newPlayers || []).filter((p) => {
        const id = p.player_id || p.user_id;
        if (!id || seenNewIds.has(id)) return false;
        seenNewIds.add(id);
        return true;
      });
      if (uniqueNewPlayers.length !== (newPlayers || []).length) {
        console.log(`🔍 Auto-assign new: removed duplicate players: ${(newPlayers || []).length} → ${uniqueNewPlayers.length} unique`);
      }
      console.log(`🔄 Auto-assigning ${uniqueNewPlayers.length} new player(s)...`);

      const params = {
        gr_id: selectedgrp.groupDetails.gr_id,
        ts_id: selectedSession.ts_id,
        te_id: selectedex.te_id,
      };

      const slotTaken = (p) =>
        p &&
        typeof p === "object" &&
        Object.keys(p).length > 0 &&
        (p.player_id || p.user_first_name || p.user_alias);

      // Process each new player (re-fetch tables each time so batch adds see updated occupancy)
      for (const newPlayer of uniqueNewPlayers) {
        const tableResponse = await tablelist(params);
        const physicalTables = (tableResponse.data?.data || [])
          .filter((table) => table.table_id && table.table_number != null)
          .sort((a, b) => (a.table_number || 0) - (b.table_number || 0));

        if (physicalTables.length === 0) {
          console.warn("⚠️ No physical tables available for assignment");
          break;
        }

        // 1) Prefer a completely empty table (matches "tables available" / spare physical tables).
        //    Old logic paired on a half-full table before checking empty — that forced replace
        //    dialogs even when empty tables existed for this exercise.
        const emptyTable = physicalTables.find(
          (table) => !slotTaken(table.player1) && !slotTaken(table.player2)
        );

        // 2) Else use the last table with exactly one player (pair the newcomer as second slot).
        let tableWithOnePlayer = null;
        if (!emptyTable) {
          for (let i = physicalTables.length - 1; i >= 0; i--) {
            const table = physicalTables[i];
            const hasPlayer1 = slotTaken(table.player1);
            const hasPlayer2 = slotTaken(table.player2);

            if (hasPlayer1 && !hasPlayer2) {
              tableWithOnePlayer = { table, position: "player2", existingPlayer: table.player1 };
              break;
            }
            if (!hasPlayer1 && hasPlayer2) {
              tableWithOnePlayer = { table, position: "player1", existingPlayer: table.player2 };
              break;
            }
          }
        }

        if (emptyTable) {
          const assignParams = {
            table_id: emptyTable.table_id,
            ts_id: selectedSession.ts_id,
            te_id: selectedex.te_id,
            player1: newPlayer.player_id,
            player2: null,
          };
          console.log(
            `✅ Assigning new player ${newPlayer.user_alias || newPlayer.user_first_name || newPlayer.player_id} to empty table ${emptyTable.table_number} as player1`
          );
          await playeradtable(assignParams);
        } else if (tableWithOnePlayer) {
          const { table, position, existingPlayer } = tableWithOnePlayer;
          const assignParams = {
            table_id: table.table_id,
            ts_id: selectedSession.ts_id,
            te_id: selectedex.te_id,
            [position === "player1" ? "player1" : "player2"]: newPlayer.player_id,
            [position === "player1" ? "player2" : "player1"]: existingPlayer.player_id,
          };
          console.log(
            `✅ Assigning new player ${newPlayer.user_alias || newPlayer.user_first_name || newPlayer.player_id} to table ${table.table_number} as ${position}`
          );
          await playeradtable(assignParams);
        } else {
          // Default remains 2 per table — extra seats are coach-chosen, never auto-filled.
          const remaining = uniqueNewPlayers.slice(uniqueNewPlayers.indexOf(newPlayer) + 1);
          const seenTableIds = new Set();
          const uniqueDialogTables = physicalTables
            .filter((t) => {
              if (!t?.table_id || seenTableIds.has(t.table_id)) return false;
              seenTableIds.add(t.table_id);
              return true;
            })
            .sort((a, b) => (a.table_number || 0) - (b.table_number || 0));
          setAssignTableDialogPlayer(newPlayer);
          setAssignTableDialogTables(uniqueDialogTables);
          setAssignTableDialogRemaining(remaining);
          setAssignTableDialogOpen(true);
          return;
        }
      }
      
      // Refresh tables and players
      await gettablelist(selectedgrp?.groupDetails?.gr_id);
      await getplayerlist(selectedSession.ts_id, selectedgrp?.groupDetails?.gr_id);
      
      // Propagate assignments to all other exercises (same as handleAutoAssign does)
      if (exercises.length > 1 && selectedex?.te_id) {
        console.log(`🔄 Copying new player table setup to all ${exercises.length - 1} remaining exercises...`);
        await assignToAllExercises(selectedex.te_id);
        await gettablelist(selectedgrp?.groupDetails?.gr_id);
      }
      
      console.log("✅ Auto-assignment of new player(s) completed");
    } catch (error) {
      console.error("❌ Error auto-assigning new player:", error);
      setperror(error.response?.data?.message || error.message || t("error_auto_assign"));
    } finally {
      setIsAutoAssigning(false);
    }
  };

  const getPlayerDisplayName = (p) =>
    p?.user_alias || p?.user_first_name || p?.player_id || "";

  const handleTogglePersonalizeMode = async () => {
    if (!personalizeMode && !canAddPlayersForDisplayedExercise) return;
    if (!personalizeMode) {
      try {
        const grId = selectedgrp?.groupDetails?.gr_id;
        if (grId) {
          const res = await grpplayerlist({ gr_id: grId });
          setPersonalizeGroupPlayers(res.data?.data || []);
        }
        const typesRes = await axios.get(`${ip_back}api/types/getType`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (typesRes.data.status === "success") {
          setExerciseTypes(typesRes.data.data || []);
        }
      } catch (err) {
        console.error("Error fetching data for personalize mode:", err);
      }
    }
    setPersonalizeMode((prev) => !prev);
  };

  const handlePlayerClickForPersonalize = async (player) => {
    if (!player?.player_id) return;
    const slot = isPersonalizedTrainingSlot(selectedex);
    if (!personalizeMode && !slot) return;
    if (!slot && !canAddPlayersForDisplayedExercise) return;
    if (!exerciseTypes.length) {
      try {
        const typesRes = await axios.get(`${ip_back}api/types/getType`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (typesRes.data.status === "success") {
          setExerciseTypes(typesRes.data.data || []);
        }
      } catch (err) {
        console.error("Error fetching types for personalize:", err);
      }
    }
    if (!personalizeGroupPlayers.length) {
      try {
        const grId = selectedgrp?.groupDetails?.gr_id;
        if (grId) {
          const res = await grpplayerlist({ gr_id: grId });
          setPersonalizeGroupPlayers(res.data?.data || []);
        }
      } catch (err) {
        console.error("Error fetching group players for personalize:", err);
      }
    }
    setPersonalizePlayerId(player.player_id);
    setPersonalizeDialogOpen(true);
  };

  const handlePersonalizeExerciseSelected = async (recommendedEx, playerId) => {
    if (!recommendedEx?.ex_id || !selectedex?.te_id || !playerId) return;
    try {
      await personalizeExerciseForPlayer({
        te_id: selectedex.te_id,
        player_id: playerId,
        ex_id: recommendedEx.ex_id,
      });
      setPersonalizeSnackbar({ open: true, message: t("exercisePersonalized") || "Exercise personalized successfully", severity: "success" });
      setPersonalizeDialogOpen(false);
      setPersonalizePlayerId(null);
      setPersonalizeMode(false);
      if (selectedSession?.ts_id) {
        await getexlist(selectedSession.ts_id);
        await gettablelist(selectedgrp?.groupDetails?.gr_id);
      }
    } catch (err) {
      setPersonalizeSnackbar({ open: true, message: err?.message || t("errorPersonalizing") || "Error personalizing exercise", severity: "error" });
    }
  };

  const handleOpenPersonalizedExDetails = async (override) => {
    if (!override?.ex_id) return;
    setPersonalizedExDetailsOpen(true);
    setPersonalizedExDetailsLoading(true);
    setPersonalizedExDetailsData(null);
    try {
      const response = await exdetails({ ex_id: override.ex_id });
      const data = response?.data?.data;
      if (data) setPersonalizedExDetailsData(data);
    } catch (err) {
      console.error("Failed to load personalized exercise details:", err);
    } finally {
      setPersonalizedExDetailsLoading(false);
    }
  };

  const renderPlayerPersonalizedRow = (player, exercise) => {
    if (!player?.player_id) return null;
    const slot = isPersonalizedTrainingSlot(exercise || selectedex);
    const override = overrideForPlayer(
      exercise || selectedex,
      player.player_id,
      player.user_id
    );
    const lng = (currentlng || "en").split("-")[0];
    const name = localizedPersonalizedName(override, lng);
    if (!slot && !name) return null;
    const personalizedLabel =
      t("personalizedExercise", { lng }) ||
      (lng === "fr" ? "Personnalisé" : lng === "de" ? "Personalisiert" : lng === "es" ? "Personalizado" : "Personalized");
    const label = name
      ? `${personalizedLabel}: ${name}`
      : t("personalizedSlotMissingShort", "No assigned exercise");
    return (
      <div
        style={{
          marginTop: "4px",
          display: "flex",
          alignItems: "center",
          gap: "4px",
        }}
      >
        <span
          style={{
            fontSize: `${Math.max(10, (responsiveStyles.statsFontSize || 12) - 2)}px`,
            color: personalizeIdle,
            fontWeight: 600,
            fontStyle: "italic",
          }}
        >
          {label}
        </span>
        {override?.ex_id ? (
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleOpenPersonalizedExDetails(override);
            }}
            sx={{ padding: "2px", color: personalizeIdle }}
            title={t("exdetails", { lng }) || "Exercise details"}
          >
            <InfoOutlinedIcon sx={{ fontSize: 16 }} />
          </IconButton>
        ) : null}
        {isCoachView ? (
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handlePlayerClickForPersonalize(player);
            }}
            sx={{ padding: "2px", color: personalizeIdle }}
            title={t("personalizedSlotUpdate", "Change this player's exercise")}
          >
            <EditOutlinedIcon sx={{ fontSize: 16 }} />
          </IconButton>
        ) : null}
      </div>
    );
  };

  const handleClosePersonalizedExDetails = () => {
    setPersonalizedExDetailsOpen(false);
    setPersonalizedExDetailsData(null);
  };

  // replacePosition: "player1" | "player2" | "player3" | "addNth" | "addThird"
  const handleAssignTableDialogSelect = async (table, replacePosition) => {
    const player = assignTableDialogPlayer;
    const remaining = assignTableDialogRemaining || [];
    if (!player || !table?.table_id || !selectedSession?.ts_id || !selectedex?.te_id || !selectedgrp?.groupDetails?.gr_id) {
      setAssignTableDialogOpen(false);
      setAssignTableDialogPlayer(null);
      setAssignTableDialogTables([]);
      setAssignTableDialogRemaining([]);
      setIsAutoAssigning(false);
      return;
    }
    try {
      const seatedIds = seatedPlayerIds(table);
      const base = {
        table_id: table.table_id,
        ts_id: selectedSession.ts_id,
        te_id: selectedex.te_id,
      };

      let assignParams;
      if (replacePosition === "addNth" || replacePosition === "addThird") {
        assignParams = {
          ...base,
          player_ids: [...seatedIds, player.player_id],
        };
      } else if (replacePosition === "player1") {
        const next = [...seatedIds];
        if (next.length) next[0] = player.player_id;
        else next.push(player.player_id);
        assignParams = { ...base, player_ids: next };
      } else if (replacePosition === "player2") {
        const next = [...seatedIds];
        if (next.length > 1) next[1] = player.player_id;
        else next.push(player.player_id);
        assignParams = { ...base, player_ids: next };
      } else if (replacePosition === "player3") {
        const next = [...seatedIds];
        if (next.length > 2) next[2] = player.player_id;
        else next.push(player.player_id);
        assignParams = { ...base, player_ids: next };
      } else {
        assignParams = {
          ...base,
          player_ids: seatedIds.length ? [...seatedIds, player.player_id] : [player.player_id],
        };
      }

      await playeradtable(assignParams);
      setAssignTableDialogOpen(false);
      setAssignTableDialogPlayer(null);
      setAssignTableDialogTables([]);
      setAssignTableDialogRemaining([]);
      await gettablelist(selectedgrp?.groupDetails?.gr_id);
      await getplayerlist(selectedSession.ts_id, selectedgrp?.groupDetails?.gr_id);

      // Propagate to all exercises after dialog assignment
      if (exercises.length > 1 && selectedex?.te_id) {
        await assignToAllExercises(selectedex.te_id);
        await gettablelist(selectedgrp?.groupDetails?.gr_id);
      }

      if (remaining.length > 0) {
        await handleAutoAssignNewPlayer(remaining);
      }
    } catch (err) {
      console.error("Error assigning new player to selected table:", err);
      setperror(err.response?.data?.message || err.message || t("error_auto_assign"));
    } finally {
      setIsAutoAssigning(false);
    }
  };

  const handleAssignTableDialogClose = () => {
    setAssignTableDialogOpen(false);
    setAssignTableDialogPlayer(null);
    setAssignTableDialogTables([]);
    setAssignTableDialogRemaining([]);
    setIsAutoAssigning(false);
    if (selectedgrp?.groupDetails?.gr_id) gettablelist(selectedgrp.groupDetails.gr_id);
    if (selectedSession?.ts_id && selectedgrp?.groupDetails?.gr_id) {
      getplayerlist(selectedSession.ts_id, selectedgrp.groupDetails.gr_id);
    }
  };

  return (
    <>
      {selectedSession ? (
        <>
          <div className="session-setup" style={personalizeBrandVars}>
              {/* Add players — directly under session info card (Training page) */}
              {(hasPermission("PLAYER_ADD_TO_SESSION") ||
                hasPermission("PLAYER_REMOVE_FROM_SESSION")) &&
                showSessionAddPlayers && (
                <div className="session-setup__add-players">
                  <button
                    type="button"
                    onClick={() => canOpenPresenceDialog && setOpenPlayerModal(true)}
                    disabled={!canOpenPresenceDialog}
                    style={{
                      padding: "10px 20px",
                      backgroundColor: canOpenPresenceDialog
                        ? "#28a745"
                        : "#9e9e9e",
                      color: "white",
                      border: "none",
                      borderRadius: "8px",
                      cursor: canOpenPresenceDialog ? "pointer" : "not-allowed",
                      fontSize: "15px",
                      fontWeight: 600,
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "10px",
                      boxShadow: "0px 2px 6px rgba(0,0,0,0.15)",
                      opacity: canOpenPresenceDialog ? 1 : 0.85,
                    }}
                    title={
                      canAddPlayersForDisplayedExercise
                        ? t("add_players") || "Add players"
                        : canOpenPresenceDialog
                        ? t("presenceCorrectionHint") ||
                          "This session is finalized. You can still correct who was present or absent."
                        : t("addPlayersOnlyBeforeExerciseStarts") ||
                          "Add or remove players only before this exercise has started (not when it is running, paused, finalized, or cancelled)."
                    }
                  >
                    <PersonAddIcon fontSize="small" />
                    {canAddPlayersForDisplayedExercise
                      ? t("add_players") || "Add players"
                      : t("correctSessionPresence") ||
                        t("managePlayerPresence") ||
                        "Correct presence"}
                  </button>
                </div>
              )}
              {(!exercises || exercises.length === 0) && (
                <p className="select_msg" style={{ marginTop: 12 }}>
                  {!players || players.length === 0
                    ? t("add_exercises_training") || "Add Exercises of the training"
                    : t("Please_select_ex_table")}
                </p>
              )}
              {selectedex && showManualSeatingToolbar && (
                <Box
                  className="session-setup__rotate-ranking-row"
                  sx={{
                    width: "100%",
                    maxWidth: 1100,
                    mx: "auto",
                    mb: 2,
                    px: { xs: 0.5, sm: 1 },
                    display: "flex",
                    flexWrap: "wrap",
                    alignItems: "center",
                    justifyContent: "flex-start",
                    gap: 1.5,
                  }}
                >
                  <Tooltip
                    title={
                      isCurrentExerciseLocked
                        ? t("exercise_locked_tooltip") ||
                          "This exercise is currently being executed. No modifications allowed."
                        : t("rotate_players_left") ||
                          "Rotate players left between tables"
                    }
                  >
                    <span>
                      <IconButton
                        onClick={() => handleRotatePlayers("left")}
                        disabled={isCurrentExerciseLocked}
                        color="primary"
                        sx={{
                          width: 48,
                          height: 48,
                          bgcolor: "rgba(var(--brand-primary-rgb), 0.12)",
                          border: "1px solid rgba(var(--brand-primary-rgb), 0.28)",
                          "&:hover": {
                            bgcolor: "rgba(var(--brand-primary-rgb), 0.2)",
                          },
                        }}
                        aria-label={t("rotate_left") || "Rotate left"}
                      >
                        <RotateLeftIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip
                    title={
                      isCurrentExerciseLocked
                        ? t("exercise_locked_tooltip") ||
                          "This exercise is currently being executed. No modifications allowed."
                        : t("assign_by_ranking_tooltip") ||
                          "Order players by rating level (best players on table 1)"
                    }
                  >
                    <span>
                      <IconButton
                        onClick={handleAssignByRanking}
                        disabled={isAutoAssigning || isCurrentExerciseLocked}
                        color="primary"
                        sx={{
                          width: 48,
                          height: 48,
                          bgcolor: "rgba(var(--brand-primary-rgb), 0.18)",
                          border: "1px solid rgba(var(--brand-primary-rgb), 0.35)",
                          "&:hover": {
                            bgcolor: "rgba(var(--brand-primary-rgb), 0.28)",
                          },
                        }}
                        aria-label={
                          t("order_by_rating_level") || "Order by rating"
                        }
                      >
                        {isAutoAssigning ? (
                          <CircularProgress size={22} color="inherit" />
                        ) : (
                          <LeaderboardIcon />
                        )}
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip
                    title={
                      isCurrentExerciseLocked
                        ? t("exercise_locked_tooltip") ||
                          "This exercise is currently being executed. No modifications allowed."
                        : t("rotate_players_right") ||
                          "Rotate players right between tables"
                    }
                  >
                    <span>
                      <IconButton
                        onClick={() => handleRotatePlayers("right")}
                        disabled={isCurrentExerciseLocked}
                        color="primary"
                        sx={{
                          width: 48,
                          height: 48,
                          bgcolor: "rgba(var(--brand-primary-rgb), 0.12)",
                          border: "1px solid rgba(var(--brand-primary-rgb), 0.28)",
                          "&:hover": {
                            bgcolor: "rgba(var(--brand-primary-rgb), 0.2)",
                          },
                        }}
                        aria-label={t("rotate_right") || "Rotate right"}
                      >
                        <RotateRightIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
              )}
              {/* Exercise Navigation — same pattern as CoachExerciseControl */}
              <div style={{ marginBottom: "20px", textAlign: "center" }}>
                {exercises.length > 1 && (
                  <Paper
                    elevation={0}
                    sx={{
                      p: 1.25,
                      mb: 1.5,
                      maxWidth: 1100,
                      width: "100%",
                      mx: "auto",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      flexWrap: "wrap",
                      gap: 1,
                      border: "1px solid rgba(var(--brand-primary-rgb), 0.15)",
                      borderRadius: 3,
                      bgcolor: "var(--brand-surface, #fff)",
                    }}
                  >
                    <Button
                      variant="outlined"
                      startIcon={<NavigateBeforeIcon />}
                      onClick={() => {
                        if (currentExerciseIndex <= 0) return;
                        const prevIndex = currentExerciseIndex - 1;
                        setCurrentExerciseIndex(prevIndex);
                        setselectedex(exercises[prevIndex]);
                      }}
                      disabled={currentExerciseIndex === 0}
                      sx={navBtnSx}
                    >
                      {t("previous") || "Previous"}
                    </Button>
                    <Chip
                      label={`${t("exercise")} ${currentExerciseIndex + 1} / ${exercises.length}`}
                      sx={{
                        fontWeight: 700,
                        bgcolor: "rgba(var(--brand-primary-rgb), 0.1)",
                        color: personalizeActive,
                        border: "1px solid rgba(var(--brand-primary-rgb), 0.2)",
                      }}
                    />
                    <Button
                      variant="outlined"
                      endIcon={<NavigateNextIcon />}
                      onClick={() => {
                        if (currentExerciseIndex >= exercises.length - 1) return;
                        const nextIndex = currentExerciseIndex + 1;
                        setCurrentExerciseIndex(nextIndex);
                        setselectedex(exercises[nextIndex]);
                      }}
                      disabled={currentExerciseIndex === exercises.length - 1}
                      sx={navBtnSx}
                    >
                      {t("next") || "Next"}
                    </Button>
                  </Paper>
                )}

                {/* Exercise Slider - Only show image, no arrows - Hide if no exercises */}
              {exercises && exercises.length > 0 && (
                <ExerciseSlider
                  seteximg={seteximg}
                  exercises={exercises}
                  setselectedex={setselectedex}
                  selectedExerciseId={selectedex?.te_id}
                  exerciseStates={exerciseStates}
                  hideArrows={true}
                />
              )}
              {selectedex && resolveTeExecutionType(selectedex) && (
                <Chip
                  size="small"
                  label={teExecutionTypeLabel(resolveTeExecutionType(selectedex), t)}
                  sx={{ mt: 1, mb: 0.5 }}
                  color={
                    isKotExercise(selectedex)
                      ? "secondary"
                      : isTeSequences(selectedex)
                        ? "primary"
                        : "default"
                  }
                  variant="outlined"
                />
              )}
              </div>
              

              {selectedex?.te_id &&
                Array.isArray(
                  socketSequenceMetaByTeId[selectedex.te_id]?.coach_warnings
                ) &&
                socketSequenceMetaByTeId[selectedex.te_id].coach_warnings.length >
                  0 && (
                  <Alert severity="warning" sx={{ mb: 1.5, textAlign: "left" }}>
                    {socketSequenceMetaByTeId[selectedex.te_id].coach_warnings.join(
                      " · "
                    )}
                  </Alert>
                )}

              {/* Lock Warning - shown when exercise is locked */}
              {selectedex && isCurrentExerciseLocked && (
                <div style={{ 
                  marginBottom: "12px", 
                  padding: "8px", 
                  backgroundColor: "#fff3cd", 
                  borderRadius: "5px",
                  fontSize: "12px",
                  color: "#856404",
                  textAlign: "center"
                }}>
                  ⚠️ {t("exercise_locked_message")}
                </div>
              )}

              {/* Tables information */}
              {selectedex && (
                <div style={{ marginBottom: "20px", textAlign: "center", position: "relative" }}>
                  {/* Display tables available vs required */}
                  <div style={{ 
                    marginBottom: "15px", 
                    padding: "10px", 
                    backgroundColor: "#f5f5f5", 
                    borderRadius: "5px",
                    display: "inline-block"
                  }}>
                    <div style={{ fontSize: "14px", color: "#666" }}>
                      <strong>{t("tables_available") || "Tables Available"}: {(() => {
                        const totalTables = selectedgrp?.groupDetails?.gr_number_of_tables || tables?.length || 0;
                        // Count occupied tables (tables with at least one player assigned)
                        const occupiedTables = tables
                          ? tables.filter(tableHasAssignedPlayer).length
                          : 0;
                        return totalTables - occupiedTables;
                      })()}</strong>
                    </div>
                    {selectedSession?.ts_number_of_tables_required !== undefined && (
                      <>
                        <div style={{ fontSize: "14px", color: "#666", marginTop: "5px" }}>
                          <strong>{t("tables_required") || "Tables Required"}:</strong>{" "}
                          <span style={{
                            color: selectedSession.ts_number_of_tables_required > (selectedgrp?.groupDetails?.gr_number_of_tables || tables?.length || 0) 
                              ? "#d32f2f" 
                              : "#2e7d32",
                            fontWeight: "bold"
                          }}>
                            {selectedSession.ts_number_of_tables_required}
                          </span>
                          {" "}({players?.filter(p => p.presensee === true).length || 0} {t("players_present") || "players present"})
                        </div>
                        {(selectedgrp?.groupDetails?.gr_number_of_tables || 0) > selectedSession.ts_number_of_tables_required && (
                          <div style={{ fontSize: "14px", color: "#0877bc", marginTop: "5px", fontWeight: "bold" }}>
                            {t("tables_remaining") || "Tables Remaining"}:{" "}
                            {(selectedgrp?.groupDetails?.gr_number_of_tables || 0) - selectedSession.ts_number_of_tables_required}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                  {selectedSession?.ts_number_of_tables_required > (selectedgrp?.groupDetails?.gr_number_of_tables || tables?.length || 0) && (
                    <div style={{ 
                      color: "#d32f2f", 
                      fontSize: "14px", 
                      marginTop: "10px",
                      fontWeight: "bold"
                    }}>
                      ⚠️ {t("not_enough_tables_warning") || "Warning: Not enough tables available for all present players!"}
                    </div>
                  )}
                  {perror && (
                    <p style={{ color: "red", marginTop: "10px" }}>{perror}</p>
                  )}
                </div>
              )}

              {/* Next Exercise Card - Show when no exercise is started */}
              {!isSessionStarted && selectedex && exercises && exercises.length > 0 && (() => {
                const currentlng = currentAppLanguage();
                const nextExercise = selectedex;
                const isKotNextExercise = isKotExercise(nextExercise);
                const nextKotMaxRounds =
                  isKotNextExercise && nextExercise.kot_config?.max_rounds != null
                    ? nextExercise.kot_config.max_rounds
                    : null;
                const nextExerciseStateId =
                  exerciseStates[nextExercise.te_id] !== undefined
                    ? exerciseStates[nextExercise.te_id]
                    : resolveExerciseStateIdFromRow(nextExercise);
                
                // Get exercise type and subtype names
                let typeName = "";
                let subTypeName = "";
                if (nextExercise.type_ex) {
                  typeName = nextExercise.type_ex[`type_name_${currentlng}`] || nextExercise.type_ex.type_name_en || "";
                } else if (nextExercise.sub_type_ex?.tbl_type_ex) {
                  typeName = nextExercise.sub_type_ex.tbl_type_ex[`type_name_${currentlng}`] || nextExercise.sub_type_ex.tbl_type_ex.type_name_en || "";
                }
                if (nextExercise.sub_type_ex) {
                  subTypeName = nextExercise.sub_type_ex[`sub_type_name_${currentlng}`] || nextExercise.sub_type_ex.sub_type_name_en || "";
                }
                
                // Get first sequence if available
                const firstSequence = nextExercise.sequences && Array.isArray(nextExercise.sequences) && nextExercise.sequences.length > 0
                  ? nextExercise.sequences[0]
                  : null;
                
                let sequenceSubTypeName = "";
                if (firstSequence?.sub_type_ex) {
                  sequenceSubTypeName = firstSequence.sub_type_ex[`sub_type_name_${currentlng}`] || firstSequence.sub_type_ex.sub_type_name_en || "";
                }
                
                // Format time helper function
                const formatTime = (seconds) => {
                  if (!seconds && seconds !== 0) return "";
                  const mins = Math.floor(seconds / 60);
                  const secs = seconds % 60;
                  return `${mins}:${secs < 10 ? "0" : ""}${secs}`;
                };
                
                // Calculate remaining time for exercise (from te_duration in minutes)
                let exerciseRemainingTime = null;
                if (!isKotNextExercise && nextExercise.te_duration) {
                  exerciseRemainingTime = parseInt(nextExercise.te_duration) * 60;
                }
                
                // Calculate remaining time for first sequence
                let sequenceRemainingTime = null;
                if (firstSequence?.sequence_duration) {
                  sequenceRemainingTime = parseInt(firstSequence.sequence_duration) * 60;
                }
                
                return (
                  <div style={{
                    marginBottom: "20px",
                    padding: "20px",
                    backgroundColor: "#fff3e0",
                    borderRadius: "12px",
                    border: "3px solid #ff9800",
                    boxShadow: "0px 4px 12px rgba(255, 152, 0, 0.3)"
                  }}>
                    <div style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      flexWrap: "wrap",
                      gap: "24px"
                    }}>
                      {/* Exercise Information with Timer */}
                      <div style={{ flex: 1, minWidth: "250px" }}>
                        <div style={{ 
                          fontSize: "12px", 
                          fontWeight: "600", 
                          color: "#065a8a", 
                          marginBottom: "6px",
                          textTransform: "uppercase",
                          letterSpacing: "0.5px"
                        }}>
                          {t("next_exercise") || "Next Exercise"}:
                        </div>
                        <div style={{ 
                          fontSize: "20px", 
                          fontWeight: "700", 
                          color: "#ff9800", 
                          marginBottom: "12px"
                        }}>
                          {nextExercise.te_order ? `${nextExercise.te_order}. ` : ""}
                          {nextExercise.ex_name || (typeName && subTypeName ? `${typeName} - ${subTypeName}` : subTypeName || typeName || t("exercise") || "Exercise")}
                        </div>
                        {exerciseRemainingTime !== null && (
                          <div style={{ 
                            display: "flex", 
                            alignItems: "center", 
                            gap: "12px",
                            marginTop: "8px"
                          }}>
                            <div style={{ fontSize: "12px", color: "#666", fontWeight: "600" }}>
                              {t("duration") || "Duration"}:
                            </div>
                            <div style={{ 
                              fontSize: "28px", 
                              fontWeight: "700", 
                              color: "#ff9800",
                              fontFamily: "monospace",
                              lineHeight: "1"
                            }}>
                              {formatTime(exerciseRemainingTime)}
                            </div>
                          </div>
                        )}
                        <div style={{ fontSize: "12px", color: "#999", marginTop: "8px", fontStyle: "italic" }}>
                          {t("exercise_to_be_executed") || "Exercise to be executed"}
                        </div>
                      </div>
                      
                      {/* Sequence Information with Timer */}
                      {firstSequence && !isKotNextExercise && (
                        <div style={{ flex: 1, minWidth: "250px" }}>
                          <div style={{ 
                            fontSize: "12px", 
                            fontWeight: "600", 
                            color: "#065a8a", 
                            marginBottom: "6px",
                            textTransform: "uppercase",
                            letterSpacing: "0.5px"
                          }}>
                            {t("first_sequence") || "First Sequence"}
                          </div>
                          <div style={{ 
                            fontSize: "18px", 
                            fontWeight: "600", 
                            color: "#1976d2", 
                            marginBottom: "12px"
                          }}>
                            {sequenceSubTypeName || t("sequence") || "Sequence"}
                          </div>
                          {sequenceRemainingTime !== null && (
                            <div style={{ 
                              display: "flex", 
                              alignItems: "center", 
                              gap: "12px",
                              marginTop: "8px"
                            }}>
                              <div style={{ fontSize: "12px", color: "#666", fontWeight: "600" }}>
                                {t("duration") || "Duration"}:
                              </div>
                              <div style={{ 
                                fontSize: "28px", 
                                fontWeight: "700", 
                                color: "#1976d2",
                                fontFamily: "monospace",
                                lineHeight: "1"
                              }}>
                                {formatTime(sequenceRemainingTime)}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    {isKotNextExercise ? (
                      <div className="table-setup-exercise-card__kot">
                        <div className="ex_add_box__kot-meta table-setup-exercise-card__kot-meta">
                          <span className="ex_add_box__type-badge">
                            {t("kingOfTheTable")}
                          </span>
                          <span className="ex_add_box__kot-chip">
                            {nextExercise.kot_config?.kot_mode === "FULL_SET"
                              ? t("kotModeFullSet")
                              : t("kotModeStopScoreLead")}
                          </span>
                          {nextKotMaxRounds != null ? (
                            <span className="ex_add_box__kot-chip">
                              {t("kotMaxRounds")} {nextKotMaxRounds}
                            </span>
                          ) : null}
                        </div>
                        <KingOfTableSettings
                          embedded
                          te_id={nextExercise.te_id}
                          ts_id={selectedSession?.ts_id}
                          initialConfig={nextExercise.kot_config || null}
                          canEdit={false}
                          isCoachView={isCoachView}
                          viewerPlayerId={viewerPlayerId}
                          readOnlyView={!isCoachView}
                          socket={socket}
                          isActive={false}
                          exerciseStateId={nextExerciseStateId}
                          teStartTime={nextExercise.te_start_time}
                          teEndTime={nextExercise.te_end_time}
                          plannedDurationMinutes={nextExercise.te_duration}
                        />
                      </div>
                    ) : null}
                  </div>
                );
              })()}

              {/* Current Exercise and Sequence Display Card - Only show when session is started */}
              {isSessionStarted && (() => {
                // Determine currentExercise (match mobile sessionDetails): started → paused →
                // first scheduled by te_order; exclude finalized/cancelled as primary picks.
                const validExercises = exercises
                  .filter((ex) => ex && ex.te_id)
                  .slice()
                  .sort((a, b) => (a.te_order ?? 0) - (b.te_order ?? 0));

                const stateOf = (ex) =>
                  exerciseStates[ex.te_id] !== undefined
                    ? exerciseStates[ex.te_id]
                    : ex.te_state;

                let currentExercise = validExercises.find(
                  (ex) => stateOf(ex) === TE_STATE_STARTED
                );
                if (!currentExercise) {
                  currentExercise = validExercises.find(
                    (ex) => stateOf(ex) === TE_STATE_PAUSED
                  );
                }
                if (!currentExercise) {
                  currentExercise = validExercises.find(
                    (ex) => stateOf(ex) === TE_STATE_SCHEDULED
                  );
                }
                if (!currentExercise) {
                  currentExercise = validExercises.find((ex) => {
                    const s = stateOf(ex);
                    return s !== TE_STATE_FINALIZED && s !== TE_STATE_CANCELLED;
                  });
                }
                
                if (!currentExercise) return null;
                
                const currentSequenceIndex = currentSequences[currentExercise.te_id];
                const sequenceTimer = sequenceTimers[currentExercise.te_id];
                const exerciseTimer = exerciseTimers[currentExercise.te_id];
                const currentlng = currentAppLanguage();
                
                // Get exercise state - prioritize real-time exerciseStates over stored te_state
                // This ensures the card updates immediately when exerciseStateUpdate is received
                const exerciseState = exerciseStates[currentExercise.te_id] !== undefined 
                  ? exerciseStates[currentExercise.te_id] 
                  : (currentExercise.te_state || null);
                const isExerciseStarted = exerciseState === TE_STATE_STARTED;
                
                // Get the current sequence object from the exercise
                const currentSequenceObj = currentExercise.sequences && 
                                         Array.isArray(currentExercise.sequences) && 
                                         typeof currentSequenceIndex === 'number' &&
                                         currentSequenceIndex >= 0 &&
                                         currentSequenceIndex < currentExercise.sequences.length
                                         ? currentExercise.sequences[currentSequenceIndex]
                                         : (currentExercise.sequences && currentExercise.sequences.length > 0 ? currentExercise.sequences[0] : null);
                
                // Format time helper function
                const formatTime = (seconds) => {
                  if (!seconds && seconds !== 0) return "";
                  const mins = Math.floor(seconds / 60);
                  const secs = seconds % 60;
                  return `${mins}:${secs < 10 ? "0" : ""}${secs}`;
                };
                
                // Get exercise type and subtype names
                let typeName = "";
                let subTypeName = "";
                if (currentExercise.type_ex) {
                  typeName = currentExercise.type_ex[`type_name_${currentlng}`] || currentExercise.type_ex.type_name_en || "";
                } else if (currentExercise.sub_type_ex?.tbl_type_ex) {
                  typeName = currentExercise.sub_type_ex.tbl_type_ex[`type_name_${currentlng}`] || currentExercise.sub_type_ex.tbl_type_ex.type_name_en || "";
                }
                if (currentExercise.sub_type_ex) {
                  subTypeName = currentExercise.sub_type_ex[`sub_type_name_${currentlng}`] || currentExercise.sub_type_ex.sub_type_name_en || "";
                }
                
                // Get sequence subtype name if available
                let sequenceSubTypeName = "";
                if (currentSequenceObj?.sub_type_ex) {
                  sequenceSubTypeName = currentSequenceObj.sub_type_ex[`sub_type_name_${currentlng}`] || currentSequenceObj.sub_type_ex.sub_type_name_en || "";
                }

                const isKotRoundExercise = isKotExercise(currentExercise);
                const currentKotMaxRounds =
                  isKotRoundExercise && currentExercise.kot_config?.max_rounds != null
                    ? currentExercise.kot_config.max_rounds
                    : null;
                
                // Calculate remaining time for exercise
                let exerciseRemainingTime = null;
                if (
                  !isKotRoundExercise &&
                  isExerciseStarted &&
                  exerciseTimer !== undefined &&
                  exerciseTimer !== null
                ) {
                  exerciseRemainingTime = exerciseTimer;
                } else if (!isKotRoundExercise && currentExercise.te_duration) {
                  // If exercise is not started, use te_duration (in minutes) converted to seconds
                  exerciseRemainingTime = parseInt(currentExercise.te_duration) * 60;
                }
                
                // Calculate remaining time for sequence
                let sequenceRemainingTime = null;
                if (isExerciseStarted && sequenceTimer !== undefined && sequenceTimer !== null) {
                  sequenceRemainingTime = sequenceTimer;
                } else if (currentSequenceObj?.sequence_duration) {
                  // If sequence is not started, use sequence_duration (in minutes) converted to seconds
                  sequenceRemainingTime = parseInt(currentSequenceObj.sequence_duration) * 60;
                }

                return (
                  <div style={{
                    marginBottom: "20px",
                    padding: "20px",
                    backgroundColor: isExerciseStarted ? "#e8f5e9" : "#fff3e0",
                    borderRadius: "12px",
                    border: `3px solid ${isExerciseStarted ? "#28a745" : "#ff9800"}`,
                    boxShadow: `0px 4px 12px rgba(${isExerciseStarted ? "40, 167, 69" : "255, 152, 0"}, 0.3)`
                  }}>
                    <div style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      flexWrap: "wrap",
                      gap: "24px"
                    }}>
                      {/* Exercise Information with Timer */}
                      <div style={{ flex: 1, minWidth: "250px" }}>
                        <div style={{ 
                          fontSize: "12px", 
                          fontWeight: "600", 
                          color: "#065a8a", 
                          marginBottom: "6px",
                          textTransform: "uppercase",
                          letterSpacing: "0.5px"
                        }}>
                          {isExerciseStarted ? (t("current_exercise") || "Current Exercise") : (t("next_exercise") || "Next Exercise")}
                        </div>
                        <div style={{ 
                          fontSize: "20px", 
                          fontWeight: "700", 
                          color: isExerciseStarted ? "#28a745" : "#ff9800", 
                          marginBottom: "12px"
                        }}>
                          {currentExercise.te_order ? `${currentExercise.te_order}. ` : ""}
                          {typeName && subTypeName ? `${typeName} - ${subTypeName}` : subTypeName || typeName || t("exercise") || "Exercise"}
                        </div>
                        {exerciseRemainingTime !== null && (
                          <div style={{ 
                            display: "flex", 
                            alignItems: "center", 
                            gap: "12px",
                            marginTop: "8px"
                          }}>
                            <div style={{ fontSize: "12px", color: "#666", fontWeight: "600" }}>
                              {t("remaining_time") || "Remaining"}:
                            </div>
                            <div style={{ 
                              fontSize: "28px", 
                              fontWeight: "700", 
                              color: isExerciseStarted ? "#28a745" : "#ff9800",
                              fontFamily: "monospace",
                              lineHeight: "1"
                            }}>
                              {formatTime(exerciseRemainingTime)}
                            </div>
                          </div>
                        )}
                        {!isExerciseStarted && (
                          <div style={{ fontSize: "12px", color: "#999", marginTop: "8px", fontStyle: "italic" }}>
                            {t("exercise_to_be_executed") || "Exercise to be executed"}
                          </div>
                        )}
                      </div>
                      
                      {/* Sequence Information with Timer */}
                      {currentSequenceObj && !isKotRoundExercise && (
                        <div style={{ flex: 1, minWidth: "250px" }}>
                          <div style={{ 
                            fontSize: "12px", 
                            fontWeight: "600", 
                            color: "#065a8a", 
                            marginBottom: "6px",
                            textTransform: "uppercase",
                            letterSpacing: "0.5px"
                          }}>
                            {isExerciseStarted ? (t("current_sequence") || "Current Sequence") : (t("first_sequence") || "First Sequence")}
                          </div>
                          <div style={{ 
                            fontSize: "18px", 
                            fontWeight: "600", 
                            color: "#1976d2", 
                            marginBottom: "12px"
                          }}>
                            {sequenceSubTypeName || t("sequence") || "Sequence"}
                          </div>
                          {sequenceRemainingTime !== null && (
                            <div style={{ 
                              display: "flex", 
                              alignItems: "center", 
                              gap: "12px",
                              marginTop: "8px"
                            }}>
                              <div style={{ fontSize: "12px", color: "#666", fontWeight: "600" }}>
                                {t("remaining_time") || "Remaining"}:
                              </div>
                              <div style={{ 
                                fontSize: "28px", 
                                fontWeight: "700", 
                                color: "#1976d2",
                                fontFamily: "monospace",
                                lineHeight: "1"
                              }}>
                                {formatTime(sequenceRemainingTime)}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    {isKotRoundExercise ? (
                      <div className="table-setup-exercise-card__kot">
                        <div className="ex_add_box__kot-meta table-setup-exercise-card__kot-meta">
                          <span className="ex_add_box__type-badge">
                            {t("kingOfTheTable")}
                          </span>
                          <span className="ex_add_box__kot-chip">
                            {currentExercise.kot_config?.kot_mode === "FULL_SET"
                              ? t("kotModeFullSet")
                              : t("kotModeStopScoreLead")}
                          </span>
                          {currentKotMaxRounds != null ? (
                            <span className="ex_add_box__kot-chip">
                              {t("kotMaxRounds")} {currentKotMaxRounds}
                            </span>
                          ) : null}
                        </div>
                        <KingOfTableSettings
                          embedded
                          te_id={currentExercise.te_id}
                          ts_id={selectedSession?.ts_id}
                          initialConfig={currentExercise.kot_config || null}
                          canEdit={false}
                          isCoachView={isCoachView}
                          viewerPlayerId={viewerPlayerId}
                          readOnlyView={!isCoachView}
                          canEnterMatchResults={
                            isCoachView && isExerciseStarted
                          }
                          canEnterOwnMatchResults={
                            !isCoachView &&
                            isPresent &&
                            Boolean(viewerPlayerId) &&
                            isExerciseStarted
                          }
                          socket={socket}
                          isActive={isExerciseStarted}
                          exerciseStateId={exerciseState}
                          teStartTime={currentExercise.te_start_time}
                          teEndTime={currentExercise.te_end_time}
                          plannedDurationMinutes={currentExercise.te_duration}
                          remainingSeconds={exerciseTimer}
                        />
                      </div>
                    ) : null}
                  </div>
                );
              })()}

              {/* After current/next exercise card: turn icons + ranking row, then personalize + grid */}
              {selectedex && (
                <Box
                  className="session-setup__table-actions"
                  sx={{
                    width: "100%",
                    maxWidth: 1100,
                    mx: "auto",
                    mt: 2,
                    mb: 2,
                    px: { xs: 0.5, sm: 1 },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 1.5,
                    }}
                  >
                    {showPersonalizeControl ? (
                    <button
                      type="button"
                      onClick={() =>
                        personalizeControlEnabled && handleTogglePersonalizeMode()
                      }
                      disabled={!personalizeControlEnabled}
                      style={{
                        padding: "10px 18px",
                        backgroundColor: personalizeControlEnabled
                          ? personalizeMode
                            ? personalizeActive
                            : personalizeIdle
                          : "#9e9e9e",
                        color: "white",
                        border:
                          personalizeMode && personalizeControlEnabled
                            ? "2px solid #fff"
                            : "none",
                        borderRadius: "8px",
                        cursor: personalizeControlEnabled ? "pointer" : "not-allowed",
                        fontSize: "14px",
                        fontWeight: 600,
                        boxShadow:
                          personalizeMode && personalizeControlEnabled
                            ? `0px 0px 12px ${alpha(personalizeActive, 0.5)}`
                            : "0px 2px 6px rgba(0,0,0,0.15)",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "8px",
                        transition: "all 0.2s ease",
                        opacity: personalizeControlEnabled ? 1 : 0.85,
                      }}
                      title={
                        personalizeMode
                          ? t("cancelPersonalize") ||
                            "Cancel personalization – click to exit"
                          : canAddPlayersForDisplayedExercise
                            ? t("personalizeExercise") ||
                              "Personalize exercise for a player"
                            : t("addPlayersOnlyBeforeExerciseStarts") ||
                              "Personalize only before this exercise has started (not when it is running, paused, finalized, or cancelled)."
                      }
                    >
                      <PersonOutlineIcon fontSize="small" />
                      {personalizeMode
                        ? t("cancelPersonalize") || "Cancel"
                        : t("personalize") || "Personalize"}
                    </button>
                    ) : null}
                    {isCoachView &&
                    typeof window !== "undefined" &&
                    window.innerWidth > 650 ? (
                      <Tooltip title={t("configure_grid_layout") || "Grid layout"}>
                        <IconButton
                          onClick={() => setGridConfigOpen(true)}
                          sx={{
                            width: 48,
                            height: 48,
                            bgcolor: "#0877BC",
                            color: "white",
                            "&:hover": { bgcolor: "#065a8a" },
                            boxShadow: "0px 2px 6px rgba(0,0,0,0.15)",
                          }}
                          aria-label={t("configure_grid_layout") || "Grid layout"}
                        >
                          <SettingsIcon />
                        </IconButton>
                      </Tooltip>
                    ) : null}
                  </Box>
                </Box>
              )}

              {/* Personalize mode banner */}
              {personalizeMode && showPersonalizeControl && (
                <div style={{
                  marginBottom: "12px",
                  padding: "12px 16px",
                  background: `linear-gradient(135deg, ${personalizeIdle} 0%, ${personalizeActive} 100%)`,
                  borderRadius: "10px",
                  color: "#fff",
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  fontSize: "14px",
                  fontWeight: 500,
                  boxShadow: `0 2px 8px ${alpha(personalizeIdle, 0.35)}`,
                  animation: "fadeIn 0.3s ease",
                }}>
                  <PersonOutlineIcon sx={{ fontSize: 22 }} />
                  {t("personalizeInstructions") || "Click on a player's name on any table to personalize the exercise for them."}
                </div>
              )}

              <div 
                className="table_setup_list"
                style={{
                  gridTemplateColumns: `repeat(${gridColumns}, 1fr)`,
                  gridTemplateRows: selectedSession?.ts_grid_rows ? `repeat(${selectedSession.ts_grid_rows}, auto)` : 'auto'
                }}
              >
                {tables && tables.length > 0 ? (() => {
                  const tablesWithPlayers = sortTablesByStation(
                    tables.filter(tableHasAssignedPlayer),
                    mapTableIdToSubgroup(sessionSubgroups)
                  );
                  if (tablesWithPlayers.length === 0) {
                    return (
                      <div
                        style={{ textAlign: "center", padding: "40px", color: "#666", gridColumn: "1 / -1" }}
                      >
                        <p style={{ fontSize: "16px", marginBottom: "10px" }}>
                          {t(
                            "no_tables_with_players",
                            "No tables with players assigned yet."
                          )}
                        </p>
                      </div>
                    );
                  }
                  return tablesWithPlayers.map((table, tableIndex) => {
                  // Get current exercise and sequence info for role display
                  const activeStateId = "st202406071447279901"; // "started"
                  const finalizedStateId = "st202406071447279903"; // "finalized"
                  
                  // Use selectedex (selected exercise) first, then find running exercise, otherwise use first available
                  let currentExerciseForTable = null;
                  
                  // Priority 1: Use selected exercise if available
                  if (selectedex && selectedex.te_id) {
                    currentExerciseForTable = selectedex;
                  }
                  
                  // Priority 2: Find running (started) exercise
                  if (!currentExerciseForTable) {
                    currentExerciseForTable = exercises.find(ex => {
                      if (!ex || !ex.te_id) return false;
                      const exState = exerciseStates[ex.te_id] || ex.te_state;
                      return exState === activeStateId;
                    });
                  }
                  
                  // Priority 3: Find first non-finalized exercise
                  if (!currentExerciseForTable) {
                    const validExercises = exercises.filter(ex => ex && ex !== null && ex !== undefined && ex.te_id);
                    currentExerciseForTable = validExercises.find(ex => {
                      const exState = exerciseStates[ex.te_id] || ex.te_state;
                      return exState !== finalizedStateId;
                    });
                  }
                  
                  // Get current sequence for this table
                  let currentSequenceForTable = null;
                  if (currentExerciseForTable && currentExerciseForTable.sequences && Array.isArray(currentExerciseForTable.sequences)) {
                    const currentSequenceIndexForTable = currentSequences[currentExerciseForTable.te_id];
                    if (typeof currentSequenceIndexForTable === 'number' && 
                        currentSequenceIndexForTable >= 0 && 
                        currentSequenceIndexForTable < currentExerciseForTable.sequences.length) {
                      currentSequenceForTable = currentExerciseForTable.sequences[currentSequenceIndexForTable];
                    } else if (currentExerciseForTable.sequences.length > 0) {
                      currentSequenceForTable = currentExerciseForTable.sequences[0];
                    }
                  }
                  
                  // Convert table_number to string for comparison (handle both string and number)
                  const tableNumber = String(table.table_number || tableIndex + 1);
                  const tableHasPlayer1 = table.player1 !== null && table.player1 !== undefined && typeof table.player1 === 'object' && Object.keys(table.player1).length > 0 && (table.player1.player_id || table.player1.user_first_name || table.player1.user_alias);
                  const tableHasPlayer2 = table.player2 !== null && table.player2 !== undefined && typeof table.player2 === 'object' && Object.keys(table.player2).length > 0 && (table.player2.player_id || table.player2.user_first_name || table.player2.user_alias);
                  const tableHasPlayer3 = table.player3 !== null && table.player3 !== undefined && typeof table.player3 === 'object' && Object.keys(table.player3).length > 0 && (table.player3.player_id || table.player3.user_first_name || table.player3.user_alias);
                  const isSinglePlayerTable = (tableHasPlayer1 && !tableHasPlayer2) || (!tableHasPlayer1 && tableHasPlayer2);

                  const exerciseToSearch = selectedex || currentExerciseForTable;

                  // Roles follow **table slot**, not player identity: P1 = side A (player_a_role), P2 = side B (player_b_role).
                  // Protocol v2: prefer `current_sequences_by_table` per table when the backend partitions lanes.
                  // Otherwise live socket/timer payloads override plan rows so badges update on every sequence transition.
                  let player1Role = null;
                  let player2Role = null;
                  const exStateForRoles =
                    exerciseToSearch?.te_id != null
                      ? exerciseStates[exerciseToSearch.te_id] ||
                        exerciseToSearch.te_state
                      : null;
                  const isExerciseRunningForRoles =
                    exStateForRoles === TE_STATE_STARTED ||
                    exStateForRoles === TE_STATE_PAUSED;
                  const seqMetaForRoles =
                    exerciseToSearch?.te_id != null
                      ? socketSequenceMetaByTeId[exerciseToSearch.te_id]
                      : null;
                  const partitionRoles = pickRolesFromPartitionMeta(
                    seqMetaForRoles,
                    tableNumber
                  );
                  const liveRoles =
                    exerciseToSearch?.te_id != null
                      ? liveSequenceRolesByTeId[exerciseToSearch.te_id]
                      : null;
                  if (
                    isExerciseRunningForRoles &&
                    partitionRoles &&
                    (partitionRoles.player_a_role != null ||
                      partitionRoles.player_b_role != null)
                  ) {
                    player1Role = partitionRoles.player_a_role || null;
                    player2Role = partitionRoles.player_b_role || null;
                  } else if (
                    isExerciseRunningForRoles &&
                    liveRoles &&
                    (liveRoles.player_a_role != null ||
                      liveRoles.player_b_role != null)
                  ) {
                    player1Role = liveRoles.player_a_role || null;
                    player2Role = liveRoles.player_b_role || null;
                  } else if (
                    isTeSequences(exerciseToSearch) &&
                    exerciseToSearch?.sequences?.length
                  ) {
                    const idx =
                      exerciseToSearch.te_id != null
                        ? currentSequences[exerciseToSearch.te_id]
                        : null;
                    const seq = resolveSequenceRowForTableRoles(
                      exerciseToSearch,
                      tableNumber,
                      idx
                    );
                    if (seq) {
                      player1Role = seq.player_a_role || null;
                      player2Role = seq.player_b_role || null;
                    }
                  }

                  const hasPlayer1 = table.player1 !== null && 
                    table.player1 !== undefined && 
                    typeof table.player1 === 'object' && 
                    Object.keys(table.player1).length > 0 &&
                    (table.player1.player_id || table.player1.user_first_name || table.player1.user_alias);
                  const hasPlayer2 = table.player2 !== null && 
                    table.player2 !== undefined && 
                    typeof table.player2 === 'object' && 
                    Object.keys(table.player2).length > 0 &&
                    (table.player2.player_id || table.player2.user_first_name || table.player2.user_alias);
                  const hasPlayer3 = table.player3 !== null && 
                    table.player3 !== undefined && 
                    typeof table.player3 === 'object' && 
                    Object.keys(table.player3).length > 0 &&
                    (table.player3.player_id || table.player3.user_first_name || table.player3.user_alias);
                  const extraPlayers = extraSeatedPlayers(table);
                  const seatedCount = orderedSeatedPlayers(table).length;
                  const showAddPlayerButton = true;
                  const stationMeta =
                    sessionSubgroups.find(
                      (sg) => String(sg.tsg_id) === String(table.tsg_id)
                    ) ||
                    mapTableIdToSubgroup(sessionSubgroups).get(String(table.table_id)) ||
                    null;
                  const stationColor = table.tsg_color || stationMeta?.color || null;
                  const stationName = table.tsg_name || stationMeta?.name || null;

                  const slotDropActive = (ptype) =>
                    (Boolean(draggingPlayerId) &&
                      dragOverTableId === table.table_id &&
                      dragOverPlayerType === ptype) ||
                    (touchDropHighlight?.tableId === table.table_id &&
                      touchDropHighlight?.playerType === ptype);

                  const playerTouchSwapClass = (playerRow, ptype) => {
                    if (personalizeMode || !playerRow?.player_id || !touchPlayerSwapUi) return "";
                    const isSource =
                      touchPlayerSwapUi.sourceTableId === table.table_id &&
                      touchPlayerSwapUi.sourcePlayerType === ptype &&
                      touchPlayerSwapUi.sourcePlayerId === playerRow.player_id;
                    if (!isSource) return "";
                    if (touchPlayerSwapUi.phase === "holding") return " table_player_label--swap-holding";
                    if (touchPlayerSwapUi.phase === "dragging") return " table_player_label--swap-dragging";
                    return "";
                  };

                  const isTouchSwapSourceRow = (playerRow, ptype) =>
                    Boolean(
                      touchPlayerSwapUi &&
                        touchPlayerSwapUi.sourceTableId === table.table_id &&
                        touchPlayerSwapUi.sourcePlayerType === ptype &&
                        touchPlayerSwapUi.sourcePlayerId === playerRow?.player_id
                    );

                  return (
                    <div
                      key={table.table_id || tableIndex}
                      className={`table_setups${stationColor ? " table_setups--station" : ""}`}
                      data-table-index={tableIndex}
                      draggable={!isCurrentExerciseLocked}
                      onDragStart={isCurrentExerciseLocked ? undefined : handleTableDragStart(tableIndex)}
                      onDragEnd={handleTableDragEnd}
                      onDragOver={isCurrentExerciseLocked ? undefined : handleTableDragOver}
                      onDrop={isCurrentExerciseLocked ? undefined : handleTableDrop(tableIndex)}
                      style={{
                        minHeight: `${responsiveStyles.cardMinHeight}px`,
                        padding: responsiveStyles.cardPadding,
                        cursor: isCurrentExerciseLocked ? "not-allowed" : "move",
                        opacity: draggedTableIndex === tableIndex ? 0.5 : (isCurrentExerciseLocked ? 0.7 : 1),
                        position: "relative",
                        ...(stationColor
                          ? { "--psg-accent": stationColor, borderTop: `4px solid ${stationColor}` }
                          : {}),
                      }}
                    >
                      {stationName ? (
                        <div className="table_setups__station-label">
                          {stationName}
                        </div>
                      ) : null}
                      {/* Add Player Button - Top Right Corner (always enabled, even during exercise) */}
                      {showAddPlayerButton && kotTableEditingEnabled && (
                        <IconButton
                          onClick={(e) => {
                            e.stopPropagation();
                            if (seatedCount >= 2) {
                              setAddExtraTable(table);
                            } else {
                              handleadd(table.table_id);
                            }
                          }}
                          size="small"
                          sx={{
                            position: "absolute",
                            top: "8px",
                            right: "8px",
                            bgcolor: "#28a745",
                            color: "white",
                            "&:hover": { bgcolor: "#218838" },
                            zIndex: 10,
                            padding: "6px",
                            minWidth: "auto",
                            width: "32px",
                            height: "32px"
                          }}
                          title={t("add_player_to_table") || "Add player to table"}
                        >
                          <PersonAddIcon fontSize="small" />
                        </IconButton>
                      )}
                      
                      <div
                        className="table_setups_content"
                        style={{
                          display: "flex",
                          flexWrap: "wrap",
                          alignItems: "stretch",
                        }}
                      >
                      <div
                        data-slot="player1"
                        className={slotDropActive("player1") ? "table_player_label_slot--swap-drop-target" : undefined}
                        style={{
                          padding: responsiveStyles.playerContainerPadding,
                          transition: "box-shadow 0.15s ease, border-color 0.15s ease, background-color 0.15s ease",
                          ...(isSinglePlayerTable && !tableHasPlayer1
                            ? {
                                backgroundColor: "#fff8e1",
                                border: "2px dashed #ff9800",
                                borderRadius: "8px",
                                position: "relative"
                              }
                            : {})
                        }}
                        onDragOver={!isCurrentExerciseLocked ? (e) => { e.stopPropagation(); handleDragOver(table, "player1")(e); } : undefined}
                        onDragLeave={!isCurrentExerciseLocked ? handleDragLeave : undefined}
                        onDrop={!isCurrentExerciseLocked ? (e) => { e.stopPropagation(); handleDrop(table, "player1")(e); } : undefined}
                      >
                        {(() => {
                          // Check if player1 is empty/null/undefined
                          const player1 = table.player1;
                          const hasPlayer1 = player1 !== null && 
                                           player1 !== undefined &&
                                           typeof player1 === 'object' && 
                                           Object.keys(player1).length > 0 &&
                                           (player1.player_id || player1.user_first_name || player1.user_alias);
                          return !hasPlayer1;
                        })() ? (
                          isSinglePlayerTable ? (
                            <div
                              className="table_player_label"
                              style={{
                                padding: "8px 12px",
                                backgroundColor: "#f5f5f5",
                                border: "2px dashed #999",
                                borderRadius: "8px",
                                color: "#666",
                                fontWeight: "600",
                                textAlign: "center",
                                fontSize: `${responsiveStyles.playerNameFontSize}px`
                              }}
                            >
                              {t("empty") || "Empty"}
                            </div>
                          ) : !kotTableEditingEnabled ? (
                            <div
                              className="table_player_label"
                              style={{
                                padding: "8px 12px",
                                backgroundColor: "#f5f5f5",
                                border: "2px dashed #ccc",
                                borderRadius: "8px",
                                color: "#888",
                                fontWeight: 600,
                                textAlign: "center",
                                fontSize: `${responsiveStyles.playerNameFontSize}px`,
                              }}
                            >
                              {t("empty") || "Empty"}
                            </div>
                          ) : (
                          <select
                            className="table_select_player"
                            value={
                              selplayer?.tableid === table.table_id
                                ? selplayer?.id || ""
                                : ""
                            }
                            onChange={(e) => handlesetplayer1(e, table)}
                            style={{
                              cursor: "pointer"
                            }}
                          >
                            <option value="" disabled>
                              {t("playerlist")}
                            </option>
                            {(() => {
                              const available = filterPlayersAvailableToSeat(
                                players,
                                tables,
                                {
                                  excludeIds: [
                                    table.player2?.player_id,
                                    table.player2?.user_id,
                                    selplayer2?.tableid === table.table_id
                                      ? selplayer2?.id
                                      : null,
                                  ],
                                }
                              );
                              if (!available.length) {
                                return (
                                  <option value="" disabled>
                                    {t("no_players_available") ||
                                      "No players available"}
                                  </option>
                                );
                              }
                              return available.map((d, index) => {
                                const rankingPoints =
                                  d.ranking_points !== null &&
                                  d.ranking_points !== undefined
                                    ? d.ranking_points
                                    : null;
                                const classement =
                                  d.classement ||
                                  d.fltt_classement ||
                                  d.classment ||
                                  null;
                                const name =
                                  d.user_alias ||
                                  d.user_first_name ||
                                  d.player_id ||
                                  `Player ${index + 1}`;
                                const displayText = [
                                  rankingPoints !== null
                                    ? `${rankingPoints} pts`
                                    : null,
                                  classement || null,
                                  name,
                                ]
                                  .filter(Boolean)
                                  .join(" • ");
                                return (
                                  <option value={d.player_id} key={d.player_id || index}>
                                    {displayText}
                                  </option>
                                );
                              });
                            })()}
                          </select>
                          )
                        ) : (
                          <div className="table_player_label_container">
                            {renderPlayerRankingStats(
                              table?.player1,
                              responsiveStyles.statsFontSize
                            )}
                            <div 
                              className={`table_player_label${personalizeMode ? " personalize-highlight" : ""}${!personalizeMode ? " table_player_label--coach-profile" : ""}${playerTouchSwapClass(table.player1, "player1")}`}
                              draggable={personalizeMode ? false : isCoachView && allowHtml5PlayerDrag && !isCurrentExerciseLocked}
                              onClick={personalizeMode ? () => handlePlayerClickForPersonalize(table.player1) : undefined}
                              onDoubleClick={
                                personalizeMode
                                  ? undefined
                                  : (e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      handleOpenPlayerProfile(table.player1);
                                    }
                              }
                              onDragStart={personalizeMode ? undefined : (isCurrentExerciseLocked ? undefined : handleDragStart(table.player1, "player1"))}
                              onDragEnd={personalizeMode ? undefined : (isCurrentExerciseLocked ? undefined : handleDragEnd)}
                              onDragOver={personalizeMode ? undefined : (isCurrentExerciseLocked ? undefined : handleDragOver(table, "player1"))}
                              onDragLeave={personalizeMode ? undefined : (isCurrentExerciseLocked ? undefined : handleDragLeave)}
                              onDrop={personalizeMode ? undefined : (isCurrentExerciseLocked ? undefined : handleDrop(table, "player1"))}
                              onTouchStart={personalizeMode ? undefined : (isCurrentExerciseLocked ? undefined : handleTouchStart(table.player1, "player1", table))}
                              onTouchEnd={
                                personalizeMode
                                  ? undefined
                                  : isCurrentExerciseLocked
                                  ? handleTouchEndProfileOnly(table.player1)
                                  : handleTouchEndOnLabel(table, "player1")
                              }
                              title={
                                !personalizeMode
                                  ? t("doubleTapProfileHint") ||
                                    "Double-click or double-tap name for profile"
                                  : undefined
                              }
                              role="button"
                              tabIndex={0}
                              style={{ 
                                fontSize: `${responsiveStyles.playerNameFontSize}px`,
                                cursor: personalizeMode ? "pointer" : (isCurrentExerciseLocked ? "pointer" : (draggingPlayerId === table.player1?.player_id ? "grabbing" : "grab")),
                                opacity:
                                  isCurrentExerciseLocked && !personalizeMode
                                    ? 0.7
                                    : isTouchSwapSourceRow(table.player1, "player1")
                                    ? undefined
                                    : draggingPlayerId === table.player1?.player_id
                                    ? 0.5
                                    : 1,
                                transform: isTouchSwapSourceRow(table.player1, "player1")
                                  ? undefined
                                  : draggingPlayerId === table.player1?.player_id
                                  ? "scale(0.95)"
                                  : "none",
                                transition: "all 0.2s ease",
                                WebkitTouchCallout: "none",
                                WebkitUserSelect: "none",
                                userSelect: "none",
                                backgroundColor: personalizeMode ? alpha(personalizeIdle, 0.08) : ((dragOverTableId === table.table_id && dragOverPlayerType === "player1") ? "#e3f2fd" : "transparent"),
                                borderRadius: personalizeMode ? "6px" : ((dragOverTableId === table.table_id && dragOverPlayerType === "player1") ? "4px" : "0"),
                                padding: personalizeMode ? "4px 8px" : undefined,
                                border: personalizeMode ? `1px dashed ${personalizeIdle}` : undefined,
                              }}
                            >
                              {(() => {
                                const player = table?.player1;
                                const noNameLabel = t("noName", { lng: (currentlng || "en").split("-")[0] }) || "No name";
                                if (!player) return noNameLabel;
                                if (typeof player === 'object') {
                                  return player.user_first_name || player.user_alias || player.player_id || noNameLabel;
                                }
                                return noNameLabel;
                              })()}
                            </div>
                            {renderPlayerPersonalizedRow(table.player1, currentExerciseForTable)}
                            {/* Display Heart Rate for Player 1 (side A) — live stream only */}
                            {(() => {
                              const tableHR = hrData[table.table_id];
                              const player1HR = resolveTableSlotLiveHr(
                                tableHR,
                                "player1",
                                table?.player1
                              );
                              if (player1HR != null) {
                                return (
                                  <div 
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: "4px",
                                      marginTop: "4px",
                                      fontSize: `${Math.max(11, responsiveStyles.statsFontSize)}px`,
                                      fontWeight: "600",
                                      color: player1HR > 180 ? "#f44336" : player1HR > 150 ? "#ff9800" : "#4caf50"
                                    }}
                                  >
                                    <span style={{ fontSize: "14px" }}>❤️</span>
                                    <span>{player1HR} BPM</span>
                                  </div>
                                );
                              }
                              return null;
                            })()}
                            {/* Display player role badge if available */}
                            {player1Role && (
                              <div
                                className={`table_player_role_badge ${
                                  player1Role === "active"
                                    ? "table_player_role_badge--active"
                                    : player1Role === "sparring"
                                    ? "table_player_role_badge--sparring"
                                    : "table_player_role_badge--both"
                                }`}
                              >
                                {player1Role === "active"
                                  ? t("performance_role", "Performance")
                                  : player1Role === "sparring"
                                  ? t("sparring") || "Sparring"
                                  : t("both") || "Both"}
                              </div>
                            )}
                          </div>
                        )}
                        {!table.player1 || (typeof table.player1 === 'object' && Object.keys(table.player1).length === 0) || (!table.player1.player_id && !table.player1.user_first_name && !table.player1.user_alias) ? (
                        kotTableEditingEnabled ? (
                        <p className="table_addplayer_title">
                          {t("players")} 1
                        </p>
                        ) : null
                        ) : null}
                      </div>
                      <div className="table_cbicon">
                        <img 
                          src={tableimg} 
                          alt="Table" 
                          className="tableimg" 
                          style={{ width: `${responsiveStyles.imageWidth}px` }}
                        />
                        <p 
                          className="table_title"
                          style={{ fontSize: `${responsiveStyles.tableNumberFontSize}px` }}
                        >
                          {table.table_number || tableIndex + 1}
                        </p>
                      </div>
                      <div
                        data-slot="player2"
                        className={slotDropActive("player2") ? "table_player_label_slot--swap-drop-target" : undefined}
                        style={{
                          padding: responsiveStyles.playerContainerPadding,
                          transition: "box-shadow 0.15s ease, border-color 0.15s ease, background-color 0.15s ease",
                          ...(isSinglePlayerTable && !tableHasPlayer2
                            ? {
                                backgroundColor: "#fff8e1",
                                border: "2px dashed #ff9800",
                                borderRadius: "8px",
                                position: "relative"
                              }
                            : {})
                        }}
                        onDragOver={!isCurrentExerciseLocked ? (e) => { e.stopPropagation(); handleDragOver(table, "player2")(e); } : undefined}
                        onDragLeave={!isCurrentExerciseLocked ? handleDragLeave : undefined}
                        onDrop={!isCurrentExerciseLocked ? (e) => { e.stopPropagation(); handleDrop(table, "player2")(e); } : undefined}
                      >
                        {(() => {
                          // Check if player2 is empty/null/undefined
                          const player2 = table.player2;
                          const hasPlayer2 = player2 !== null && 
                                           player2 !== undefined &&
                                           typeof player2 === 'object' && 
                                           Object.keys(player2).length > 0 &&
                                           (player2.player_id || player2.user_first_name || player2.user_alias);
                          return !hasPlayer2;
                        })() ? (
                          isSinglePlayerTable ? (
                            <div
                              className="table_player_label"
                              style={{
                                padding: "8px 12px",
                                backgroundColor: "#f5f5f5",
                                border: "2px dashed #999",
                                borderRadius: "8px",
                                color: "#666",
                                fontWeight: "600",
                                textAlign: "center",
                                fontSize: `${responsiveStyles.playerNameFontSize}px`
                              }}
                            >
                              {t("empty") || "Empty"}
                            </div>
                          ) : !kotTableEditingEnabled ? (
                            <div
                              className="table_player_label"
                              style={{
                                padding: "8px 12px",
                                backgroundColor: "#f5f5f5",
                                border: "2px dashed #ccc",
                                borderRadius: "8px",
                                color: "#888",
                                fontWeight: 600,
                                textAlign: "center",
                                fontSize: `${responsiveStyles.playerNameFontSize}px`,
                              }}
                            >
                              {t("empty") || "Empty"}
                            </div>
                          ) : (
                          <select
                            className="table_select_player"
                            value={
                              selplayer2?.tableid === table.table_id
                                ? selplayer2?.id || ""
                                : ""
                            }
                            onChange={(e) => handlesetplayer2(e, table)}
                            style={{
                              cursor: "pointer"
                            }}
                          >
                            <option value="" disabled>
                              {t("playerlist")}{" "}
                            </option>
                            {(() => {
                              const available = filterPlayersAvailableToSeat(
                                players,
                                tables,
                                {
                                  excludeIds: [
                                    table.player1?.player_id,
                                    table.player1?.user_id,
                                    selplayer?.tableid === table.table_id
                                      ? selplayer?.id
                                      : null,
                                  ],
                                }
                              );
                              if (!available.length) {
                                return (
                                  <option value="" disabled>
                                    {t("no_players_available") ||
                                      "No players available"}
                                  </option>
                                );
                              }
                              return available.map((d, index) => {
                                const rankingPoints =
                                  d.ranking_points !== null &&
                                  d.ranking_points !== undefined
                                    ? Math.round(d.ranking_points)
                                    : null;
                                const classement =
                                  d.classement ||
                                  d.fltt_classement ||
                                  d.classment ||
                                  null;
                                const name =
                                  d.user_alias ||
                                  d.user_first_name ||
                                  d.player_id ||
                                  `Player ${index + 1}`;
                                let displayText = "";
                                if (classement) {
                                  displayText =
                                    rankingPoints !== null
                                      ? `${classement} - ${rankingPoints} pts`
                                      : classement;
                                } else if (rankingPoints !== null) {
                                  displayText = `${rankingPoints} pts`;
                                } else {
                                  displayText = name;
                                }
                                displayText = displayText
                                  ? `${displayText} • ${name}`
                                  : name;
                                return (
                                  <option value={d.player_id} key={d.player_id || index}>
                                    {displayText}
                                  </option>
                                );
                              });
                            })()}
                          </select>
                          )
                        ) : (
                          <div className="table_player_label_container">
                            {renderPlayerRankingStats(
                              table?.player2,
                              responsiveStyles.statsFontSize
                            )}
                            <div 
                              className={`table_player_label${personalizeMode ? " personalize-highlight" : ""}${!personalizeMode ? " table_player_label--coach-profile" : ""}${playerTouchSwapClass(table.player2, "player2")}`}
                              draggable={personalizeMode ? false : isCoachView && allowHtml5PlayerDrag && !isCurrentExerciseLocked}
                              onClick={personalizeMode ? () => handlePlayerClickForPersonalize(table.player2) : undefined}
                              onDoubleClick={
                                personalizeMode
                                  ? undefined
                                  : (e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      handleOpenPlayerProfile(table.player2);
                                    }
                              }
                              onDragStart={personalizeMode ? undefined : (isCurrentExerciseLocked ? undefined : handleDragStart(table.player2, "player2"))}
                              onDragEnd={personalizeMode ? undefined : (isCurrentExerciseLocked ? undefined : handleDragEnd)}
                              onDragOver={personalizeMode ? undefined : (isCurrentExerciseLocked ? undefined : handleDragOver(table, "player2"))}
                              onDragLeave={personalizeMode ? undefined : (isCurrentExerciseLocked ? undefined : handleDragLeave)}
                              onDrop={personalizeMode ? undefined : (isCurrentExerciseLocked ? undefined : handleDrop(table, "player2"))}
                              onTouchStart={personalizeMode ? undefined : (isCurrentExerciseLocked ? undefined : handleTouchStart(table.player2, "player2", table))}
                              onTouchEnd={
                                personalizeMode
                                  ? undefined
                                  : isCurrentExerciseLocked
                                  ? handleTouchEndProfileOnly(table.player2)
                                  : handleTouchEndOnLabel(table, "player2")
                              }
                              title={
                                !personalizeMode
                                  ? t("doubleTapProfileHint") ||
                                    "Double-click or double-tap name for profile"
                                  : undefined
                              }
                              role="button"
                              tabIndex={0}
                              style={{ 
                                fontSize: `${responsiveStyles.playerNameFontSize}px`,
                                cursor: personalizeMode ? "pointer" : (isCurrentExerciseLocked ? "pointer" : (draggingPlayerId === table.player2?.player_id ? "grabbing" : "grab")),
                                opacity:
                                  isCurrentExerciseLocked && !personalizeMode
                                    ? 0.7
                                    : isTouchSwapSourceRow(table.player2, "player2")
                                    ? undefined
                                    : draggingPlayerId === table.player2?.player_id
                                    ? 0.5
                                    : 1,
                                transform: isTouchSwapSourceRow(table.player2, "player2")
                                  ? undefined
                                  : draggingPlayerId === table.player2?.player_id
                                  ? "scale(0.95)"
                                  : "none",
                                transition: "all 0.2s ease",
                                WebkitTouchCallout: "none",
                                WebkitUserSelect: "none",
                                userSelect: "none",
                                backgroundColor: personalizeMode ? alpha(personalizeIdle, 0.08) : ((dragOverTableId === table.table_id && dragOverPlayerType === "player2") ? "#e3f2fd" : "transparent"),
                                borderRadius: personalizeMode ? "6px" : ((dragOverTableId === table.table_id && dragOverPlayerType === "player2") ? "4px" : "0"),
                                padding: personalizeMode ? "4px 8px" : undefined,
                                border: personalizeMode ? `1px dashed ${personalizeIdle}` : undefined,
                              }}
                            >
                              {(() => {
                                const player = table?.player2;
                                const noNameLabel = t("noName", { lng: (currentlng || "en").split("-")[0] }) || "No name";
                                if (!player) return noNameLabel;
                                if (typeof player === 'object') {
                                  return player.user_first_name || player.user_alias || player.player_id || noNameLabel;
                                }
                                return noNameLabel;
                              })()}
                            </div>
                            {renderPlayerPersonalizedRow(table.player2, currentExerciseForTable)}
                            {/* Display Heart Rate for Player 2 (side B) — live stream only */}
                            {(() => {
                              const tableHR = hrData[table.table_id];
                              const player2HR = resolveTableSlotLiveHr(
                                tableHR,
                                "player2",
                                table?.player2
                              );
                              if (player2HR != null) {
                                return (
                                  <div 
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: "4px",
                                      marginTop: "4px",
                                      fontSize: `${Math.max(11, responsiveStyles.statsFontSize)}px`,
                                      fontWeight: "600",
                                      color: player2HR > 180 ? "#f44336" : player2HR > 150 ? "#ff9800" : "#4caf50"
                                    }}
                                  >
                                    <span style={{ fontSize: "14px" }}>❤️</span>
                                    <span>{player2HR} BPM</span>
                                  </div>
                                );
                              }
                              return null;
                            })()}
                            {/* Display player role badge if available */}
                            {player2Role && (
                              <div
                                className={`table_player_role_badge ${
                                  player2Role === "active"
                                    ? "table_player_role_badge--active"
                                    : player2Role === "sparring"
                                    ? "table_player_role_badge--sparring"
                                    : "table_player_role_badge--both"
                                }`}
                              >
                                {player2Role === "active"
                                  ? t("performance_role", "Performance")
                                  : player2Role === "sparring"
                                  ? t("sparring") || "Sparring"
                                  : t("both") || "Both"}
                              </div>
                            )}
                          </div>
                        )}
                        {!table.player2 || (typeof table.player2 === 'object' && Object.keys(table.player2).length === 0) || (!table.player2.player_id && !table.player2.user_first_name && !table.player2.user_alias) ? (
                        kotTableEditingEnabled ? (
                        <p className="table_addplayer_title">
                          {t("players")} 2
                        </p>
                        ) : null
                        ) : null}
                      </div>
                      {!isKotDisplayedExercise &&
                        tableHasPlayer1 &&
                        tableHasPlayer2 &&
                        hasPlayer3 && (
                        <div
                          data-slot="player3"
                          className={slotDropActive("player3") ? "table_player_label_slot--swap-drop-target" : undefined}
                          style={{
                            width: "100%",
                            flexBasis: "100%",
                            marginTop: 6,
                            paddingTop: 8,
                            borderTop: "1px solid #e8e8e8",
                            padding: responsiveStyles.playerContainerPadding,
                            transition: "box-shadow 0.15s ease, border-color 0.15s ease, background-color 0.15s ease",
                          }}
                          onDragOver={!isCurrentExerciseLocked ? (e) => { e.stopPropagation(); handleDragOver(table, "player3")(e); } : undefined}
                          onDragLeave={!isCurrentExerciseLocked ? handleDragLeave : undefined}
                          onDrop={!isCurrentExerciseLocked ? (e) => { e.stopPropagation(); handleDrop(table, "player3")(e); } : undefined}
                        >
                            <div className="table_player_label_container">
                              <div
                                className={`table_player_label${personalizeMode ? " personalize-highlight" : ""}${!personalizeMode ? " table_player_label--coach-profile" : ""}${playerTouchSwapClass(table.player3, "player3")}`}
                                draggable={personalizeMode ? false : isCoachView && allowHtml5PlayerDrag && !isCurrentExerciseLocked}
                                onClick={personalizeMode ? () => handlePlayerClickForPersonalize(table.player3) : undefined}
                                onDoubleClick={
                                  personalizeMode
                                    ? undefined
                                    : (e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        handleOpenPlayerProfile(table.player3);
                                      }
                                }
                                onDragStart={personalizeMode ? undefined : (isCurrentExerciseLocked ? undefined : handleDragStart(table.player3, "player3"))}
                                onDragEnd={personalizeMode ? undefined : (isCurrentExerciseLocked ? undefined : handleDragEnd)}
                                onDragOver={personalizeMode ? undefined : (isCurrentExerciseLocked ? undefined : handleDragOver(table, "player3"))}
                                onDragLeave={personalizeMode ? undefined : (isCurrentExerciseLocked ? undefined : handleDragLeave)}
                                onDrop={personalizeMode ? undefined : (isCurrentExerciseLocked ? undefined : handleDrop(table, "player3"))}
                                onTouchStart={personalizeMode ? undefined : (isCurrentExerciseLocked ? undefined : handleTouchStart(table.player3, "player3", table))}
                                onTouchEnd={
                                  personalizeMode
                                    ? undefined
                                    : isCurrentExerciseLocked
                                    ? handleTouchEndProfileOnly(table.player3)
                                    : handleTouchEndOnLabel(table, "player3")
                                }
                                title={
                                  !personalizeMode
                                    ? t("doubleTapProfileHint") ||
                                      "Double-click or double-tap name for profile"
                                    : undefined
                                }
                                role="button"
                                tabIndex={0}
                                style={{
                                  fontSize: `${responsiveStyles.playerNameFontSize}px`,
                                  cursor: personalizeMode
                                    ? "pointer"
                                    : isCurrentExerciseLocked
                                    ? "pointer"
                                    : draggingPlayerId === table.player3?.player_id
                                    ? "grabbing"
                                    : "grab",
                                  opacity:
                                    isCurrentExerciseLocked && !personalizeMode
                                      ? 0.7
                                      : isTouchSwapSourceRow(table.player3, "player3")
                                      ? undefined
                                      : draggingPlayerId === table.player3?.player_id
                                      ? 0.5
                                      : 1,
                                  transform: isTouchSwapSourceRow(table.player3, "player3")
                                    ? undefined
                                    : draggingPlayerId === table.player3?.player_id
                                    ? "scale(0.95)"
                                    : "none",
                                  transition: "all 0.2s ease",
                                  WebkitTouchCallout: "none",
                                  WebkitUserSelect: "none",
                                  userSelect: "none",
                                  backgroundColor:
                                    personalizeMode
                                      ? alpha(personalizeIdle, 0.08)
                                      : dragOverTableId === table.table_id && dragOverPlayerType === "player3"
                                      ? "#e3f2fd"
                                      : "transparent",
                                  borderRadius: personalizeMode ? "6px" : dragOverTableId === table.table_id && dragOverPlayerType === "player3" ? "4px" : "0",
                                  padding: personalizeMode ? "4px 8px" : undefined,
                                  border: personalizeMode ? `1px dashed ${personalizeIdle}` : undefined,
                                }}
                              >
                                {table.player3?.user_first_name ||
                                  table.player3?.user_alias ||
                                  table.player3?.player_id ||
                                  (t("noName", { lng: (currentlng || "en").split("-")[0] }) || "No name")}
                              </div>
                              {renderPlayerPersonalizedRow(table.player3, currentExerciseForTable)}
                            </div>
                        </div>
                      )}
                      {extraPlayers.length > 0 ? (
                        <div className="table_setups__extras">
                          {extraPlayers.map((p, extraIdx) => (
                            <span
                              key={p.player_id || extraIdx}
                              className="table_setups__extra-chip"
                            >
                              {p.user_alias || p.user_first_name || p.player_id}
                            </span>
                          ))}
                        </div>
                      ) : null}
                      </div>
                    </div>
                  );
                  });
                })() : (
                  <div style={{ textAlign: "center", padding: "40px", color: "#666" }}>
                    <p style={{ fontSize: "16px", marginBottom: "10px" }}>
                      {t("no_tables_configured") || "No tables configured for this group's training room."}
                    </p>
                    <p style={{ fontSize: "14px" }}>
                      {t("please_configure_tables") || "Please configure tables in the training room settings."}
                    </p>
                  </div>
                )}
              </div>
            </div>
        </>
      ) : (
        <p className="select_msg">{t("please_select_session")}</p>
      )}
      <GridConfigDialog
        open={gridConfigOpen}
        onClose={() => setGridConfigOpen(false)}
        selectedSession={selectedSession}
        initialColumns={gridColumns}
        initialRows={selectedSession?.ts_grid_rows || null}
        onUpdate={async (newColumns, newRows) => {
          // Update local state immediately with new values
          if (newColumns !== undefined) {
            setGridColumns(newColumns);
          }
          
          // Update selectedSession if it's available (for immediate UI update)
          if (selectedSession) {
            selectedSession.ts_grid_columns = newColumns;
            if (newRows !== undefined) {
              selectedSession.ts_grid_rows = newRows;
            }
          }
          
          setGridConfigOpen(false);
          
          // Force a refresh of tables to apply new grid layout
          await gettablelist(selectedgrp?.groupDetails?.gr_id);
        }}
      />
      
      {/* Assign new player to table (when all tables full or coach picks table) */}
      <Dialog
        open={assignTableDialogOpen}
        onClose={handleAssignTableDialogClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          {t("assign_new_player_to_table") || "Assign new player to table"}
        </DialogTitle>
        <DialogContent dividers>
          {assignTableDialogPlayer && (
            <Box sx={{ mb: 2, p: 1.5, backgroundColor: "#e3f2fd", borderRadius: 1, display: "flex", alignItems: "center", gap: 1 }}>
              <PersonAddIcon sx={{ color: "#1976d2" }} />
              <Typography variant="body1">
                <strong>{getPlayerDisplayName(assignTableDialogPlayer)}</strong>
                {" — "}
                {t("assign_new_player_table_hint") ||
                  "Replace a player, or add as an extra seat on a table that already has two."}
              </Typography>
            </Box>
          )}
          {(assignTableDialogTables || []).map((tbl) => {
            const seatedOnTable = orderedSeatedPlayers(tbl);
            const hasP1 = seatedOnTable.length >= 1;
            const hasP2 = seatedOnTable.length >= 2;
            const hasP3 = seatedOnTable.length >= 3;
            const p1Name = getPlayerDisplayName(seatedOnTable[0] || tbl?.player1);
            const p2Name = getPlayerDisplayName(seatedOnTable[1] || tbl?.player2);
            const p3Name = getPlayerDisplayName(seatedOnTable[2] || tbl?.player3);
            return (
              <Box key={tbl.table_id} sx={{ mb: 1.5, border: "1px solid #e0e0e0", borderRadius: 2, overflow: "hidden" }}>
                <Box sx={{ px: 2, py: 1, backgroundColor: "#f5f5f5", borderBottom: "1px solid #e0e0e0" }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    {t("table_with_number", { number: tbl.table_number ?? "" })}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                  {hasP1 && hasP2 && !isKotDisplayedExercise && (
                    <ListItemButton
                      onClick={() => handleAssignTableDialogSelect(tbl, "addNth")}
                      sx={{
                        py: 1.5,
                        px: 2,
                        borderBottom: "1px solid #f0f0f0",
                        "&:hover": { backgroundColor: "#e8f5e9" },
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", width: "100%", gap: 1 }}>
                        <Typography sx={{ flex: 1, fontWeight: 600, color: "#2e7d32" }}>
                          {t("add_as_extra_player") || "Add as extra player at this table"}
                        </Typography>
                      </Box>
                    </ListItemButton>
                  )}
                  {hasP1 && (
                    <ListItemButton
                      onClick={() => handleAssignTableDialogSelect(tbl, "player1")}
                      sx={{
                        py: 1.5, px: 2,
                        borderBottom: hasP2 ? "1px solid #f0f0f0" : "none",
                        "&:hover": { backgroundColor: "#ffebee" }
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", width: "100%", gap: 1 }}>
                        <Box sx={{
                          width: 32, height: 32, borderRadius: "50%",
                          backgroundColor: "#339AF0", color: "#fff",
                          display: "flex", alignItems: "center", justifyContent: "center",
                          fontSize: "0.75rem", fontWeight: 700, flexShrink: 0
                        }}>P1</Box>
                        <Typography sx={{ flex: 1, fontWeight: 500 }}>{p1Name}</Typography>
                        <Typography variant="caption" sx={{ color: "#f44336", fontWeight: 600 }}>
                          {t("replace") || "Replace"}
                        </Typography>
                      </Box>
                    </ListItemButton>
                  )}
                  {hasP2 && (
                    <ListItemButton
                      onClick={() => handleAssignTableDialogSelect(tbl, "player2")}
                      sx={{
                        py: 1.5, px: 2,
                        "&:hover": { backgroundColor: "#ffebee" }
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", width: "100%", gap: 1 }}>
                        <Box sx={{
                          width: 32, height: 32, borderRadius: "50%",
                          backgroundColor: "#ff9800", color: "#fff",
                          display: "flex", alignItems: "center", justifyContent: "center",
                          fontSize: "0.75rem", fontWeight: 700, flexShrink: 0
                        }}>P2</Box>
                        <Typography sx={{ flex: 1, fontWeight: 500 }}>{p2Name}</Typography>
                        <Typography variant="caption" sx={{ color: "#f44336", fontWeight: 600 }}>
                          {t("replace") || "Replace"}
                        </Typography>
                      </Box>
                    </ListItemButton>
                  )}
                  {hasP3 && (
                    <ListItemButton
                      onClick={() => handleAssignTableDialogSelect(tbl, "player3")}
                      sx={{
                        py: 1.5,
                        px: 2,
                        borderTop: hasP1 || hasP2 ? "1px solid #f0f0f0" : "none",
                        "&:hover": { backgroundColor: "#ffebee" },
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", width: "100%", gap: 1 }}>
                        <Box
                          sx={{
                            width: 32,
                            height: 32,
                            borderRadius: "50%",
                            backgroundColor: "#9c27b0",
                            color: "#fff",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "0.75rem",
                            fontWeight: 700,
                            flexShrink: 0,
                          }}
                        >
                          P3
                        </Box>
                        <Typography sx={{ flex: 1, fontWeight: 500 }}>{p3Name}</Typography>
                        <Typography variant="caption" sx={{ color: "#f44336", fontWeight: 600 }}>
                          {t("replace") || "Replace"}
                        </Typography>
                      </Box>
                    </ListItemButton>
                  )}
                  {!hasP1 && !hasP2 && (
                    <ListItemButton
                      onClick={() => handleAssignTableDialogSelect(tbl, "player1")}
                      sx={{ py: 1.5, px: 2, "&:hover": { backgroundColor: "#e8f5e9" } }}
                    >
                      <Typography sx={{ color: "#4caf50", fontWeight: 500 }}>
                        {t("empty_table_assign") || "Assign to this empty table"}
                      </Typography>
                    </ListItemButton>
                  )}
                </Box>
              </Box>
            );
          })}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleAssignTableDialogClose} color="inherit">
            {t("cancel") || "Cancel"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(addExtraTable)}
        onClose={() => setAddExtraTable(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          {t("add_player_to_table") || "Add player to table"}
          {addExtraTable?.table_number != null
            ? ` #${addExtraTable.table_number}`
            : ""}
        </DialogTitle>
        <DialogContent dividers>
          {(() => {
            const available = filterPlayersAvailableToSeat(players, tables);
            if (!available.length) {
              return (
                <Typography color="text.secondary">
                  {t("no_players_available") || "No players available"}
                </Typography>
              );
            }
            return available.map((p) => (
              <ListItemButton
                key={p.player_id}
                onClick={async () => {
                  const table = addExtraTable;
                  if (!table?.table_id || !selectedSession?.ts_id || !selectedex?.te_id) return;
                  try {
                    await playeradtable({
                      table_id: table.table_id,
                      ts_id: selectedSession.ts_id,
                      te_id: selectedex.te_id,
                      player_ids: [...seatedPlayerIds(table), p.player_id],
                    });
                    setAddExtraTable(null);
                    await gettablelist(selectedgrp?.groupDetails?.gr_id);
                    await getplayerlist(selectedSession.ts_id, selectedgrp?.groupDetails?.gr_id);
                    if (exercises.length > 1 && selectedex?.te_id) {
                      await assignToAllExercises(selectedex.te_id);
                      await gettablelist(selectedgrp?.groupDetails?.gr_id);
                    }
                  } catch (err) {
                    console.error("add extra player:", err);
                    setperror(err.response?.data?.message || err.message || t("error"));
                  }
                }}
              >
                <ListItemText
                  primary={p.user_alias || p.user_first_name || p.player_id}
                />
              </ListItemButton>
            ));
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddExtraTable(null)} color="inherit">
            {t("cancel") || "Cancel"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Manage Players Modal */}
      {selectedSession && (
        <PlayerPresenceDialog
          open={openPlayerModal}
          onClose={() => setOpenPlayerModal(false)}
          selectedSession={selectedSession}
          selectedgrp={selectedgrp}
          presentation="sheet"
          allowInviteWhenClosed
          presenceCorrectionOnly={sessionClosedForPresence}
          trainingTables={Array.isArray(tables) ? tables : []}
          trainingSelectedExercise={selectedex}
          onAfterThirdPlayerTableAssign={async () => {
            if (!selectedgrp?.groupDetails?.gr_id || !selectedSession?.ts_id) return;
            await gettablelist(selectedgrp.groupDetails.gr_id);
            await getplayerlist(selectedSession.ts_id, selectedgrp.groupDetails.gr_id);
            if (exercises.length > 1 && selectedex?.te_id) {
              await assignToAllExercises(selectedex.te_id);
              await gettablelist(selectedgrp.groupDetails.gr_id);
            }
          }}
          onUpdate={async () => {
            await getplayerlist(selectedSession.ts_id, selectedgrp?.groupDetails?.gr_id);
          }}
          onDone={async ({ presentPlayers = [] } = {}) => {
            if (
              !shouldAutoSeatAfterPresenceChange({
                sessionClosed: sessionClosedForPresence,
                exerciseLive: exerciseHasStartedForPlayerTools,
              }) ||
              !selectedex?.te_id ||
              !selectedSession?.ts_id ||
              !selectedgrp?.groupDetails?.gr_id ||
              !Array.isArray(presentPlayers) ||
              presentPlayers.length === 0 ||
              isAutoAssigning
            ) {
              return;
            }
            try {
              const tableResponse = await tablelist({
                gr_id: selectedgrp.groupDetails.gr_id,
                ts_id: selectedSession.ts_id,
                te_id: selectedex.te_id,
              });
              const tablesArray = tableResponse.data?.data || [];
              const plans = planPresenceSeatAssignments(presentPlayers, tablesArray);

              setIsAutoAssigning(true);
              if (plans.length > 0) {
                for (const plan of plans) {
                  const params = {
                    player1: plan.player1,
                    table_id: plan.table_id,
                    ts_id: selectedSession.ts_id,
                    te_id: selectedex.te_id,
                  };
                  if (plan.player2) params.player2 = plan.player2;
                  await playeradtable(params);
                }
                await gettablelist(selectedgrp.groupDetails.gr_id);
                await getplayerlist(
                  selectedSession.ts_id,
                  selectedgrp.groupDetails.gr_id
                );
              }
              if (exercises.length > 1 && selectedex?.te_id) {
                await assignToFollowingExercises(selectedex.te_id);
                await gettablelist(selectedgrp.groupDetails.gr_id);
              }
            } catch (e) {
              console.error("TableSetup - presence seat assign failed:", e);
            } finally {
              setIsAutoAssigning(false);
            }
          }}
        />
      )}

      {/* Personalize Exercise Dialog */}
      <PersonalizeExerciseDialog
        open={personalizeDialogOpen}
        onClose={() => {
          setPersonalizeDialogOpen(false);
          setPersonalizePlayerId(null);
        }}
        exercise={selectedex}
        groupPlayers={personalizeGroupPlayers}
        selectedSession={selectedSession}
        selectedgrp={selectedgrp}
        exerciseTypes={exerciseTypes}
        currentlng={currentlng}
        t={t}
        onExerciseSelected={handlePersonalizeExerciseSelected}
        preSelectedPlayerId={personalizePlayerId}
      />

      {/* Personalized exercise details dialog */}
      <Dialog
        open={personalizedExDetailsOpen}
        onClose={handleClosePersonalizedExDetails}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle>{t("exdetails", { lng: (currentlng || "en").split("-")[0] }) || "Exercise details"}</DialogTitle>
        <DialogContent>
          {personalizedExDetailsLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : personalizedExDetailsData ? (
            <ExerciseCard
              exercise={personalizedExDetailsData}
              lng={currentlng}
              t={t}
              selectMode="single"
              showDurationInput={false}
            />
          ) : (
            <Typography color="text.secondary">{t("te_not_found", { lng: (currentlng || "en").split("-")[0] }) || "Exercise not found"}</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePersonalizedExDetails} color="primary" variant="contained">
            {t("close", { lng: (currentlng || "en").split("-")[0] }) || "Close"}
          </Button>
        </DialogActions>
      </Dialog>

      <PlayerTrainingProfileDialog
        open={playerProfileOpen}
        onClose={() => {
          setPlayerProfileOpen(false);
          setPlayerProfileUserId(null);
          setPlayerProfilePlayerId(null);
        }}
        userId={playerProfileUserId}
        playerId={playerProfilePlayerId}
      />

      {/* Personalize feedback snackbar */}
      <Snackbar
        open={personalizeSnackbar.open}
        autoHideDuration={4000}
        onClose={() => setPersonalizeSnackbar((prev) => ({ ...prev, open: false }))}
      >
        <Alert variant="filled" severity={personalizeSnackbar.severity}>
          {personalizeSnackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};
